import {
  Box,
  useTheme,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Autocomplete,
  Chip,
  createFilterOptions,
  Divider,
  Switch,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  List,
  ListItemButton,
  CircularProgress,
  Popper,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVertRounded";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { useStateContext } from "contexts/ContextProvider";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import Overlay from "components/Overlay";
import { OVERLAY_NAMES } from "constants/Overlays";
import { TimeField } from "@mui/x-date-pickers";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { HtmlTooltip } from "components/HeatMap";
import { useAuth } from "../../../contexts/AuthContext";
import InlineGridSelector from "components/InlineGridSelector";
import UploadFileIcon from "@mui/icons-material/UploadFileRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import FeedbackRoundedIcon from "@mui/icons-material/FeedbackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { getTextFieldBorderStyles } from "../../../theme";
import { usePageTitle } from "hooks/usePageTitle";
import { countryOptions } from "constants/countryData";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePresence } from "contexts/PresenceContext";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { useNavigate } from "react-router-dom";
import { parsePhoneNumber, formatNumber } from "libphonenumber-js";
import { getMileageRateForDate } from "helpers/mileageRateHelper";
import { getRequestorInvoiceDefaults } from "helpers/requestorGroupHelpers";
import { buildMultiTimezoneLines } from "helpers/timezoneHelpers";

// Helper: format a phone number based on country code (national format, e.g. (555) 555-1234)
const formatPhoneNumber = (raw, countryCode) => {
  if (!raw) return "";
  try {
    const phoneNumber = parsePhoneNumber(raw, countryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      return phoneNumber.formatNational();
    }
  } catch {
    // fall through
  }
  return raw;
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/New_York");

export const getBorderColor = (
  theme,
  isNewAssignment,
  newData,
  oldData,
  optional = false,
) => {
  const flag = newData === null || newData === "";

  if (!isNewAssignment && newData !== oldData) {
    if (optional)
      return flag ? theme.palette.error.main : theme.palette.info[500];

    return theme.palette.info[400];
  } else {
    if (optional)
      return flag ? theme.palette.error.main : theme.palette.primary.main;

    return flag ? theme.palette.error.main : theme.palette.primary.main;
  }
};

// Helper function for time field button styling
const getTimeFieldButtonSx = (theme) => ({
  padding: "2px",
  bgcolor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  "&:hover": {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.background.default,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  "&:disabled": {
    bgcolor: theme.palette.action.disabledBackground,
  },
});

// Helper function to create time field adornments with increment/decrement buttons
const createTimeFieldAdornments = (
  theme,
  disabled,
  onDecrement,
  onIncrement,
) => ({
  startAdornment: (
    <InputAdornment position="start">
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onDecrement}
        sx={getTimeFieldButtonSx(theme)}
      >
        <RemoveRoundedIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ),
  endAdornment: (
    <InputAdornment position="end">
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onIncrement}
        sx={getTimeFieldButtonSx(theme)}
      >
        <AddRoundedIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ),
});

const MENTION_TRIGGER_REGEX = /(^|\s)([@#])([a-zA-Z0-9._-]*)$/;

const getTagContextAtCursor = (text, cursorPosition) => {
  if (typeof text !== "string") return null;
  const safeCursor =
    typeof cursorPosition === "number" ? cursorPosition : text.length;
  const beforeCursor = text.slice(0, safeCursor);
  const match = beforeCursor.match(MENTION_TRIGGER_REGEX);
  if (!match) return null;
  return {
    trigger: match[2],
    query: (match[3] || "").trim(),
    cursorPosition: safeCursor,
  };
};

const getMemberMentionToken = (member) => {
  const nameToken = String(member?.Name || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "");
  if (nameToken) return nameToken;
  const emailLocalPart = String(member?.Email || "")
    .split("@")[0]
    .trim()
    .toLowerCase()
    .replace(/[^a-zA-Z0-9._-]/g, "");
  if (emailLocalPart) return emailLocalPart;
  return "";
};

// Module-level constant for interpretation cost type options.
// Defines all recognized cost types with their metadata and calculation flags.
// includeInLinguistPay: whether the type contributes to TotalLinguistPay calculation.
const INTERPRETATION_BASE_COST_TYPE_OPTIONS = [
  {
    key: "linguistPay",
    label: "Linguist Pay",
    costTypeName: "Linguist Pay",
    sourceLegacyField: "LinguistPay",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: false,
    includeInLinguistPay: false,
  },
  {
    key: "mileageCost",
    label: "Mileage",
    costTypeName: "Mileage",
    sourceLegacyField: "MileageCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
  {
    key: "parkingFeesTolls",
    label: "Parking Fees",
    costTypeName: "Parking Fees",
    sourceLegacyField: "ParkingFeesTolls",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
  {
    key: "additionalCost",
    label: "Additional Cost",
    costTypeName: "Additional Cost",
    sourceLegacyField: "AdditionalCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: false,
  },
  {
    key: "additionalLinguistCost",
    label: "Additional Linguist Cost",
    costTypeName: "Additional Linguist Cost",
    sourceLegacyField: "AdditionalLinguistCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
  {
    key: "paymentFees",
    label: "Payment Fees",
    costTypeName: "Payment Fees",
    sourceLegacyField: "PaymentFees",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: false,
    includeInLinguistPay: true,
  },
  {
    key: "equipmentCost",
    label: "Equipment Cost",
    costTypeName: "Equipment",
    sourceLegacyField: "EquipmentCost",
    defaultIncludeInTotalCost: false,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: false,
  },
  {
    key: "travelTimeCost",
    label: "Travel Time",
    costTypeName: "Travel Time",
    sourceLegacyField: "TravelTimeCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
];

// Cost formula helper (2026-09-23, per Amine): renders the calculation
// breakdown under Account Charge / Total Linguist Pay / Total Cost. Every
// number carries a hover tooltip explaining the cost it represents.
const CostFormulaHelper = ({ parts }) => {
  if (!parts || parts.length === 0) return null;

  return (
    <>
      {parts.map((part, index) =>
        part.tip ? (
          <Tooltip key={index} title={part.tip} arrow placement="top">
            <Box
              component="span"
              sx={{
                cursor: "help",
                borderBottom: "1px dotted",
                borderColor: "text.disabled",
              }}
            >
              {part.text}
            </Box>
          </Tooltip>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
};

const EMPTY_LINGUIST_CLIENT_STATS = {
  completedAssignments: 0,
  cancelledUnableToFillAssignments: 0,
};

const EMPTY_LINGUIST_DISTANCE = { distanceMiles: null };

const EMPTY_LANGUAGE_CLIENT_STATS = {
  requestCount: 0,
};

const EMPTY_REQUESTOR_CLIENT_STATS = {
  servicesCount: 0,
};

const LONG_PRESS_AUTOFILL_MS = 900;
const LOREM_NAME_SEEDS = [
  "Lorem ipsum",
  "Dolor sit",
  "Amet consectetur",
  "Adipiscing elit",
  "Sed do",
  "Eiusmod tempor",
  "Incididunt ut",
  "Labore magna",
];
const LOREM_ADDRESS_SEEDS = [
  "123 Lorem Ave",
  "456 Ipsum Street",
  "789 Dolor Road",
  "321 Sit Plaza",
  "654 Amet Lane",
  "987 Elit Court",
];

const isNotApplicableLinguist = (linguist) =>
  String(linguist?.Name || "")
    .trim()
    .toUpperCase() === "N/A";

const getLinguistFirstName = (linguistName) => {
  const fullName = String(linguistName || "").trim();
  if (!fullName) return "";
  return fullName.split(/\s+/)[0] || fullName;
};

const toDayjsTime = (value) => {
  if (!value) return null;
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value, "HH:mm:ss");
  return parsed?.isValid?.() ? parsed : null;
};

const normalizeCostTypeName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const INTERPRETATION_COST_TYPE_NAME_ALIASES = {
  "parking fees": "parkingFeesTolls",
  "parking fee": "parkingFeesTolls",
  "parking fees and tolls": "parkingFeesTolls",
  "parking fee and tolls": "parkingFeesTolls",
  "parking fees tolls": "parkingFeesTolls",
};

const InterpretationNewEdit = () => {
  const filter = createFilterOptions();

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { currentEntity, setCurrentEntity } = usePresence();

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const {
    overlayData,
    setOverlayData,
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
  } = useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_NEW
      ? "New Interpretation"
      : "Edit Interpretation",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  // State for paid assignment warning dialog
  const [showPaidWarning, setShowPaidWarning] = useState(false);
  const [paidWarningMessage, setPaidWarningMessage] = useState("");
  const [pendingSave, setPendingSave] = useState(false);

  const isNewAssignment =
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_NEW;
  const prefilledData = useMemo(
    () => (isNewAssignment ? overlayData?.PrefilledData || {} : {}),
    [isNewAssignment, overlayData?.PrefilledData],
  );
  const isAmineUser = (currentUser?.Name || "").trim() === "Amine";

  // Paid-invoice lock: a service on a PAID invoice is frozen — Save is
  // disabled and a red message shows instead of the confirm-dialog dance.
  const paidLocked =
    !isNewAssignment && Boolean(overlayData?.Invoice?.PaymentReceivedDate);

  // Freeze ALL fields too (not just Save): force isEditable off while the
  // service sits on a paid invoice. Runs whenever either changes, so no
  // error-recovery setIsEditable(true) can re-enable a paid-locked form.
  useEffect(() => {
    if (paidLocked) setIsEditable(false);
  }, [paidLocked, isEditable]);

  // Edit lock for existing services only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "service",
    isNewAssignment ? null : overlayData.id,
  );

  const [, setAddNewClient] = useState(false);
  const [, setAddNewRequestor] = useState(false);
  const [, setAddNewInvoiceTo] = useState(false);

  // Snapshot the entity the underlying view (list/profile) had before this
  // overlay took over, so closing the overlay can restore it instead of
  // nulling it — nulling would drop the still-mounted list/profile out of its
  // entity room and it would stop receiving live updates.
  const previousEntityRef = useRef(null);
  const hasTakenOverEntityRef = useRef(false);

  // Set entity tracking for presence and updates
  useEffect(() => {
    if (!isNewAssignment && overlayData.id) {
      if (!hasTakenOverEntityRef.current) {
        hasTakenOverEntityRef.current = true;
        // Snapshot only on first takeover: currentEntity is intentionally NOT
        // a dependency, so the closure keeps the underlying view's entity from
        // the render that created this effect.
        previousEntityRef.current = currentEntity;
      }
      setCurrentEntity({
        entityType: "Interpretation",
        entityId: overlayData.id,
      });
    }
    return () => {
      if (hasTakenOverEntityRef.current) {
        // Restore the underlying view's entity (keeps its room alive) instead
        // of clearing it; fall back to clearing when there was nothing to
        // restore.
        setCurrentEntity(
          previousEntityRef.current || { entityType: null, entityId: null },
        );
        hasTakenOverEntityRef.current = false;
        previousEntityRef.current = null;
      }
    };
  }, [isNewAssignment, overlayData.id, setCurrentEntity]);

  // Tracks whether the user has unsaved form edits. Kept in a ref (declared
  // before the state it mirrors, since `hasChanges` is defined later in the
  // component body) so the socket refetch handler can check it synchronously.
  const hasChangesRef = useRef(false);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = () => {
    // If the user has unsaved edits, don't clobber their form with the remote
    // snapshot. Refreshing overlayData here would reset the dirty baseline
    // (initialDataRef syncs from overlayData) and silently discard their work.
    // Keep the list/profile behind the overlay fresh and warn instead.
    if (hasChangesRef.current) {
      triggerPageRefresh();
      showSnackbar(
        "This assignment was updated by another member. Review your changes before saving.",
        theme,
        "info",
        theme.palette.warning.main,
      );
      return;
    }
    // Reload the overlay data with fresh data from server
    api
      .get(`/services/interpretations/${overlayData.id}`)
      .then((response) => {
        setOverlayData({
          ...overlayData,
          ...response.data,
        });
        // Refresh the list behind the overlay, but skip the success snackbar —
        // form fields are local state and don't change on remote updates, so
        // "Assignment data refreshed" would be misleading.
        triggerPageRefresh();
      })
      .catch((err) => console.log("Error refetching assignment:", err));
  };

  useAssignmentUpdates({
    modelType: "Interpretation",
    recordId: isNewAssignment ? null : overlayData.id,
    onUpdate: handleAssignmentRefetch,
  });

  const updateClientRate = useCallback((client, currentLangID) => {
    const clientRates = client?.ClientRates;
    if (!Array.isArray(clientRates) || clientRates.length === 0) return 0;

    return (
      clientRates.find((clientRate) => clientRate.Language.id === currentLangID)
        ?.Rate ||
      clientRates.find((clientRate) => clientRate.Language.id === 1)?.Rate ||
      0
    );
  }, []);

  const completing =
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE;

  const [name, setName] = useState(
    isNewAssignment ? prefilledData.Name || "" : overlayData.Name,
  );

  const [date, setDate] = useState(
    isNewAssignment
      ? prefilledData.Date
        ? dayjs.utc(prefilledData.Date)
        : null
      : dayjs.utc(overlayData.Date),
  );

  // Holiday state for the selected date
  const [selectedDateHoliday, setSelectedDateHoliday] = useState(null);

  const [repeatUntilDate, setRepeatUntilDate] = useState(
    isNewAssignment || !overlayData.Date
      ? dayjs.utc().add(1, "month")
      : dayjs.utc(overlayData.Date).add(1, "month"),
  );
  const [, setRepeatUntilDateError] = useState(false);

  // Fetch holiday for the selected date
  useEffect(() => {
    const fetchHolidayForDate = async () => {
      if (!date) {
        setSelectedDateHoliday(null);
        return;
      }

      try {
        const dateKey = date.format("YYYY-MM-DD");
        const response = await api.get("/holidays", {
          params: {
            startDate: dateKey,
            endDate: dateKey,
            active: true,
          },
        });

        if (response?.data && response.data.length > 0) {
          setSelectedDateHoliday(response.data[0]);
        } else {
          setSelectedDateHoliday(null);
        }
      } catch (error) {
        console.error("Error fetching holiday:", error);
        setSelectedDateHoliday(null);
      }
    };

    fetchHolidayForDate();
  }, [date]);

  const [projectNumber, setProjectNumber] = useState(
    overlayData.ProjectNumber || "",
  );

  const [interpretationType, setInterpretationType] = useState(
    isNewAssignment
      ? prefilledData.InterpretationType || ""
      : overlayData.ServiceSubtype?.Name,
  );
  const [interpretationSubtypeOptions, setInterpretationSubtypeOptions] =
    useState([]);

  const resolveInterpretationSubtypeId = useCallback(
    (typeName) => {
      const normalized = String(typeName || "")
        .trim()
        .toLowerCase();
      if (!normalized) return null;
      const match = interpretationSubtypeOptions.find(
        (opt) =>
          String(opt?.Name || "")
            .trim()
            .toLowerCase() === normalized,
      );
      const parsed = Number(match?.id);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    },
    [interpretationSubtypeOptions],
  );

  const interpretationTypeOptions = useMemo(() => {
    const fromSubtypes = interpretationSubtypeOptions
      .map((option) => String(option?.Name || "").trim())
      .filter(Boolean);
    const fallback = (overlayData?.InterpretationTypes || [])
      .map((option) => String(option || "").trim())
      .filter(Boolean);

    const base = fromSubtypes.length > 0 ? fromSubtypes : fallback;
    const unique = Array.from(new Set(base));

    const current = String(interpretationType || "").trim();
    if (current && !unique.includes(current)) {
      unique.unshift(current);
    }

    return unique;
  }, [
    interpretationSubtypeOptions,
    overlayData?.InterpretationTypes,
    interpretationType,
  ]);

  const [client, setClient] = useState(
    isNewAssignment ? prefilledData.Client || "" : overlayData.Client,
  );
  const [languageClientStatsById, setLanguageClientStatsById] = useState({});

  const [requestor, setRequestor] = useState(
    isNewAssignment ? prefilledData.Requestor || "" : overlayData.Requestor,
  );
  const [requestorClientStatsById, setRequestorClientStatsById] = useState({});

  const [invoiceTo, setInvoiceTo] = useState(
    isNewAssignment ? prefilledData.Invoicer || "" : overlayData.Invoicer,
  );

  // CC recipients resolved from the requestor's group (read-only display;
  // not persisted on the service).
  const [ccRequestors, setCcRequestors] = useState([]);
  const applyRequestorInvoiceDefaults = useCallback((requestorValue) => {
    const { invoiceTo: nextInvoiceTo, ccRequestors: nextCcRequestors } =
      getRequestorInvoiceDefaults(requestorValue);
    setInvoiceTo(nextInvoiceTo || "");
    setCcRequestors(nextCcRequestors);
  }, []);

  const [language, setLanguage] = useState(
    isNewAssignment
      ? prefilledData.Language || ""
      : overlayData.ServiceTargetLanguages?.[0]?.Language || "",
  );

  const initialOfferLinguists = useMemo(() => {
    const initialLinguist = isNewAssignment
      ? prefilledData.Linguist || null
      : overlayData.Linguist || null;

    return initialLinguist ? [initialLinguist] : [];
  }, [isNewAssignment, overlayData.Linguist, prefilledData.Linguist]);

  const [linguist, setLinguist] = useState(
    isNewAssignment ? prefilledData.Linguist || "" : overlayData.Linguist,
  );
  const [offerLinguists, setOfferLinguists] = useState(initialOfferLinguists);
  const [linguistClientStatsById, setLinguistClientStatsById] = useState({});
  const [linguistDistancesById, setLinguistDistancesById] = useState({});
  const [isCalculatingLinguistDistances, setIsCalculatingLinguistDistances] =
    useState(false);
  const linguistDistanceRequestIdRef = useRef(0);
  const [linguistInputValue, setLinguistInputValue] = useState(
    isNotApplicableLinguist(isNewAssignment ? null : overlayData.Linguist)
      ? ""
      : isNewAssignment
        ? prefilledData.Linguist?.Name || ""
        : overlayData.Linguist?.Name || "",
  );
  const setSelectedOfferLinguists = useCallback((nextLinguists) => {
    const normalized = Array.isArray(nextLinguists)
      ? nextLinguists.filter(Boolean)
      : [];

    setOfferLinguists(normalized);
    setLinguist(normalized[0] || "");
    setLinguistInputValue("");
    // A linguist is optional while the assignment status is Requested
    // (pre-assign outreach flow).
    setLinguistError(
      normalized.length === 0 &&
        !String(interpretationStatusRef.current || "").startsWith("Requested"),
    );
  }, []);

  const [address, setAddress] = useState(
    isNewAssignment ? prefilledData.Address || "" : overlayData.Address,
  );

  const [interpretationStatus, setInterpretationStatus] = useState(
    isNewAssignment
      ? prefilledData.InterpretationStatus || "Requested"
      : completing
        ? "Completed"
        : overlayData.InterpretationStatus,
  );

  // Keep a ref of the current status for use inside the stable callback above.
  const interpretationStatusRef = useRef(interpretationStatus);
  useEffect(() => {
    interpretationStatusRef.current = interpretationStatus;
  }, [interpretationStatus]);

  const [cancellationReason, setCancellationReason] = useState(
    isNewAssignment ? "" : overlayData.CancellationReason,
  );

  useEffect(() => {
    let isMounted = true;

    api
      .get("/services/interpretations/types/options")
      .then((response) => {
        if (!isMounted) return;
        setInterpretationSubtypeOptions(
          Array.isArray(response.data) ? response.data : [],
        );
      })
      .catch((error) => {
        console.error("Error fetching interpretation subtype options:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const [accountCharge, setAccountCharge] = useState(
    isNewAssignment
      ? prefilledData.AccountCharge || ""
      : overlayData.AccountCharge,
  );

  const [totalCost, setTotalCost] = useState(
    isNewAssignment ? prefilledData.TotalCost || "" : overlayData.TotalCost,
  );

  // Calculation-breakdown parts for the three money fields. Each part is
  // { text, tip } — a `tip` renders that number as a hover tooltip naming the
  // cost it represents (2026-09-23, per Amine).
  const [totalCostHelperParts, setTotalCostHelperParts] = useState([]);
  const [accountChargeHelperParts, setAccountChargeHelperParts] = useState([]);
  const [totalLinguistPayHelperParts, setTotalLinguistPayHelperParts] =
    useState([]);

  // Dynamic map of cost type values - keyed by cost type key (e.g. "mileageCost").
  // New DB cost types are automatically captured without per-type code changes.
  const [costValues, setCostValues] = useState(() => {
    if (isNewAssignment) {
      return Object.fromEntries(
        INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => [
          opt.key,
          prefilledData[opt.sourceLegacyField] !== undefined &&
          prefilledData[opt.sourceLegacyField] !== null
            ? prefilledData[opt.sourceLegacyField]
            : "",
        ]),
      );
    }
    return Object.fromEntries(
      INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => [
        opt.key,
        overlayData[opt.sourceLegacyField] !== undefined &&
        overlayData[opt.sourceLegacyField] !== null
          ? overlayData[opt.sourceLegacyField]
          : "",
      ]),
    );
  });
  const [costErrors, setCostErrors] = useState({});

  // Alias const derivations for backward compatibility with save payloads, auto-calc, etc.
  const linguistPay = costValues.linguistPay ?? "";
  const additionalCost = costValues.additionalCost ?? 0;
  const additionalLinguistCost = costValues.additionalLinguistCost ?? "";
  const equipmentCost = costValues.equipmentCost ?? "";
  const mileageCost = costValues.mileageCost ?? 0;
  const parkingFeesTolls = costValues.parkingFeesTolls ?? 0;
  const paymentFees = costValues.paymentFees ?? 0;
  const travelTimeCost = costValues.travelTimeCost ?? 0;

  // Alias setter functions for backward compatibility
  const setLinguistPay = (v) =>
    setCostValues((prev) => ({ ...prev, linguistPay: v }));
  const setAdditionalCost = (v) =>
    setCostValues((prev) => ({ ...prev, additionalCost: v }));
  const setAdditionalLinguistCost = (v) =>
    setCostValues((prev) => ({ ...prev, additionalLinguistCost: v }));
  const setEquipmentCost = (v) =>
    setCostValues((prev) => ({ ...prev, equipmentCost: v }));
  const setMileageCost = (v) =>
    setCostValues((prev) => ({ ...prev, mileageCost: v }));
  const setParkingFeesTolls = (v) =>
    setCostValues((prev) => ({ ...prev, parkingFeesTolls: v }));
  const setPaymentFees = (v) =>
    setCostValues((prev) => ({ ...prev, paymentFees: v }));
  const setTravelTimeCost = (v) =>
    setCostValues((prev) => ({ ...prev, travelTimeCost: v }));

  const [mode, setMode] = useState(
    overlayData.Mode === null ||
      overlayData.Mode === "" ||
      overlayData.Mode === undefined
      ? "Consecutive"
      : overlayData.Mode,
  );

  const getDurationValue = useCallback((timeStart, timeEnd) => {
    if (timeStart && timeEnd && timeStart.isValid() && timeEnd.isValid()) {
      const diffMs = timeEnd.diff(timeStart);

      // Convert to minutes and round up to nearest 15-minute increment
      const minutes = diffMs / 1000 / 60;
      const roundedMinutes = Math.ceil(minutes / 15) * 15;
      const roundedMs = roundedMinutes * 60 * 1000;

      return dayjs.utc(roundedMs);
    }
    return null;
  }, []);

  const [rate, setRate] = useState(
    isNewAssignment ? Number(prefilledData.Rate || 0) : overlayData.Rate,
  );

  // Track original linguist and helper text for historical rates
  const [originalLinguistId] = useState(
    isNewAssignment ? null : overlayData.Linguist?.id,
  );
  const [rateHelperText, setRateHelperText] = useState("");

  // On load, if linguist rate changed since assignment, show both rates
  useEffect(() => {
    if (isNewAssignment) return;

    // Prefer latest linguist data from overlayData.Linguists (matched by id, then Name)
    const matchedLinguist =
      overlayData.Linguists?.find((ling) =>
        overlayData.Linguist?.id
          ? ling.id === overlayData.Linguist.id
          : overlayData.Linguist?.Name
            ? ling.Name === overlayData.Linguist.Name
            : false,
      ) || overlayData.Linguist;

    const linguistCurrentRateRaw =
      matchedLinguist?.LinguistLanguages?.[0]?.Rate ?? null;
    const assignmentRateRaw = overlayData.Rate;

    const linguistCurrentRateNum = Number(linguistCurrentRateRaw);
    const assignmentRateNum = Number(assignmentRateRaw);

    const linguistCurrentRate = Number.isNaN(linguistCurrentRateNum)
      ? 0
      : linguistCurrentRateNum;
    const assignmentRate = Number.isNaN(assignmentRateNum)
      ? 0
      : assignmentRateNum;

    if (assignmentRate !== linguistCurrentRate) {
      setRateHelperText(
        `Linguist New Rate: $${linguistCurrentRate} | Linguist Assignment Rate: $${assignmentRate}`,
      );
    }
  }, [
    isNewAssignment,
    overlayData.Linguist,
    overlayData.Linguists,
    overlayData.Rate,
  ]);

  const [contactPersonName, setContactPersonName] = useState(
    isNewAssignment
      ? prefilledData.ContactPersonName || ""
      : overlayData.ContactPersonName,
  );

  const [contactPersonNumber, setContactPersonNumber] = useState(
    isNewAssignment
      ? prefilledData.ContactPersonNumber || ""
      : overlayData.ContactPersonNumber,
  );
  const [contactPersonOptions, setContactPersonOptions] = useState([]);

  const initialContactPersons = useMemo(() => {
    if (!isNewAssignment && Array.isArray(overlayData.ContactPersons)) {
      const normalized = overlayData.ContactPersons.map((contact) => {
        const countryCode = contact?.CountryCode || "US";
        const rawPhone = contact?.PhoneNumber || "";
        const formatted = rawPhone
          ? formatPhoneNumber(rawPhone, countryCode)
          : "";
        return {
          id: contact?.id || contact?.ContactPersonID || null,
          Name: contact?.Name || "",
          PhoneNumber: formatted,
          Ext: contact?.Ext || "",
          countryCode,
        };
      }).filter(
        (contact) => contact.Name || contact.PhoneNumber || contact.Ext,
      );

      if (normalized.length > 0) {
        return normalized;
      }
    }

    if (
      !isNewAssignment &&
      (overlayData.ContactPersonName || overlayData.ContactPersonNumber)
    ) {
      const rawPhone = overlayData.ContactPersonNumber || "";
      const formatted = rawPhone ? formatPhoneNumber(rawPhone, "US") : "";
      return [
        {
          id: overlayData.ContactPerson?.id || null,
          Name: overlayData.ContactPersonName || "",
          PhoneNumber: formatted,
          Ext: overlayData.ContactPerson?.Ext || "",
          countryCode: "US",
        },
      ];
    }

    return [
      { id: null, Name: "", PhoneNumber: "", Ext: "", countryCode: "US" },
    ];
  }, [
    isNewAssignment,
    overlayData.ContactPersonName,
    overlayData.ContactPersonNumber,
    overlayData.ContactPerson,
    overlayData.ContactPersons,
  ]);

  const [contactPersons, setContactPersons] = useState(initialContactPersons);

  useEffect(() => {
    let active = true;

    if (!client?.id) {
      setContactPersonOptions([]);
      return undefined;
    }

    api
      .get("/contactPersons", { params: { clientId: client.id } })
      .then((response) => {
        if (!active) return;
        setContactPersonOptions(
          Array.isArray(response?.data) ? response.data : [],
        );
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching contact persons:", error);
        setContactPersonOptions([]);
      });

    return () => {
      active = false;
    };
  }, [client?.id]);

  useEffect(() => {
    const firstContact = (contactPersons || [])[0] || null;
    setContactPersonName(firstContact?.Name || "");
    setContactPersonNumber(firstContact?.PhoneNumber || "");
  }, [contactPersons]);

  const addContactPersonRow = () => {
    setContactPersons((prev) => [
      ...prev,
      { id: null, Name: "", PhoneNumber: "", Ext: "", countryCode: "US" },
    ]);
  };

  const removeContactPersonRow = (index) => {
    setContactPersons((prev) => {
      if (prev.length <= 1) {
        return [
          { id: null, Name: "", PhoneNumber: "", Ext: "", countryCode: "US" },
        ];
      }
      return prev.filter((_, rowIndex) => rowIndex !== index);
    });
  };

  const updateContactPersonRow = (index, key, value) => {
    setContactPersons((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        return {
          ...row,
          // Preserve the contact's id when editing Name/Phone/Ext — clearing
          // it here made the server treat the row as a NEW contact and create
          // a duplicate instead of updating the existing one (issue #7).
          id: key === "id" ? value : row.id,
          [key]: value,
        };
      }),
    );
  };

  const handleContactPersonSelect = (index, newValue) => {
    if (typeof newValue === "string") {
      setContactPersons((prev) =>
        prev.map((row, rowIndex) =>
          rowIndex === index
            ? {
                ...row,
                id: null,
                Name: newValue,
                PhoneNumber: row.PhoneNumber || "",
                Ext: row.Ext || "",
              }
            : row,
        ),
      );
      return;
    }

    if (newValue && newValue.inputValue) {
      setContactPersons((prev) =>
        prev.map((row, rowIndex) =>
          rowIndex === index
            ? {
                ...row,
                id: null,
                Name: newValue.inputValue,
                PhoneNumber: row.PhoneNumber || "",
                Ext: row.Ext || "",
              }
            : row,
        ),
      );
      return;
    }

    if (!newValue) {
      setContactPersons((prev) =>
        prev.map((row, rowIndex) =>
          rowIndex === index
            ? { ...row, id: null, Name: "", PhoneNumber: "", Ext: "" }
            : row,
        ),
      );
      return;
    }

    const newPhones = newValue.PhoneNumbers || [];
    const newPrimary = newPhones.find((p) => p.IsPrimary) || newPhones[0];
    const matchedCountry = newPrimary?.CountryCode
      ? countryOptions.find(
          (c) => String(c.callingCode) === String(newPrimary.CountryCode),
        )
      : null;
    const rawPhone = newPrimary?.Number || newValue.PhoneNumber || "";
    setContactPersons((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const countryCode = matchedCountry?.code || row.countryCode || "US";
        const formattedPhone = rawPhone
          ? formatPhoneNumber(rawPhone, countryCode)
          : "";
        return {
          ...row,
          id: newValue.id || null,
          Name: newValue.Name || "",
          PhoneNumber: formattedPhone,
          Ext: newPrimary?.Ext || newValue.Ext || "",
          countryCode,
          callingCode: newPrimary?.CountryCode
            ? String(newPrimary.CountryCode)
            : row.callingCode || "1",
        };
      }),
    );
  };

  const normalizedContactPersonsForPayload = contactPersons
    .map((contact) => {
      // Strip formatting before saving — keep only digits
      const rawPhone = (contact?.PhoneNumber || "").replace(/\D/g, "");
      return {
        id: contact?.id || null,
        Name: (contact?.Name || "").trim(),
        PhoneNumber: rawPhone,
        Ext: (contact?.Ext || "").trim(),
        CountryCode: contact.callingCode || "1",
      };
    })
    .filter(
      (contact) =>
        contact.Name.length > 0 ||
        contact.PhoneNumber.length > 0 ||
        contact.Ext.length > 0,
    );

  const contactRowsValidation = useMemo(() => {
    const rows = Array.isArray(contactPersons) ? contactPersons : [];
    const requiresBothFields = rows.length > 1;

    return rows.map((contact) => {
      const hasName = Boolean((contact?.Name || "").trim());
      const hasPhone = Boolean((contact?.PhoneNumber || "").trim());

      if (!requiresBothFields && !hasName && !hasPhone) {
        return {
          nameInvalid: false,
          phoneInvalid: false,
          rowInvalid: false,
        };
      }

      const nameInvalid = !hasName;
      const phoneInvalid = false;

      return {
        nameInvalid,
        phoneInvalid,
        rowInvalid: nameInvalid,
      };
    });
  }, [contactPersons]);

  const hasContactPersonsError = useMemo(
    () => contactRowsValidation.some((row) => row.rowInvalid),
    [contactRowsValidation],
  );

  const [twoHoursMin, setTwoHoursMin] = useState(
    isNewAssignment
      ? (linguist && linguist.MinimumHours && linguist.MinimumHours === 2) ||
          false
      : overlayData.ApplyTwoHourMinimum ||
          (linguist && linguist.MinimumHours && linguist.MinimumHours === 2) ||
          false,
  );

  const [profit, setProfit] = useState(
    isNewAssignment ? prefilledData.Profit || "" : overlayData.Profit,
  );

  const [margin, setMargin] = useState(
    isNewAssignment ? prefilledData.Margin || "" : overlayData.Margin,
  );

  const [note, setNote] = useState("");
  const [mentionTrigger, setMentionTrigger] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionCursor, setMentionCursor] = useState(null);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [isMentionLoading, setIsMentionLoading] = useState(false);
  const [serviceTagSuggestions, setServiceTagSuggestions] = useState([]);
  const [members, setMembers] = useState([]);

  // The Service record is the source of truth for the client rate when
  // editing. It must never be derived from the Client's standard rates.
  const initialClientRate = isNewAssignment
    ? ""
    : overlayData.ServiceClientRate ?? "";

  const initialNewClientRate = (() => {
    if (!isNewAssignment) return null;
    const preClient = prefilledData.Client;
    const preLang = prefilledData.Language;
    if (preClient && preLang?.id) {
      const computed = updateClientRate(preClient, preLang.id);
      return computed || "";
    }
    return "";
  })();

  const [clientRate, setClientRate] = useState(
    isNewAssignment ? initialNewClientRate : initialClientRate,
  );

  const [clientRateSimultaneous, setClientRateSimultaneous] = useState(
    isNewAssignment
      ? initialNewClientRate || 0
      : overlayData.ServiceClientRateSimultaneous,
  );

  const [rateSimultaneous, setRateSimultaneous] = useState(
    isNewAssignment
      ? Number(prefilledData.RateSimultaneous || 0)
      : overlayData.RateSimultaneous,
  );

  const [totalLinguistPay, setTotalLinguistPay] = useState(
    isNewAssignment
      ? prefilledData.TotalLinguistPay || ""
      : overlayData.TotalLinguistPay,
  );

  const [clientRateDiscount, setClientRateDiscount] = useState(
    isNewAssignment
      ? Number(prefilledData.ClientRateDiscount || 0)
      : overlayData.ClientRateDiscount,
  );

  const [timeStart, setTimeStart] = useState(
    isNewAssignment
      ? prefilledData.TimeStart
        ? toDayjsTime(prefilledData.TimeStart)
        : ""
      : dayjs(overlayData.TimeStart, "HH:mm:ss"),
  );
  const [timeEnd, setTimeEnd] = useState(
    isNewAssignment
      ? prefilledData.TimeEnd
        ? toDayjsTime(prefilledData.TimeEnd)
        : ""
      : dayjs(overlayData.TimeEnd, "HH:mm:ss"),
  );

  const [duration, setDuration] = useState(
    isNewAssignment
      ? prefilledData.TimeStart && prefilledData.TimeEnd
        ? getDurationValue(
            toDayjsTime(prefilledData.TimeStart),
            toDayjsTime(prefilledData.TimeEnd),
          )
        : null
      : dayjs(overlayData.Duration, "HH:mm:ss"),
  );

  // Initialize actual times as null
  const [actualStart, setActualStart] = useState(
    isNewAssignment
      ? prefilledData.ActualStart
        ? toDayjsTime(prefilledData.ActualStart)
        : ""
      : overlayData.ActualStart === "00:00:00"
        ? completing
          ? dayjs(overlayData.TimeStart, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualStart, "HH:mm:ss"),
  );
  const [actualEnd, setActualEnd] = useState(
    isNewAssignment
      ? prefilledData.ActualEnd
        ? toDayjsTime(prefilledData.ActualEnd)
        : ""
      : overlayData.ActualEnd === "00:00:00"
        ? completing
          ? dayjs(overlayData.TimeEnd, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualEnd, "HH:mm:ss"),
  );
  const [actualDuration, setActualDuration] = useState(
    isNewAssignment
      ? prefilledData.ActualDuration
        ? toDayjsTime(prefilledData.ActualDuration)
        : prefilledData.ActualStart && prefilledData.ActualEnd
          ? getDurationValue(
              toDayjsTime(prefilledData.ActualStart),
              toDayjsTime(prefilledData.ActualEnd),
            )
          : null
      : overlayData.ActualDuration === "00:00:00"
        ? completing
          ? dayjs(overlayData.Duration, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualDuration, "HH:mm:ss"),
  );

  // Also initialize scheduling and preparation times as null
  const [schedulingTime, setSchedulingTime] = useState(
    isNewAssignment
      ? prefilledData.SchedulingTime
        ? toDayjsTime(prefilledData.SchedulingTime)
        : null
      : overlayData.SchedulingTime === "00:00:00"
        ? null
        : dayjs(overlayData.SchedulingTime, "HH:mm:ss"),
  );
  const [preparationTime, setPreparationTime] = useState(
    isNewAssignment
      ? prefilledData.PreparationTime
        ? toDayjsTime(prefilledData.PreparationTime)
        : null
      : overlayData.PreparationTime === "00:00:00"
        ? null
        : dayjs(overlayData.PreparationTime, "HH:mm:ss"),
  );
  const [travelTimeDuration, setTravelTimeDuration] = useState(
    isNewAssignment
      ? prefilledData.TravelTimeDuration
        ? toDayjsTime(prefilledData.TravelTimeDuration)
        : null
      : overlayData.TravelTimeDuration === "00:00:00" ||
          !overlayData.TravelTimeDuration
        ? null
        : dayjs(overlayData.TravelTimeDuration, "HH:mm:ss"),
  );
  const [totalDuration, setTotalDuration] = useState(
    isNewAssignment
      ? prefilledData.TimeStart && prefilledData.TimeEnd
        ? getDurationValue(
            toDayjsTime(prefilledData.TimeStart),
            toDayjsTime(prefilledData.TimeEnd),
          )
        : null
      : overlayData.TotalDuration === "00:00:00"
        ? completing
          ? dayjs(overlayData.Duration, "HH:mm:ss")
          : null
        : dayjs(overlayData.TotalDuration, "HH:mm:ss"),
  );

  const [mileageRoundtrip, setMileageRoundtrip] = useState(
    isNewAssignment
      ? Number(prefilledData.MileageRoundtrip || 0)
      : overlayData.MileageRoundtrip,
  );
  const [, setHasExplicitMileageEdit] = useState(false);

  const [mileageRates, setMileageRates] = useState([]);

  const getMileageRateForYear = useCallback(
    (dateValue) => {
      if (!dateValue) return 0.7;
      return getMileageRateForDate(mileageRates, dateValue, 0.7);
    },
    [mileageRates],
  );

  const [shouldAutoCalculateMileageCost, setShouldAutoCalculateMileageCost] =
    useState(isNewAssignment);

  // Tracks manual edits to the Mileage Cost amount field. Once the user types
  // a custom cost, neither the auto-calc decision below nor the async cost-line
  // hydration should override it.
  const mileageCostManuallyEditedRef = useRef(false);

  // Mirror of shouldAutoCalculateMileageCost for use inside the async
  // initializeCostRows effect, which resolves after mount and would otherwise
  // overwrite a freshly recalculated Mileage Cost with the stale saved amount.
  const shouldAutoCalculateMileageCostRef = useRef(isNewAssignment);

  const applyShouldAutoCalculateMileageCost = useCallback((value) => {
    shouldAutoCalculateMileageCostRef.current = value;
    setShouldAutoCalculateMileageCost(value);
  }, []);

  useEffect(() => {
    if (isNewAssignment) return;

    // Never re-enable auto-calculation after a manual override.
    if (mileageCostManuallyEditedRef.current) return;

    if (!overlayData) return;
    if (!date?.isValid()) return;

    // Mileage rates load asynchronously; until they arrive the fallback rate
    // (0.7) would produce a wrong comparison. Decide only once real rates
    // are available.
    if (mileageRates.length === 0) return;

    const calculatedMileageCost = (
      parseFloat(mileageRoundtrip || 0) * getMileageRateForYear(date)
    ).toFixed(2);

    const savedMileageCost =
      overlayData?.MileageCost == null || overlayData?.MileageCost === ""
        ? "0.00"
        : parseFloat(overlayData.MileageCost).toFixed(2);

    // Rates can now change mid-year. If the saved cost was computed with an
    // outdated rate, enable auto-calc so it is recalculated on open using the
    // current rate for the assignment's date.
    //
    // A saved cost of 0.00 must NOT be treated as outdated (2026-09-23, per
    // Amine): with roundtrip miles still recorded it differs from the derived
    // miles × rate figure on EVERY open, so auto-calc was re-enabled each
    // time, the cost was re-derived, and the removed Mileage row was added
    // back by itself — and saved back to the DB. Saved-0 means Mileage was
    // deliberately removed / never charged; only a non-zero saved cost can be
    // outdated.
    const savedMileageCostValue = parseFloat(savedMileageCost) || 0;
    applyShouldAutoCalculateMileageCost(
      savedMileageCostValue !== 0 &&
        savedMileageCost !== calculatedMileageCost,
    );
    // Decide once per open, after real rates are available. Re-running on
    // every overlayData / roundtrip / date change is what allowed the late
    // recalculation that reset the value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewAssignment, mileageRates, overlayData?.id]);

  const [details, setDetails] = useState(
    isNewAssignment ? prefilledData.Details || "" : overlayData.Details,
  );

  const [showAllLinguists, setShowAllLinguists] = useState(false);

  const [showRecurring, setShowRecurring] = useState(false);

  const [filterLingsByLangs, setFilterLingsByLangs] = useState(true);

  const APP_TIMEZONE = "America/New_York";

  const getTimezoneLabel = (timeZone) => {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "long",
      }).formatToParts(new Date());

      return (
        parts.find((part) => part.type === "timeZoneName")?.value ||
        String(timeZone)
      );
    } catch (error) {
      return String(timeZone);
    }
  };

  const getTimezoneCurrentTime = (timeZone) => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(new Date());
    } catch (error) {
      return null;
    }
  };

  const [timezoneWarning, setTimezoneWarning] = useState(null);

  // Check for timezone mismatches between client and all selected linguists
  useEffect(() => {
    const clientTimezone = client?.Timezone || "America/New_York";
    const linguists = Array.isArray(offerLinguists) ? offerLinguists : [];

    if (!client && linguists.length === 0) {
      setTimezoneWarning(null);
      return;
    }

    // Collect unique timezones among linguists
    const linguistTimezones = new Set(
      linguists.map((l) => l?.Timezone || "America/New_York"),
    );

    const clientDiffersFromSomeLinguist = linguists.some(
      (l) => (l?.Timezone || "America/New_York") !== clientTimezone,
    );
    const linguistsHaveDifferentTimezones = linguistTimezones.size > 1;

    // Only warn on a REAL mismatch: parties disagree with each other.
    // All parties in the SAME non-EST timezone (e.g. everyone in
    // America/Chicago) is not a conflict and must not warn.
    const shouldWarn =
      clientDiffersFromSomeLinguist || linguistsHaveDifferentTimezones;

    if (!shouldWarn) {
      setTimezoneWarning(null);
      return;
    }

    const clientLabel = client?.Account || "Client";
    const clientTzLabel = getTimezoneLabel(clientTimezone);
    const clientCurrentTime = getTimezoneCurrentTime(clientTimezone);
    const lines = [];

    // Always show client timezone with name and current time
    lines.push(
      `Client: ${clientLabel} — ${clientTzLabel} (currently ${clientCurrentTime || "N/A"})`,
    );

    // Determine which linguists to show:
    // - If linguists have different timezones among themselves, show ALL of them
    // - Otherwise, show linguists whose timezone differs from the client's
    let linguistsToShow;
    if (linguistsHaveDifferentTimezones) {
      linguistsToShow = linguists;
    } else {
      linguistsToShow = linguists.filter(
        (l) => (l?.Timezone || "America/New_York") !== clientTimezone,
      );
    }

    linguistsToShow.forEach((l) => {
      const tz = l?.Timezone || "America/New_York";
      const tzLabel = getTimezoneLabel(tz);
      const currentTime = getTimezoneCurrentTime(tz);
      lines.push(
        `Linguist: ${l?.Name || "Linguist"} — ${tzLabel} (currently ${currentTime || "N/A"})`,
      );
    });

    setTimezoneWarning("Timezone difference:\n" + lines.join("\n"));
  }, [client, offerLinguists]);

  // Converted assignment times for Client/Linguist non-EST timezones.
  // Renders lines like "10:00 AM - 11:00 AM EST" + "Client: 7:00 AM - 8:00 AM PDT"
  // under the Start/End/Duration row when any party is outside EST.
  const timezoneTimeLines = useMemo(() => {
    if (!timeStart || !timeEnd || !date) return [];

    const clientTz = client?.Timezone || "America/New_York";
    const linguistTzs = Array.isArray(offerLinguists)
      ? offerLinguists
          .map((l) => l?.Timezone || "America/New_York")
          .filter(Boolean)
      : [];

    const hasNonEST =
      clientTz !== "America/New_York" ||
      linguistTzs.some((tz) => tz !== "America/New_York");
    if (!hasNonEST) return [];

    return buildMultiTimezoneLines({
      timeStart: timeStart.format("HH:mm:ss"),
      timeEnd: timeEnd.format("HH:mm:ss"),
      date: date.format("YYYY-MM-DD"),
      clientTimezone: clientTz,
      linguistTimezones: linguistTzs,
    });
  }, [client, date, offerLinguists, timeEnd, timeStart]);

  // Date counts cache for project number generation
  const [dateCountsCache, setDateCountsCache] = useState({});
  const [, setDateCountsLoading] = useState(false);

  const [showActualTimes, setShowActualTimes] = useState(
    completing
      ? true
      : isNewAssignment
        ? false
        : overlayData.ActualDuration === "00:00:00"
          ? false
          : true,
  );

  const [allowDetailsEditing, setAllowDetailsEditing] = useState(false);
  const [allowTotalDurationEditing, setAllowTotalDurationEditing] =
    useState(false);
  const [
    disableAutomaticCalculationOfCharges,
    setDisableAutomaticCalculationOfCharges,
  ] = useState(
    isNewAssignment
      ? prefilledData.DisableAutoChargeCalc || false
      : overlayData.DisableAutoChargeCalc || false,
  );

  const [allowShortAssignments, setAllowShortAssignments] = useState(
    isNewAssignment
      ? prefilledData.IsShortAssignment || false
      : overlayData.IsShortAssignment || false,
  );

  const [nameError, setNameError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [projectNumberError, setProjectNumberError] = useState(false);
  const [projectNumberExists, setProjectNumberExists] = useState(false);
  const [interpretationTypeError, setInterpretationTypeError] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [requestorError, setRequestorError] = useState(false);
  const [contactPersonsError, setContactPersonsError] = useState(false);
  const [invoiceToError, setInvoiceToError] = useState(false);
  const [languageError, setLanguageError] = useState(false);
  const [linguistError, setLinguistError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [linguistPaymentNotesById, setLinguistPaymentNotesById] = useState({});

  const [repeatEveryError, setRepeatEveryError] = useState(false);
  const [periodError, setPeriodError] = useState(false);
  const [repeatEvery, setRepeatEvery] = useState(1);

  const [period, setPeriod] = useState("Week");
  const periodsList = ["Day", "Week", "Month"];
  const repeatEveryList = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  const daysList = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const [tempSelectedDays, setTempSelectedDays] = useState([]);

  const [recursionStatusText, setRecursionStatusText] = useState("");

  const [recurrenceDates, setRecurrenceDates] = useState([]);

  // Fetch linguist EntityNotes and filter for payment-related ones (containing $)
  // Now supports multiple selected linguists — stores notes in a map by linguist ID.
  useEffect(() => {
    const linguistIds = (Array.isArray(offerLinguists) ? offerLinguists : [])
      .map((ling) => ling?.id)
      .filter((id) => id && !isNotApplicableLinguist({ id }));

    if (linguistIds.length === 0) {
      setLinguistPaymentNotesById({});
      return;
    }

    let cancelled = false;
    const fetchNotes = async () => {
      try {
        const results = await Promise.all(
          linguistIds.map(async (id) => {
            try {
              const response = await api.get(`/linguists/${id}/notes`);
              const paymentNotes = (response.data || []).filter(
                (note) =>
                  typeof note.Body === "string" && note.Body.includes("$"),
              );
              return { id, paymentNotes };
            } catch {
              return { id, paymentNotes: [] };
            }
          }),
        );
        if (cancelled) return;
        const map = {};
        results.forEach(({ id, paymentNotes }) => {
          map[id] = paymentNotes;
        });
        setLinguistPaymentNotesById(map);
      } catch {
        if (!cancelled) setLinguistPaymentNotesById({});
      }
    };
    fetchNotes();
    return () => {
      cancelled = true;
    };
  }, [offerLinguists]);

  useEffect(() => {
    if (isNotApplicableLinguist(linguist)) {
      setRate(0);
      setRateSimultaneous(0);
      setRateHelperText("");
      setTwoHoursMin(false);
    }
  }, [linguist]);

  // On mount: if a linguist was prefilled (from PreAssign), auto-populate rate
  useEffect(() => {
    if (!isNewAssignment) return;
    if (!linguist?.id || isNotApplicableLinguist(linguist)) return;
    if (
      !Array.isArray(linguist.LinguistLanguages) ||
      linguist.LinguistLanguages.length === 0
    )
      return;

    const rateRaw = linguist.LinguistLanguages[0]?.Rate;
    const rateNum = Number(rateRaw);
    if (!Number.isNaN(rateNum) && rateNum > 0) {
      setRate(rateNum);
      // Simultaneous rate defaults to the SAME rate (no auto markup) —
      // special treatment is applied manually when needed.
      setRateSimultaneous(rateNum);
      setTwoHoursMin(linguist.MinimumHours === 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch bulk date counts on component mount (60 days back, 180 days forward)
  useEffect(() => {
    const fetchDateCounts = async () => {
      setDateCountsLoading(true);
      try {
        const today = dayjs().tz(APP_TIMEZONE);
        const startDate = today.subtract(60, "day").format("YYYY-MM-DD");
        const endDate = today.add(180, "day").format("YYYY-MM-DD");

        const response = await api.get(
          "/services/interpretations/date-counts",
          {
            params: { startDate, endDate },
          },
        );

        // Convert array to object for fast lookup
        const cache = {};
        response.data.forEach((item) => {
          cache[item.date] = parseInt(item.count, 10);
        });

        setDateCountsCache(cache);
      } catch (error) {
        console.error("Error fetching date counts:", error);
      } finally {
        setDateCountsLoading(false);
      }
    };

    fetchDateCounts();
  }, []);

  // Get count for a specific date (with on-demand fallback)
  const getDateCount = useCallback(
    async (dateValue) => {
      if (!dateValue) {
        return 0;
      }

      const dateStr = dayjs(dateValue)
        .tz(APP_TIMEZONE, true)
        .format("YYYY-MM-DD");

      // Check cache first
      if (dateCountsCache.hasOwnProperty(dateStr)) {
        return dateCountsCache[dateStr];
      }

      // Date not in cache, fetch on-demand
      try {
        const response = await api.get("/services/interpretations/date-count", {
          params: { date: dateStr },
        });

        const count = parseInt(response.data.count, 10);

        // Add to cache for future use
        setDateCountsCache((prev) => ({
          ...prev,
          [dateStr]: count,
        }));

        return count;
      } catch (error) {
        console.error("[getDateCount] Error fetching date count:", error);
        return 0;
      }
    },
    [dateCountsCache],
  );

  // Generate project number from date and count
  const generateProjectNumber = useCallback((dateValue, count) => {
    if (!dateValue) return "";

    const baseDate = dayjs(dateValue).tz(APP_TIMEZONE, true);
    const index = count + 1;
    const paddedIndex = index < 10 ? `0${index}` : `${index}`;
    return baseDate.format("YYMMDD") + paddedIndex;
  }, []);

  const handleDaysChange = (days) => {
    setTempSelectedDays(days);

    if (days.length === 7 && period === "Week") {
      setPeriod("Day");
      handleRecursionChange(
        date,
        repeatEvery,
        "Day",
        repeatUntilDate,
        days,
        showRecurring,
      );
    } else if (days.length < 7 && period === "Day") {
      setPeriod("Week");
      handleRecursionChange(
        date,
        repeatEvery,
        "Week",
        repeatUntilDate,
        days,
        showRecurring,
      );
    } else
      handleRecursionChange(
        date,
        repeatEvery,
        period,
        repeatUntilDate,
        days,
        showRecurring,
      );
  };

  const handleRecursionChange = async (
    date,
    repeatEvery,
    period,
    repeatUntil,
    days,
    recurring,
  ) => {
    let finalRecursionText;

    if (!recurring) return;

    if (
      date === null ||
      date === "" ||
      !repeatUntil ||
      !repeatUntil.isValid?.()
    ) {
      setRecursionStatusText("Please set a date first");
      return;
    } else if (period === "Month") {
      const repeatEveryText =
        repeatEvery === 1
          ? " of Each Month until "
          : repeatEvery === 2
            ? " every Other Month "
            : " every " + repeatEvery + " Months ";

      finalRecursionText =
        "Assignment will be repeated on day " +
        date.format("DD") +
        repeatEveryText +
        repeatUntil.format("dddd, MMMM DD, YYYY");
    } else if (period === "Day") {
      const repeatEveryText =
        repeatEvery === 1 ? "" : repeatEvery === 2 ? "Other " : repeatEvery;

      const periodText =
        repeatEvery === 1 || repeatEvery === 2 ? period : period + "s";

      finalRecursionText =
        "Assignment will be repeated every " +
        repeatEveryText +
        " " +
        periodText +
        " until " +
        repeatUntil.format("dddd, MMMM DD, YYYY");
    } else {
      const daysText = formatDays(days);

      const repeatEveryText =
        repeatEvery === 1
          ? ""
          : repeatEvery === 2
            ? "Other "
            : repeatEvery + " ";

      const periodText =
        repeatEvery === 1 || repeatEvery === 2 ? period : period + "s";

      finalRecursionText =
        "Assignment will be repeated every " +
        repeatEveryText +
        "" +
        periodText +
        " on " +
        daysText +
        " until " +
        repeatUntil.format("dddd, MMMM DD, YYYY");
    }

    const tempRecurrenceDates = await getRecurrenceDates(
      date,
      repeatEvery,
      period,
      repeatUntil,
      days,
    );

    setRecurrenceDates(tempRecurrenceDates);

    const totalAssignment =
      tempRecurrenceDates === null
        ? ""
        : ".\nThere will be " + tempRecurrenceDates.length + " in total.";

    setRecursionStatusText(finalRecursionText + totalAssignment);
  };

  const dayIndex = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  function formatDays(days) {
    if (!days?.length) return "";

    const full = days.map((d) => dayjs().day(dayIndex[d]).format("dddd"));
    if (full.length === 1) return full[0];
    if (full.length === 2) return `${full[0]} and ${full[1]}`;
    return `${full.slice(0, -1).join(", ")}, and ${full[full.length - 1]}`;
  }

  const normalizeWeekday = (d) => {
    if (typeof d === "number" && d >= 0 && d <= 6) return d;

    if (typeof d === "string") {
      const key = d.trim().toLowerCase();
      const map = {
        sun: 0,
        sunday: 0,
        mon: 1,
        monday: 1,
        tue: 2,
        tues: 2,
        tuesday: 2,
        wed: 3,
        weds: 3,
        wednesday: 3,
        thu: 4,
        thur: 4,
        thurs: 4,
        thursday: 4,
        fri: 5,
        friday: 5,
        sat: 6,
        saturday: 6,
      };
      if (key in map) return map[key];
    }
    return null;
  };

  const getRecurrenceDates = async (
    date,
    repeatEvery = 1,
    period = "Day",
    repeatUntil,
    days = [],
  ) => {
    const start = dayjs(date);
    const end = dayjs(repeatUntil);

    if (
      !start.isValid() ||
      !end.isValid() ||
      repeatEvery < 1 ||
      start.isAfter(end)
    ) {
      return [];
    }

    // Preserve the time of day from the original "date"
    const baseTime = {
      hour: start.hour(),
      minute: start.minute(),
      second: start.second(),
      millisecond: start.millisecond(),
    };

    const withBaseTime = (d) =>
      d
        .hour(baseTime.hour)
        .minute(baseTime.minute)
        .second(baseTime.second)
        .millisecond(baseTime.millisecond);

    const results = [];
    const seen = new Set(); // avoid duplicates when multiple weekdays hit the same date
    const datesToFetch = []; // Collect all dates we need counts for

    if (period === "Day") {
      for (
        let cur = start;
        !cur.isAfter(end);
        cur = cur.add(repeatEvery, "day")
      ) {
        const instance = withBaseTime(cur);
        const key = instance.valueOf();
        if (!seen.has(key)) {
          seen.add(key);
          datesToFetch.push(instance);
        }
      }
    } else if (period === "Week") {
      // Normalize selected weekdays; default to the start date's weekday if none provided
      let selected = Array.isArray(days) ? days : [];
      selected = selected
        .map(normalizeWeekday)
        .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);

      if (selected.length === 0) {
        selected = [start.day()];
      }

      // Sort and unique
      selected = Array.from(new Set(selected)).sort((a, b) => a - b);

      for (const wd of selected) {
        // Find the first occurrence on/after start that falls on this weekday
        const delta = (wd - start.day() + 7) % 7; // 0..6 days forward
        let first = start.add(delta, "day");

        // Walk forward in blocks of repeatEvery weeks for this weekday
        for (
          let cur = first;
          !cur.isAfter(end);
          cur = cur.add(repeatEvery * 7, "day")
        ) {
          const instance = withBaseTime(cur);
          const key = instance.valueOf();
          if (!seen.has(key)) {
            seen.add(key);
            datesToFetch.push(instance);
          }
        }
      }
    } else if (period === "Month") {
      // Repeat on the same day-of-month/time as the start date
      for (
        let i = 0, cur = start;
        !cur.isAfter(end);
        i += 1, cur = start.add(i * repeatEvery, "month")
      ) {
        const instance = withBaseTime(cur);
        const key = instance.valueOf();
        if (!seen.has(key)) {
          seen.add(key);
          datesToFetch.push(instance);
        }
      }
    }

    // Fetch counts for all dates in parallel
    const counts = await Promise.all(
      datesToFetch.map(async (instance) => {
        const count = await getDateCount(instance);
        return { instance, count };
      }),
    );

    // Generate project numbers with the fetched counts
    counts.forEach(({ instance, count }) => {
      const projectNum = generateProjectNumber(instance, count);
      results.push({ time: instance, projectNumber: projectNum });
    });

    // Ensure chronological order
    results.sort((a, b) => a.time.valueOf() - b.time.valueOf());
    return results;
  };

  const [interpretationStatusError, setInterpretationStatusError] =
    useState(false);
  const [accountChargeError, setAccountChargeError] = useState(false);
  const [totalCostError, setTotalCostError] = useState(false);
  // Cost-field errors derived from costErrors map
  const linguistPayError = !!costErrors.linguistPay;
  const setLinguistPayError = (v) =>
    setCostErrors((prev) => ({ ...prev, linguistPay: v }));
  const setAdditionalCostError = (v) =>
    setCostErrors((prev) => ({ ...prev, additionalCost: v }));
  const [modeError, setModeError] = useState(false);
  const [rateError, setRateError] = useState(false);
  const [clientRateError, setClientRateError] = useState(false);
  const [clientRateSimultaneousError, setClientRateSimultaneousError] =
    useState(false);
  const [rateSimultaneousError, setRateSimultaneousError] = useState(false);
  const setAdditionalLinguistCostError = (v) =>
    setCostErrors((prev) => ({ ...prev, additionalLinguistCost: v }));
  const setEquipmentCostError = (v) =>
    setCostErrors((prev) => ({ ...prev, equipmentCost: v }));
  const [clientRateDiscountError, setClientRateDiscountError] = useState(false);
  const [timeStartError, setTimeStartError] = useState(false);
  const [timeEndError, setTimeEndError] = useState(false);
  useState(false);
  const [actualStartError, setActualStartError] = useState(false);
  const [actualEndError, setActualEndError] = useState(false);
  const [schedulingTimeError, setSchedulingTimeError] = useState(false);
  const [mileageRoundtripError, setMileageRoundtripError] = useState(false);
  const setParkingFeesTollsError = (v) =>
    setCostErrors((prev) => ({ ...prev, parkingFeesTolls: v }));
  const setMileageCostError = (v) =>
    setCostErrors((prev) => ({ ...prev, mileageCost: v }));
  const [detailsError, setDetailsError] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [costRows, setCostRows] = useState([]);
  // Amounts for custom DB cost types (any type not in the built-in list),
  // keyed by typeKey so get/setCostValueByType work for ALL cost types.
  const [customCostValuesByType, setCustomCostValuesByType] = useState({});
  const [nextCostRowId, setNextCostRowId] = useState(1);
  const [originalCostValuesByType, setOriginalCostValuesByType] = useState({});

  // Travel Time is only "in play" while its row is IN the cost list
  // (2026-09-23, per Amine): removing the row is how travel time is excluded
  // from an assignment. Three things key off this flag — the totals ignore the
  // type while the row is gone (no more "still accounted for" / ghost
  // "+ 0.00" entries), the auto-derive effect below does not re-create the
  // removed row from the saved duration, and the legacy TravelTimeCost column
  // is explicitly zeroed on save so the server can never re-derive a
  // travel-time cost line from a stale value. Re-adding the row resumes the
  // derivation from the saved duration.
  const hasTravelTimeRow = costRows.some(
    (row) => row.typeKey === "travelTimeCost",
  );

  // Same rule for Mileage (2026-09-23, per Amine): the auto-calc effect must
  // not derive a cost while the Mileage row is absent — the derived value
  // would re-create the removed row through the keep-rows-in-sync effect.
  const hasMileageRow = costRows.some((row) => row.typeKey === "mileageCost");

  // costTypeRows: raw API response — ALL types for this category, not just known ones
  const [costTypeRows, setCostTypeRows] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadCostTypeRules = async () => {
      try {
        const response = await api.get(
          "/serviceCosts/types/by-category/Interpretation",
        );
        const rows = Array.isArray(response.data) ? response.data : [];

        if (!isMounted) return;

        setCostTypeRows(rows);
      } catch (error) {
        console.error("Error loading interpretation cost type rules:", error);
      }
    };

    loadCostTypeRules();

    return () => {
      isMounted = false;
    };
  }, []);

  // costTypeOptions: built from all API rows so new DB types appear automatically.
  // Falls back to INTERPRETATION_BASE_COST_TYPE_OPTIONS before first API load.
  const costTypeOptions = useMemo(() => {
    const toKey = (str) =>
      str ? str.charAt(0).toLowerCase() + str.slice(1) : str;
    if (costTypeRows.length === 0) {
      return INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => ({
        ...opt,
        isEnabled: true,
        effectiveIncludeInTotalCost: opt.defaultIncludeInTotalCost,
        effectiveIncludeInAccountCharge: opt.defaultIncludeInAccountCharge,
        effectiveIncludeInLinguistPay: opt.includeInLinguistPay ?? false,
      }));
    }
    const rowsMapped = costTypeRows.map((row) => {
      const normalizedRowName = normalizeCostTypeName(row.Name);
      const aliasBaseKey =
        INTERPRETATION_COST_TYPE_NAME_ALIASES[normalizedRowName];
      const base = INTERPRETATION_BASE_COST_TYPE_OPTIONS.find(
        (b) =>
          b.sourceLegacyField === row.SourceLegacyField ||
          normalizeCostTypeName(b.costTypeName) === normalizedRowName ||
          normalizeCostTypeName(b.label) === normalizedRowName ||
          b.key === aliasBaseKey,
      );
      const key = base?.key || toKey(row.SourceLegacyField || row.Name);
      return {
        key,
        label: base?.label || row.Name,
        costTypeName: row.Name,
        sourceLegacyField: row.SourceLegacyField || null,
        includeInLinguistPay: base?.includeInLinguistPay ?? false,
        defaultIncludeInTotalCost:
          base?.defaultIncludeInTotalCost ?? row.EffectiveIncludeInTotalCost,
        defaultIncludeInAccountCharge:
          base?.defaultIncludeInAccountCharge ??
          row.EffectiveIncludeInAccountCharge,
        isEnabled: row.IsEnabledForCategory,
        effectiveIncludeInTotalCost: row.EffectiveIncludeInTotalCost,
        effectiveIncludeInAccountCharge: row.EffectiveIncludeInAccountCharge,
        effectiveIncludeInLinguistPay:
          row.EffectiveIncludeInLinguistPay != null
            ? Boolean(row.EffectiveIncludeInLinguistPay)
            : (base?.includeInLinguistPay ?? false),
      };
    });

    const mappedKeys = new Set(rowsMapped.map((row) => row.key));
    const missingBaseRows = INTERPRETATION_BASE_COST_TYPE_OPTIONS.filter(
      (base) => !mappedKeys.has(base.key),
    ).map((base) => ({
      ...base,
      isEnabled: true,
      effectiveIncludeInTotalCost: base.defaultIncludeInTotalCost,
      effectiveIncludeInAccountCharge: base.defaultIncludeInAccountCharge,
      effectiveIncludeInLinguistPay: base.includeInLinguistPay ?? false,
    }));

    return [...rowsMapped, ...missingBaseRows];
  }, [costTypeRows]);

  // Per-row inclusion flags: stored line flags win; fall back to the cost
  // type's effective defaults for pre-migration lines / new rows.
  // Payment Fees are ALWAYS excluded from Account Charge (Amine decision).
  const getRowInclusionFlags = useCallback(
    (typeKey, line) => {
      const option = costTypeOptions.find((opt) => opt.key === typeKey);
      const includeInTotalCost =
        line?.IncludeInTotalCost != null
          ? Boolean(line.IncludeInTotalCost)
          : Boolean(option?.effectiveIncludeInTotalCost ?? true);
      const includeInLinguistPay =
        line?.IncludeInLinguistPay != null
          ? Boolean(line.IncludeInLinguistPay)
          : Boolean(
              option?.effectiveIncludeInLinguistPay ??
                option?.includeInLinguistPay ??
                false,
            );
      const includeInAccountCharge =
        typeKey === "paymentFees"
          ? false
          : line?.IncludeInAccountCharge != null
            ? Boolean(line.IncludeInAccountCharge)
            : Boolean(option?.effectiveIncludeInAccountCharge ?? false);
      return { includeInTotalCost, includeInLinguistPay, includeInAccountCharge };
    },
    [costTypeOptions],
  );

  // Dynamic lookup — handles known types and any new DB types
  const resolveCostTypeKeyFromLine = useCallback(
    (line) => {
      const sourceLegacyField =
        line?.SourceLegacyField || line?.CostType?.SourceLegacyField || "";
      const costTypeName =
        line?.CostTypeName || line?.CostType?.Name || line?.CustomLabel || "";
      if (sourceLegacyField) {
        const opt = costTypeOptions.find(
          (o) =>
            String(o.sourceLegacyField || "").toLowerCase() ===
            sourceLegacyField.toLowerCase(),
        );
        if (opt) return opt.key;
        const base = INTERPRETATION_BASE_COST_TYPE_OPTIONS.find(
          (o) =>
            String(o.sourceLegacyField || "").toLowerCase() ===
            sourceLegacyField.toLowerCase(),
        );
        if (base) return base.key;
      }
      if (costTypeName) {
        const normalizedName = normalizeCostTypeName(costTypeName);
        const aliasKey = INTERPRETATION_COST_TYPE_NAME_ALIASES[normalizedName];
        if (aliasKey) return aliasKey;

        const opt = costTypeOptions.find(
          (o) =>
            normalizeCostTypeName(o.costTypeName) === normalizedName ||
            normalizeCostTypeName(o.label) === normalizedName,
        );
        if (opt) return opt.key;

        const base = INTERPRETATION_BASE_COST_TYPE_OPTIONS.find(
          (o) =>
            normalizeCostTypeName(o.costTypeName) === normalizedName ||
            normalizeCostTypeName(o.label) === normalizedName,
        );
        if (base) return base.key;
      }
      return null;
    },
    [costTypeOptions],
  );

  const getInitialCostTypeKeys = useCallback(
    (data) => {
      const preferredOrder = [
        "linguistPay",
        ...costTypeOptions
          .filter((o) => o.key !== "linguistPay")
          .map((o) => o.key),
      ];

      const nonZeroFromCostLines = new Set(
        (Array.isArray(data?.CostLines) ? data.CostLines : [])
          .filter((line) => Number(line?.Amount || 0) !== 0)
          .map((line) => resolveCostTypeKeyFromLine(line))
          .filter(Boolean),
      );

      const hasCostLinesData = nonZeroFromCostLines.size > 0;

      const nonZeroFromLegacy = new Set(
        INTERPRETATION_BASE_COST_TYPE_OPTIONS.filter(
          (opt) => Number(data?.[opt.sourceLegacyField] || 0) !== 0,
        ).map((opt) => opt.key),
      );

      const activeSet = hasCostLinesData
        ? nonZeroFromCostLines
        : nonZeroFromLegacy;

      return preferredOrder.filter(
        (key) => key === "linguistPay" || activeSet.has(key),
      );
    },
    [resolveCostTypeKeyFromLine, costTypeOptions],
  );

  const getNonZeroLegacyTypeKeys = useCallback((data) => {
    return INTERPRETATION_BASE_COST_TYPE_OPTIONS.filter((opt) => {
      const value = Number(data?.[opt.sourceLegacyField] || 0);
      return Number.isFinite(value) && value !== 0;
    }).map((opt) => opt.key);
  }, []);

  const getCostLineAmountsByType = useCallback(
    (data) => {
      const amounts = {};
      const lines = Array.isArray(data?.CostLines) ? data.CostLines : [];

      lines.forEach((line) => {
        const key = resolveCostTypeKeyFromLine(line);
        const amount = Number(line?.Amount || 0);
        if (!key || Number.isNaN(amount)) return;
        amounts[key] = amount;
      });

      return amounts;
    },
    [resolveCostTypeKeyFromLine],
  );

  const getCostValueByType = useCallback(
    (typeKey) => {
      switch (typeKey) {
        case "mileageCost":
          return mileageCost;
        case "parkingFeesTolls":
          return parkingFeesTolls;
        case "linguistPay":
          return linguistPay;
        case "additionalLinguistCost":
          return additionalLinguistCost;
        case "additionalCost":
          return additionalCost;
        case "paymentFees":
          return paymentFees;
        case "equipmentCost":
          return equipmentCost;
        case "travelTimeCost":
          return travelTimeCost;
        default:
          return customCostValuesByType[typeKey] ?? "";
      }
    },
    [
      mileageCost,
      parkingFeesTolls,
      linguistPay,
      additionalLinguistCost,
      additionalCost,
      paymentFees,
      equipmentCost,
      travelTimeCost,
      customCostValuesByType,
    ],
  );

  const getOriginalCostValueByType = (typeKey) => {
    if (
      Object.prototype.hasOwnProperty.call(originalCostValuesByType, typeKey)
    ) {
      return originalCostValuesByType[typeKey];
    }

    switch (typeKey) {
      case "mileageCost":
        return overlayData.MileageCost;
      case "parkingFeesTolls":
        return overlayData.ParkingFeesTolls;
      case "linguistPay":
        return overlayData.LinguistPay;
      case "additionalLinguistCost":
        return overlayData.AdditionalLinguistCost;
      case "additionalCost":
        return overlayData.AdditionalCost;
      case "paymentFees":
        return overlayData.PaymentFees;
      case "equipmentCost":
        return overlayData.EquipmentCost;
      case "travelTimeCost":
        return overlayData.TravelTimeCost;
      default:
        return "";
    }
  };

  // Original linguist-side travel time cost for the loaded assignment
  // (duration x assignment linguist rate / 2). Used to highlight the
  // Travel Time Cost (Linguist) field only when the linguist-side value
  // actually changed — the client-side TravelTimeCost is a different
  // figure (consecutive client rate) and must not drive this border.
  const getOriginalTravelTimeLinguistCost = () => {
    const originalDuration = overlayData.TravelTimeDuration;
    if (!originalDuration || originalDuration === "00:00:00") return 0;
    const parts = String(originalDuration).split(":");
    const originalHours =
      (parseInt(parts[0], 10) || 0) + (parseInt(parts[1], 10) || 0) / 60;
    return ((originalHours * (parseFloat(overlayData.Rate) || 0)) / 2).toFixed(
      2,
    );
  };

  const setCostValueByType = useCallback((typeKey, value) => {
    switch (typeKey) {
      case "mileageCost":
        setMileageCost(value);
        setMileageCostError(value === null);
        break;
      case "parkingFeesTolls":
        setParkingFeesTolls(value);
        setParkingFeesTollsError(value === null);
        break;
      case "linguistPay":
        setLinguistPay(value);
        setLinguistPayError(value === null);
        break;
      case "additionalLinguistCost":
        setAdditionalLinguistCost(value);
        setAdditionalLinguistCostError(value === null);
        break;
      case "additionalCost":
        setAdditionalCost(value);
        setAdditionalCostError(value === null);
        break;
      case "paymentFees":
        setPaymentFees(value);
        break;
      case "equipmentCost":
        setEquipmentCost(value);
        setEquipmentCostError(value === null);
        break;
      case "travelTimeCost":
        setTravelTimeCost(value);
        break;
      default:
        setCustomCostValuesByType((prev) => ({
          ...prev,
          [typeKey]: value,
        }));
        break;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeCostRows = async () => {
      if (isNewAssignment) {
        const prefilledCostData = {
          ...prefilledData,
          CostLines:
            Array.isArray(prefilledData?.CostLines) &&
            prefilledData.CostLines.length > 0
              ? prefilledData.CostLines
              : Array.isArray(overlayData?.CostLines)
                ? overlayData.CostLines
                : [],
        };

        const initialTypeKeys = getInitialCostTypeKeys(prefilledCostData);
        const amountsFromCostLines =
          getCostLineAmountsByType(prefilledCostData);
        const nonZeroLegacyTypeKeys =
          getNonZeroLegacyTypeKeys(prefilledCostData);

        if (!isMounted) return;

        setOriginalCostValuesByType(amountsFromCostLines);

        Object.entries(amountsFromCostLines).forEach(([typeKey, amount]) => {
          if (Number(amount || 0) !== 0) {
            // Don't clobber Mileage Cost: either the user typed a custom
            // value, or auto-calc is (re)computing it with the current rate.
            if (
              typeKey === "mileageCost" &&
              (mileageCostManuallyEditedRef.current ||
                (!isNewAssignment &&
                  shouldAutoCalculateMileageCostRef.current))
            ) {
              return;
            }
            setCostValueByType(typeKey, String(amount));
          }
        });

        const ensuredTypeKeys = Array.from(
          new Set([
            "linguistPay",
            ...initialTypeKeys,
            ...nonZeroLegacyTypeKeys,
          ]),
        );

        const lineByTypeKey = {};
        (Array.isArray(prefilledCostData.CostLines)
          ? prefilledCostData.CostLines
          : []
        ).forEach((line) => {
          const key = resolveCostTypeKeyFromLine(line);
          if (key && !lineByTypeKey[key]) lineByTypeKey[key] = line;
        });

        const rows = ensuredTypeKeys.map((typeKey, index) => {
          const flags = getRowInclusionFlags(typeKey, lineByTypeKey[typeKey]);
          return {
            id: index + 1,
            typeKey,
            ...flags,
            originalIncludeInTotalCost: flags.includeInTotalCost,
            originalIncludeInLinguistPay: flags.includeInLinguistPay,
            originalIncludeInAccountCharge: flags.includeInAccountCharge,
          };
        });

        setCostRows(rows);
        setNextCostRowId(rows.length + 1);
        return;
      }

      let fetchedCostLines = [];
      if (overlayData?.id) {
        try {
          const response = await api.get(
            `/serviceCosts/service/${overlayData.id}/lines`,
          );
          fetchedCostLines = Array.isArray(response.data?.lines)
            ? response.data.lines
            : [];
        } catch (error) {
          console.error("Error loading service cost lines:", error);
        }
      }

      const overlayDataWithCostLines = {
        ...overlayData,
        CostLines:
          fetchedCostLines.length > 0
            ? fetchedCostLines
            : Array.isArray(overlayData?.CostLines)
              ? overlayData.CostLines
              : [],
      };

      const initialTypeKeys = getInitialCostTypeKeys(overlayDataWithCostLines);
      const amountsFromCostLines = getCostLineAmountsByType(
        overlayDataWithCostLines,
      );
      const nonZeroLegacyTypeKeys = getNonZeroLegacyTypeKeys(
        overlayDataWithCostLines,
      );

      if (!isMounted) return;

      setOriginalCostValuesByType(amountsFromCostLines);

      Object.entries(amountsFromCostLines).forEach(([typeKey, amount]) => {
        if (Number(amount || 0) !== 0) {
          // Don't clobber Mileage Cost: either the user typed a custom
          // value, or auto-calc is (re)computing it with the current rate.
          if (
            typeKey === "mileageCost" &&
            (mileageCostManuallyEditedRef.current ||
              shouldAutoCalculateMileageCostRef.current)
          ) {
            return;
          }
          setCostValueByType(typeKey, String(amount));
        }
      });

      const ensuredTypeKeys = Array.from(
        new Set([...initialTypeKeys, ...nonZeroLegacyTypeKeys]),
      );

      const lineByTypeKey = {};
      (Array.isArray(overlayDataWithCostLines.CostLines)
        ? overlayDataWithCostLines.CostLines
        : []
      ).forEach((line) => {
        const key = resolveCostTypeKeyFromLine(line);
        if (key && !lineByTypeKey[key]) lineByTypeKey[key] = line;
      });

      const rows = ensuredTypeKeys.map((typeKey, index) => {
        const flags = getRowInclusionFlags(typeKey, lineByTypeKey[typeKey]);
        return {
          id: index + 1,
          typeKey,
          ...flags,
          originalIncludeInTotalCost: flags.includeInTotalCost,
          originalIncludeInLinguistPay: flags.includeInLinguistPay,
          originalIncludeInAccountCharge: flags.includeInAccountCharge,
        };
      });

      setCostRows(rows);
      setNextCostRowId(rows.length + 1);
    };

    initializeCostRows();

    return () => {
      isMounted = false;
    };
  }, [
    isNewAssignment,
    prefilledData,
    overlayData,
    overlayData?.id,
    getCostLineAmountsByType,
    getInitialCostTypeKeys,
    getNonZeroLegacyTypeKeys,
    getRowInclusionFlags,
    resolveCostTypeKeyFromLine,
    setCostValueByType,
  ]);

  // Keep cost rows in sync with non-zero values so any cost with value is always visible.
  useEffect(() => {
    const visibleTypeKeys = new Set(costRows.map((row) => row.typeKey));

    const missingKeys = costTypeOptions
      .filter((opt) => opt.key !== "linguistPay")
      .map((opt) => opt.key)
      .filter((key) => {
        const value = Number(getCostValueByType(key) || 0);
        return (
          Number.isFinite(value) && value !== 0 && !visibleTypeKeys.has(key)
        );
      });

    if (missingKeys.length === 0) return;

    setCostRows((prev) => {
      const alreadyUsed = new Set(prev.map((row) => row.typeKey));
      const rowsToAdd = missingKeys
        .filter((key) => !alreadyUsed.has(key))
        .map((key, index) => ({
          id: nextCostRowId + index,
          typeKey: key,
          ...getRowInclusionFlags(key),
        }));

      return rowsToAdd.length ? [...prev, ...rowsToAdd] : prev;
    });

    setNextCostRowId((prev) => prev + missingKeys.length);
  }, [
    costRows,
    costTypeOptions,
    getCostValueByType,
    nextCostRowId,
    mileageCost,
    parkingFeesTolls,
    additionalCost,
    additionalLinguistCost,
    paymentFees,
    equipmentCost,
    travelTimeCost,
  ]);

  const addCostRow = () => {
    const usedKeys = new Set(costRows.map((row) => row.typeKey));
    const nextOption = costTypeOptions.find(
      (option) =>
        option.key !== "linguistPay" &&
        option.isEnabled &&
        !usedKeys.has(option.key),
    );
    if (!nextOption) return;

    setCostRows((prev) => [
      ...prev,
      {
        id: nextCostRowId,
        typeKey: nextOption.key,
        ...getRowInclusionFlags(nextOption.key),
      },
    ]);
    setNextCostRowId((prev) => prev + 1);
  };

  const removeCostRow = (rowId) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey === "linguistPay") {
      return;
    }
    if (row?.typeKey) {
      setCostValueByType(row.typeKey, "");
    }
    // Removing the Mileage row clears the roundtrip miles too (2026-09-23,
    // per Amine): miles-without-cost is invalid data on this platform (the
    // 2026-05-08 cleanup migrations and the mileage-rate-mismatch task rule
    // both treat it as an error), and leaving the miles behind was what kept
    // mileage resurfacing. Re-adding the row starts fresh.
    if (row?.typeKey === "mileageCost") {
      setMileageRoundtrip("");
    }
    setCostRows((prev) => prev.filter((item) => item.id !== rowId));
  };

  const updateCostRowType = (rowId, nextTypeKey) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey === "linguistPay" && nextTypeKey !== "linguistPay") {
      return;
    }
    if (row?.typeKey && row.typeKey !== nextTypeKey) {
      setCostValueByType(row.typeKey, "");
      // Switching the Mileage row to another cost type leaves mileage
      // entirely — clear the roundtrip miles as well (see removeCostRow).
      if (row.typeKey === "mileageCost") {
        setMileageRoundtrip("");
      }
    }

    if (nextTypeKey === "mileageCost") {
      setHasExplicitMileageEdit(true);
      applyShouldAutoCalculateMileageCost(true);
    }

    setCostRows((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? {
              ...item,
              typeKey: nextTypeKey,
              ...getRowInclusionFlags(nextTypeKey),
            }
          : item,
      ),
    );
  };

  const updateCostRowFlag = (rowId, flag, value) => {
    setCostRows((prev) =>
      prev.map((item) =>
        item.id === rowId ? { ...item, [flag]: value } : item,
      ),
    );
  };

  const [progress, setProgress] = useState("");
  const [progressMessage, setProgressMessage] = useState("");
  const [shakingFields, setShakingFields] = useState(new Set());

  const [, setHasChanges] = useState(false);

  const initialDataRef = useRef(null);
  const updateCurrentProgressRef = useRef(() => {});
  const addBulkInterpretationsRef = useRef(() => {});
  const addNewInterpretationRef = useRef(() => {});
  const updateInterpretationRef = useRef(() => {});
  const saveInterpretationRef = useRef(() => {});
  const attemptSaveRef = useRef(() => {});
  const noteInputRef = useRef(null);
  const backButtonLongPressTimerRef = useRef(null);
  const backButtonLongPressTriggeredRef = useRef(false);

  const randomInt = useCallback((min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }, []);

  const pickRandom = useCallback(
    (items) => {
      if (!Array.isArray(items) || items.length === 0) return null;
      return items[randomInt(0, items.length - 1)];
    },
    [randomInt],
  );

  const clearBackButtonLongPress = useCallback(() => {
    if (backButtonLongPressTimerRef.current) {
      clearTimeout(backButtonLongPressTimerRef.current);
      backButtonLongPressTimerRef.current = null;
    }
  }, []);

  const filterLinguistsByLanguage = useCallback(
    (linguists, lang, showAllLinguists) => {
      if (!filterLingsByLangs) return linguists;

      if (!lang || lang === "") return linguists;

      return linguists.filter((linguist) => {
        if (linguist.Name === "N/A") return true;

        const languageMatches =
          linguist.LinguistLanguages &&
          linguist.LinguistLanguages.some(
            (lg) => lg.Language && lg.Language.Name === lang,
          );
        const statusMatches = showAllLinguists || linguist.Status === "Current";

        return languageMatches && statusMatches;
      });
    },
    [filterLingsByLangs],
  );

  const fillDetails = useCallback((requestor, name, language, type) => {
    const req = requestor ? requestor.trim() : "";
    const nm = name ? name.trim() : "";
    const lang = language ? language.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasLanguage = lang.length > 0;

    if (hasRequestor && hasName && hasLanguage) {
      setDetails(`${req} - ${nm} - ${lang} ${type}`);
    } else if (hasRequestor && !hasName && hasLanguage) {
      setDetails(`${req} - ${lang} ${type}`);
    } else if (!hasRequestor && hasName && hasLanguage) {
      setDetails(`${nm} - ${lang} ${type}`);
    } else if (hasRequestor && hasName && !hasLanguage) {
      setDetails(`${req} - ${nm} - ${type}`);
    } else if (hasRequestor && !hasName && !hasLanguage) {
      setDetails(`${req} - ${type}`);
    } else if (!hasRequestor && hasName && !hasLanguage) {
      setDetails(`${nm} - ${type}`);
    } else if (!hasRequestor && !hasName && hasLanguage) {
      setDetails(`${lang} ${type}`);
    } else {
      setDetails("");
    }
  }, []);

  const randomizeFormForAmine = useCallback(async () => {
    if (!isAmineUser || !isNewAssignment || !isEditable) return;

    const randomDate = dayjs().add(randomInt(-30, 120), "day").startOf("day");
    const randomName = `${pickRandom(LOREM_NAME_SEEDS) || "Lorem ipsum"} ${randomInt(100, 999)}`;
    const randomAddress = `${pickRandom(LOREM_ADDRESS_SEEDS) || "100 Lorem Blvd"}, Suite ${randomInt(1, 40)}`;
    const randomType =
      pickRandom(interpretationTypeOptions) || interpretationType;

    const randomClient = pickRandom(overlayData.Clients || []);
    const randomRequestor = pickRandom(randomClient?.Requestors || []);
    const randomLanguage = pickRandom(overlayData.Languages || []);

    const startHour = randomInt(8, 13);
    const startMinute = pickRandom([0, 15, 30, 45]) ?? 0;
    const randomStartTime = dayjs()
      .hour(startHour)
      .minute(startMinute)
      .second(0)
      .millisecond(0);

    const minEndHour = Math.max(startHour + 1, 9);
    const endHour = randomInt(minEndHour, 16);
    const endMinute = pickRandom([0, 15, 30, 45]) ?? 0;
    const randomEndTime = dayjs()
      .hour(endHour)
      .minute(endMinute)
      .second(0)
      .millisecond(0);

    const availableLinguistsForLanguage = filterLinguistsByLanguage(
      overlayData.Linguists || [],
      randomLanguage?.Name,
      showAllLinguists,
    ).filter((linguistOption) => !isNotApplicableLinguist(linguistOption));

    const randomLinguist = pickRandom(availableLinguistsForLanguage);

    const randomLinguistRateRaw =
      randomLinguist?.LinguistLanguages?.[0]?.Rate ?? "";
    const randomLinguistRateNum = Number(randomLinguistRateRaw);
    const randomLinguistRate = Number.isNaN(randomLinguistRateNum)
      ? ""
      : randomLinguistRateNum;

    const randomDuration = getDurationValue(randomStartTime, randomEndTime);
    const randomClientRate =
      randomClient && randomLanguage
        ? updateClientRate(randomClient, randomLanguage.id)
        : 0;

    setDate(randomDate);
    const randomDateCount = await getDateCount(randomDate);
    setProjectNumber(generateProjectNumber(randomDate, randomDateCount));
    setProjectNumberExists(false);
    setName(randomName);
    if (randomType) setInterpretationType(randomType);
    if (randomClient) setClient(randomClient);
    setRequestor(randomRequestor || "");
    if (randomLanguage) setLanguage(randomLanguage);
    if (randomLinguist) {
      setLinguist(randomLinguist);
      setOfferLinguists([randomLinguist]);
      if (randomLinguistRate !== "") {
        setRate(randomLinguistRate);
        setRateSimultaneous(randomLinguistRate);
      }
    } else {
      setLinguist("");
      setOfferLinguists([]);
    }
    setAddress(randomAddress);
    setTimeStart(randomStartTime);
    setTimeEnd(randomEndTime);
    setDuration(randomDuration);
    setTotalDuration(randomDuration);
    setTimeStartError(false);
    setTimeEndError(false);
    if (randomClientRate) {
      setClientRate(randomClientRate);
      setClientRateSimultaneous(randomClientRate);
    }

    fillDetails(
      randomRequestor?.Name,
      randomName,
      randomLanguage?.Name,
      randomType,
    );
  }, [
    fillDetails,
    generateProjectNumber,
    getDateCount,
    getDurationValue,
    interpretationTypeOptions,
    interpretationType,
    isAmineUser,
    isEditable,
    isNewAssignment,
    overlayData.Clients,
    overlayData.Languages,
    overlayData.Linguists,
    filterLinguistsByLanguage,
    pickRandom,
    randomInt,
    showAllLinguists,
    updateClientRate,
  ]);

  const handleBackButtonPressStart = useCallback(() => {
    if (!isAmineUser || !isNewAssignment) return;
    clearBackButtonLongPress();
    backButtonLongPressTriggeredRef.current = false;
    backButtonLongPressTimerRef.current = setTimeout(() => {
      backButtonLongPressTriggeredRef.current = true;
      void randomizeFormForAmine();
    }, LONG_PRESS_AUTOFILL_MS);
  }, [
    clearBackButtonLongPress,
    isAmineUser,
    isNewAssignment,
    randomizeFormForAmine,
  ]);

  const handleBackButtonPressEnd = useCallback(() => {
    clearBackButtonLongPress();
  }, [clearBackButtonLongPress]);

  const handleBackButtonClick = useCallback(() => {
    if (backButtonLongPressTriggeredRef.current) {
      backButtonLongPressTriggeredRef.current = false;
      return;
    }

    releaseLock();
    setOverlayName("");
  }, [releaseLock, setOverlayName]);

  useEffect(() => {
    return () => {
      clearBackButtonLongPress();
    };
  }, [clearBackButtonLongPress]);

  const handleNoteTagState = useCallback(
    (value, cursorPosition) => {
      if (!isEditable) {
        setIsMentionOpen(false);
        return;
      }
      const context = getTagContextAtCursor(value, cursorPosition);
      if (!context) {
        setIsMentionOpen(false);
        setMentionTrigger(null);
        setMentionQuery("");
        setMentionCursor(null);
        return;
      }
      setMentionTrigger(context.trigger);
      setMentionQuery(context.query);
      setMentionCursor(context.cursorPosition);
      setIsMentionOpen(true);
    },
    [isEditable],
  );

  useEffect(() => {
    let isMounted = true;
    const fetchMembers = async () => {
      try {
        const response = await api.get("/members");
        if (!isMounted) return;
        const allMembers = Array.isArray(response.data) ? response.data : [];
        setMembers(
          allMembers.filter((member) => !!getMemberMentionToken(member)),
        );
      } catch (error) {
        console.error("Error fetching mention members:", error);
        if (isMounted) setMembers([]);
      }
    };
    fetchMembers();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isMentionOpen || mentionTrigger !== "#" || !mentionQuery) {
      setServiceTagSuggestions([]);
      setIsMentionLoading(false);
      return;
    }
    const timeoutId = setTimeout(async () => {
      setIsMentionLoading(true);
      try {
        const response = await api.get(
          `/services/tag-search?query=${encodeURIComponent(mentionQuery)}&limit=10`,
        );
        setServiceTagSuggestions(
          Array.isArray(response.data) ? response.data : [],
        );
      } catch (error) {
        console.error("Error fetching service tag suggestions:", error);
        setServiceTagSuggestions([]);
      } finally {
        setIsMentionLoading(false);
      }
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [isMentionOpen, mentionQuery, mentionTrigger]);

  const userTagSuggestions = useMemo(() => {
    if (mentionTrigger !== "@") return [];
    const query = String(mentionQuery || "").toLowerCase();
    return members
      .filter((member) => {
        if (!query) return true;
        const name = String(member?.Name || "").toLowerCase();
        const email = String(member?.Email || "").toLowerCase();
        const token = getMemberMentionToken(member).toLowerCase();
        return (
          name.includes(query) || email.includes(query) || token.includes(query)
        );
      })
      .slice(0, 10);
  }, [members, mentionQuery, mentionTrigger]);

  const handleSelectServiceTag = useCallback(
    (service) => {
      const projectNumber = service?.ProjectNumber;
      if (projectNumber === null || projectNumber === undefined) return;
      const cursorPosition =
        typeof mentionCursor === "number" ? mentionCursor : note.length;
      const beforeCursor = note.slice(0, cursorPosition);
      const afterCursor = note.slice(cursorPosition);
      const replacedBefore = beforeCursor.replace(
        /(^|\s)#([a-zA-Z0-9._-]*)$/,
        (fullMatch, leadingWhitespace) =>
          `${leadingWhitespace}#${projectNumber}`,
      );
      const nextValue = `${replacedBefore} ${afterCursor.replace(/^\s*/, "")}`;
      const nextCursor = replacedBefore.length + 1;
      setNote(nextValue);
      setIsMentionOpen(false);
      setServiceTagSuggestions([]);
      setMentionQuery("");
      setMentionCursor(null);
      setMentionTrigger(null);
      requestAnimationFrame(() => {
        const input = noteInputRef.current;
        if (input && typeof input.focus === "function") {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(nextCursor, nextCursor);
          }
        }
      });
    },
    [mentionCursor, note],
  );

  const handleSelectUserTag = useCallback(
    (member) => {
      const mentionToken = getMemberMentionToken(member);
      if (!mentionToken) return;
      const cursorPosition =
        typeof mentionCursor === "number" ? mentionCursor : note.length;
      const beforeCursor = note.slice(0, cursorPosition);
      const afterCursor = note.slice(cursorPosition);
      const replacedBefore = beforeCursor.replace(
        /(^|\s)@([a-zA-Z0-9._-]*)$/,
        (fullMatch, leadingWhitespace) =>
          `${leadingWhitespace}@${mentionToken}`,
      );
      const nextValue = `${replacedBefore} ${afterCursor.replace(/^\s*/, "")}`;
      const nextCursor = replacedBefore.length + 1;
      setNote(nextValue);
      setIsMentionOpen(false);
      setMentionQuery("");
      setMentionCursor(null);
      setMentionTrigger(null);
      requestAnimationFrame(() => {
        const input = noteInputRef.current;
        if (input && typeof input.focus === "function") {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(nextCursor, nextCursor);
          }
        }
      });
    },
    [mentionCursor, note],
  );

  /**
   * Helper function to get shake animation styles
   * Apply to TextFields/Autocomplete that should shake when validation fails
   */
  const getShakeSx = (fieldId) => {
    const isShaking = shakingFields.has(fieldId);
    return isShaking
      ? {
          animation: "shake 0.6s",
          "@keyframes shake": {
            "0%, 100%": { transform: "translateX(0)" },
            "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
            "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
          },
        }
      : {};
  };

  const optionSwitchSx = {
    ml: 0.5,
    mr: 0.25,
    "& .MuiSwitch-track": {
      backgroundColor: theme.palette.common.white,
      opacity: "1 !important",
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: `${theme.palette.primary.main}40 !important`,
      borderColor: "transparent",
      opacity: "1 !important",
    },
    "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb": {
      backgroundColor: theme.palette.grey[400],
    },
  };

  const getBorderColorTimes = (newData, oldData, newData2) => {
    if (!isNewAssignment && newData !== oldData) {
      return theme.palette.info[500];
    } else {
      return newData === null || newData === ""
        ? newData2 === null && newData2 === ""
          ? theme.palette.primary.main
          : theme.palette.error.main
        : theme.palette.primary.main;
    }
  };

  /**
   * VALIDATION CONFIGURATION
   * Easily manage required fields and their validation behavior
   *
   * Structure: { fieldId, value, message, isRequired }
   * - isRequired: true = must be filled before save, false = shows in progress message
   */
  const getValidationFields = () => {
    // In-house linguists are paid monthly and legitimately have rate 0 — a 0
    // simultaneous rate is intentional when the base rate is 0.
    const isInHouseLinguist = Number(rate) === 0;
    return [
    // REQUIRED FIELDS (Must be filled)
    {
      fieldId: "Date",
      value: date,
      message: "Please add a Date",
      isRequired: true,
    },
    {
      fieldId: "ProjectNumber",
      value: projectNumber,
      message: "Please add a Project Number",
      isRequired: true,
    },
    {
      fieldId: "Name",
      value: name,
      message: "Please add a Name",
      isRequired: true,
    },
    {
      fieldId: "InterpretationType",
      value: interpretationType,
      message: "Please add an Interpretation Type",
      isRequired: true,
    },
    {
      fieldId: "Language",
      value: language,
      message: "Please add a Language",
      isRequired: true,
    },
    {
      fieldId: "Client",
      value: client,
      message: "Please add a Client",
      isRequired: true,
    },
    {
      fieldId: "ClientRate",
      value: clientRate,
      message: "Please add a Client Rate",
      isRequired: true,
    },
    // Simultaneous rates are required whenever the assignment mode is
    // Simultaneous — an unset/zero rate silently bills $0 for core time.
    // 0 is normalized to "" so it is treated as missing (matches save-time
    // validateFields behavior) — except for in-house linguists (base rate 0),
    // where a 0 simultaneous rate is intentional.
    ...(mode === "Simultaneous"
      ? [
          {
            fieldId: "RateSimultaneous",
            value:
              rateSimultaneous === null ||
              rateSimultaneous === undefined ||
              rateSimultaneous === "" ||
              (Number(rateSimultaneous) === 0 && !isInHouseLinguist)
                ? ""
                : rateSimultaneous,
            message: "Please add a Rate (Simultaneous)",
            isRequired: true,
          },
          {
            fieldId: "ClientRateSimultaneous",
            value:
              clientRateSimultaneous === 0 ||
              clientRateSimultaneous === "" ||
              clientRateSimultaneous === null
                ? ""
                : clientRateSimultaneous,
            message: "Please add a Client Rate (Simultaneous)",
            isRequired: true,
          },
        ]
      : []),
    {
      fieldId: "Requestor",
      value: requestor,
      message: "Please add a Requestor",
      isRequired: true,
    },
    {
      fieldId: "Linguist",
      value: linguist,
      message: "Please add a Linguist",
      // A linguist is optional while the assignment status is Requested
      // (pre-assign outreach flow); required for every other status.
      isRequired: !String(interpretationStatus || "").startsWith("Requested"),
    },

    // HIGHLY RECOMMENDED FIELDS (Show Progress)
    {
      fieldId: "Rate",
      value: rate,
      message: "Please add a Rate",
      isRequired: false,
    },
    {
      fieldId: "StartTime",
      value: timeStart,
      message: "Please add a Start Time",
      isRequired: false,
    },
    {
      fieldId: "EndTime",
      value: timeEnd,
      message: "Please add an End Time",
      isRequired: false,
    },
    {
      fieldId: "InterpretationStatus",
      value: interpretationStatus,
      message: "Please add an Interpretation Status",
      isRequired: false,
    },
    {
      fieldId: "LinguistPay",
      value: linguistPay,
      message: "Please add a Linguist Pay",
      isRequired: false,
    },
    {
      fieldId: "TotalCost",
      value: totalCost,
      message: "Please add a Total Cost",
      isRequired: false,
    },
    {
      fieldId: "AccountCharge",
      value: accountCharge,
      message: "Please add an Account Charge",
      isRequired: false,
    },
    {
      fieldId: "Details",
      value: details,
      message: "Please add Details",
      isRequired: false,
    },
    ];
  };

  /**
   * Trigger shake animation on missing required fields
   */
  const triggerMissingFieldsShake = () => {
    const validationFields = getValidationFields();
    const missingRequiredFields = validationFields.filter(
      (field) =>
        field.isRequired && (field.value === null || field.value === ""),
    );

    if (missingRequiredFields.length > 0) {
      const newShakingFields = new Set(
        missingRequiredFields.map((field) => field.fieldId),
      );
      setShakingFields(newShakingFields);

      // Remove shake effect after animation completes (600ms)
      setTimeout(() => {
        setShakingFields(new Set());
      }, 600);

      return false; // Validation failed
    }

    return true; // Validation passed
  };

  const updateCurrentProgress = () => {
    const validationFields = getValidationFields();
    const requiredFields = validationFields.filter((f) => f.isRequired);
    const recommendedFields = validationFields.filter((f) => !f.isRequired);

    // Check for missing required fields
    const missingRequiredField = requiredFields.find(
      (field) => field.value === null || field.value === "",
    );

    // Check for missing recommended fields
    const missingRecommendedField = recommendedFields.find(
      (field) => field.value === null || field.value === "",
    );

    // Determine progress
    const totalFields = validationFields.length;
    const filledFields = validationFields.filter(
      (field) => field.value !== null && field.value !== "",
    ).length;
    const percentProgress = (filledFields * 100) / totalFields;

    // Set message
    let message = "All Done!";
    if (missingRequiredField) {
      message = missingRequiredField.message;
    } else if (missingRecommendedField) {
      message = missingRecommendedField.message;
    }

    setProgressMessage(Number(percentProgress).toFixed(2) + "%: " + message);
    setProgress(percentProgress);
  };
  updateCurrentProgressRef.current = updateCurrentProgress;

  const handleClear = () => {
    const defaultLinguist = overlayData.Linguists?.find(
      (ling) => ling.id === 1,
    );

    setLinguist(defaultLinguist || { Name: "N/A", id: 1 });
    setOfferLinguists(defaultLinguist ? [defaultLinguist] : []);
    setLinguistInputValue("");

    setRateHelperText("");

    setRate(0);
    setRateSimultaneous(0);

    setTwoHoursMin(false);
  };

  const hasLinguistDistances = Object.keys(linguistDistancesById).length > 0;

  const linguistOptions = useMemo(
    () =>
      [...(overlayData.Linguists || [])]
        .map((linguistOption) => ({
          ...linguistOption,
          ClientServiceStats:
            linguistClientStatsById[Number(linguistOption?.id || 0)] ||
            EMPTY_LINGUIST_CLIENT_STATS,
          DistanceInfo:
            linguistDistancesById[Number(linguistOption?.id || 0)] ||
            EMPTY_LINGUIST_DISTANCE,
        }))
        .sort((left, right) => {
          // When driving distances are available, sort by closest first
          if (hasLinguistDistances) {
            const leftMiles = left?.DistanceInfo?.distanceMiles;
            const rightMiles = right?.DistanceInfo?.distanceMiles;
            // Linguists without a known distance go to the bottom
            if (leftMiles == null && rightMiles == null) {
              return String(left?.Name || "").localeCompare(
                String(right?.Name || ""),
              );
            }
            if (leftMiles == null) return 1;
            if (rightMiles == null) return -1;
            return leftMiles - rightMiles;
          }

          // Default: sort by completed assignments descending
          const completedDelta =
            (right?.ClientServiceStats?.completedAssignments || 0) -
            (left?.ClientServiceStats?.completedAssignments || 0);

          if (completedDelta !== 0) return completedDelta;
          return String(left?.Name || "").localeCompare(
            String(right?.Name || ""),
          );
        }),
    [
      overlayData.Linguists,
      linguistClientStatsById,
      linguistDistancesById,
      hasLinguistDistances,
    ],
  );

  const renderLinguistOption = useCallback(
    ({ key, ownerState, ...props }, option) => {
      if (option?.inputValue || !option?.id) {
        return (
          <Box key={key} component="li" {...props}>
            {option?.Name || ""}
          </Box>
        );
      }

      const stats = option.ClientServiceStats || EMPTY_LINGUIST_CLIENT_STATS;
      const distanceMiles = option?.DistanceInfo?.distanceMiles ?? null;

      const statsLabel =
        stats.cancelledUnableToFillAssignments > 0
          ? `Completed: ${stats.completedAssignments} | Unable to fill: ${stats.cancelledUnableToFillAssignments}`
          : `Completed: ${stats.completedAssignments}`;

      const distanceLabel =
        distanceMiles != null
          ? `${distanceMiles} mi`
          : isCalculatingLinguistDistances
            ? "Calculating..."
            : null;

      const rightLabel = distanceLabel
        ? `${distanceLabel} | ${statsLabel}`
        : statsLabel;

      return (
        <Box
          key={key}
          component="li"
          {...props}
          sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            {option?.Name || ""}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
          >
            {rightLabel}
          </Typography>
        </Box>
      );
    },
    [isCalculatingLinguistDistances],
  );

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const linguistIds = (overlayData.Linguists || [])
      .map((linguistOption) => Number(linguistOption?.id || 0))
      .filter((id) => id > 0);

    if (!clientId || !linguistIds.length) {
      setLinguistClientStatsById({});
      return undefined;
    }

    const fetchLinguistClientStats = async () => {
      try {
        const response = await api.post("/linguists/client-service-stats", {
          clientId,
          linguistIds,
        });

        if (!isCancelled) {
          setLinguistClientStatsById(response.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch linguist client service stats:", error);
        if (!isCancelled) {
          setLinguistClientStatsById({});
        }
      }
    };

    fetchLinguistClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client, overlayData.Linguists]);

  // Fetch driving distances from each linguist's address to the assignment address
  // when an address is entered and a language is selected (which filters the linguist list).
  useEffect(() => {
    let isCancelled = false;

    const trimmedAddress = (address || "").trim();
    const hasAddress = trimmedAddress.length > 0;
    const hasLanguage = Boolean(language?.id || language?.Name);
    const isInPerson =
      String(interpretationType || "")
        .trim()
        .toLowerCase() === "in-person interpretation";

    if (!isInPerson) {
      setIsCalculatingLinguistDistances(false);
      setLinguistDistancesById({});
      return undefined;
    }

    if (!hasAddress || !hasLanguage) {
      setIsCalculatingLinguistDistances(false);
      setLinguistDistancesById({});
      return undefined;
    }

    // Filter to only linguists visible in the dropdown (language-matched, same as filterLinguistsByLanguage)
    const filteredLinguists = filterLinguistsByLanguage(
      overlayData.Linguists || [],
      language?.Name,
      showAllLinguists,
    );

    const linguistIds = filteredLinguists
      .map((linguistOption) => Number(linguistOption?.id || 0))
      .filter((id) => id > 0);

    if (!linguistIds.length) {
      setIsCalculatingLinguistDistances(false);
      setLinguistDistancesById({});
      return undefined;
    }

    const requestId = linguistDistanceRequestIdRef.current + 1;
    linguistDistanceRequestIdRef.current = requestId;
    setIsCalculatingLinguistDistances(true);

    // Debounce: wait 1 second after the address stops changing before calling the API
    const timeoutId = setTimeout(async () => {
      try {
        const response = await api.post("/geocoding/driving-distances", {
          destinationAddress: trimmedAddress,
          linguistIds,
        });

        const distanceData = response.data || {};

        if (
          !isCancelled &&
          linguistDistanceRequestIdRef.current === requestId
        ) {
          setLinguistDistancesById(distanceData);
          setIsCalculatingLinguistDistances(false);
        }
      } catch (error) {
        console.error(
          "[Distances] API error:",
          error?.response?.status,
          error?.response?.data || error.message,
        );
        if (
          !isCancelled &&
          linguistDistanceRequestIdRef.current === requestId
        ) {
          setLinguistDistancesById({});
          setIsCalculatingLinguistDistances(false);
        }
      }
    }, 1000);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    address,
    language,
    interpretationType,
    overlayData.Linguists,
    filterLinguistsByLanguage,
    showAllLinguists,
  ]);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);
    const languageIds = (overlayData.Languages || [])
      .map((languageOption) => Number(languageOption?.id || 0))
      .filter((id) => id > 0);

    if (!clientId || !languageIds.length) {
      setLanguageClientStatsById({});
      return undefined;
    }

    const fetchLanguageClientStats = async () => {
      try {
        const response = await api.post(
          "/languages/client-service-request-stats",
          {
            clientId,
            languageIds,
            serviceCategory: "Interpretation",
          },
        );

        if (!isCancelled) {
          setLanguageClientStatsById(response.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch language client service stats:", error);
        if (!isCancelled) {
          setLanguageClientStatsById({});
        }
      }
    };

    fetchLanguageClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client, overlayData.Languages]);

  useEffect(() => {
    let isCancelled = false;

    const clientId = Number(client?.id || 0);

    if (!clientId) {
      setRequestorClientStatsById({});
      return undefined;
    }

    const fetchRequestorClientStats = async () => {
      try {
        const response = await api.get(
          `/clients/${clientId}/requestorsSummary`,
        );

        if (!isCancelled) {
          setRequestorClientStatsById(response.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch requestor client service stats:", error);
        if (!isCancelled) {
          setRequestorClientStatsById({});
        }
      }
    };

    fetchRequestorClientStats();

    return () => {
      isCancelled = true;
    };
  }, [client]);

  const hasSelectedClient = Number(client?.id || 0) > 0;

  const languageOptions = useMemo(() => {
    // "ALL" is the rate pseudo-language (used by client default rates) — it
    // must never appear in service language pickers.
    const languages = [...(overlayData.Languages || [])].filter(
      (language) =>
        String(language?.Name || "").trim().toUpperCase() !== "ALL",
    );

    if (!hasSelectedClient) {
      return languages;
    }

    return languages.sort((left, right) => {
      const requestDelta =
        (languageClientStatsById[Number(right?.id || 0)]?.requestCount || 0) -
        (languageClientStatsById[Number(left?.id || 0)]?.requestCount || 0);

      if (requestDelta !== 0) return requestDelta;
      return String(left?.Name || "").localeCompare(String(right?.Name || ""));
    });
  }, [hasSelectedClient, languageClientStatsById, overlayData.Languages]);

  const requestorOptions = useMemo(() => {
    const requestors = [...(client?.Requestors || [])];

    if (!hasSelectedClient) {
      return requestors;
    }

    return requestors.sort((left, right) => {
      const servicesDelta =
        (requestorClientStatsById[Number(right?.id || 0)]?.servicesCount || 0) -
        (requestorClientStatsById[Number(left?.id || 0)]?.servicesCount || 0);

      if (servicesDelta !== 0) return servicesDelta;
      return String(left?.Name || "").localeCompare(String(right?.Name || ""));
    });
  }, [hasSelectedClient, requestorClientStatsById, client?.Requestors]);

  const renderInterpretationLanguageOption = useCallback(
    ({ key, ownerState, ...props }, option) => {
      if (option?.inputValue || !option?.id || !hasSelectedClient) {
        return (
          <Box key={key} component="li" {...props}>
            {option?.Name || ""}
          </Box>
        );
      }

      const stats =
        languageClientStatsById[Number(option?.id || 0)] ||
        EMPTY_LANGUAGE_CLIENT_STATS;

      return (
        <Box
          key={key}
          component="li"
          {...props}
          sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            {option?.Name || ""}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
          >
            {`Interpretations: ${stats.requestCount}`}
          </Typography>
        </Box>
      );
    },
    [hasSelectedClient, languageClientStatsById],
  );

  const renderInterpretationRequestorOption = useCallback(
    ({ key, ownerState, ...props }, option) => {
      if (option?.inputValue || !option?.id || !hasSelectedClient) {
        return (
          <Box key={key} component="li" {...props}>
            {option?.Name || ""}
          </Box>
        );
      }

      const stats =
        requestorClientStatsById[Number(option?.id || 0)] ||
        EMPTY_REQUESTOR_CLIENT_STATS;

      return (
        <Box
          key={key}
          component="li"
          {...props}
          sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1 }}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            {option?.Name || ""}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: "auto", textAlign: "right", whiteSpace: "nowrap" }}
          >
            {`Services: ${stats.servicesCount}`}
          </Typography>
        </Box>
      );
    },
    [hasSelectedClient, requestorClientStatsById],
  );

  dayjs.extend(durationPlugin);

  const cancelCharges = useCallback(() => {
    setAccountChargeHelperParts([]);
    setTotalCostHelperParts([]);
    setTotalLinguistPayHelperParts([]);
    setLinguistPay(0);
    setTotalLinguistPay(0);
    setTotalCost(0);
    setAccountCharge(0);
    setProfit(0);
    setMargin(0);
  }, []);

  const sumDurationValues = (durations) => {
    const totalMinutes = durations.reduce((acc, d) => {
      if (!d) return acc;

      // Handle dayjs objects
      if (d.format) {
        const timeString = d.format("HH:mm");
        const [hours, minutes] = timeString.split(":").map(Number);
        return acc + hours * 60 + minutes;
      }

      // Handle numeric values (already in minutes)
      return acc + d;
    }, 0);

    return dayjs().startOf("day").add(totalMinutes, "minute");
  };

  const validateFields = () => {
    // A linguist is optional while the assignment status is Requested
    // (pre-assign outreach flow); required for every other status.
    const isLinguistRequired = !String(
      interpretationStatus || "",
    ).startsWith("Requested");

    // A Simultaneous assignment must have BOTH simultaneous rates set — an
    // unset/zero rate silently bills $0 for core time (rateSimultaneous ×
    // coreDecimal). This wiring was previously dead code: the error setters
    // never fired and these fields were omitted from validation.
    const isSimultaneous = mode === "Simultaneous";
    // In-house linguists are paid monthly and legitimately have rate 0 — a 0
    // simultaneous rate is intentional when the base rate is 0.
    const isInHouseLinguist = Number(rate) === 0;
    const isSimultaneousRateMissing = (v) =>
      v === null ||
      v === undefined ||
      v === "" ||
      (Number(v) === 0 && !isInHouseLinguist);

    const errors = {
      name: name === null,
      date: date === null,
      projectNumber: projectNumber === null,
      interpretationType: interpretationType === null,
      client: client === null,
      clientRate: clientRate === null,
      requestor: requestor === null,
      contactPersons: hasContactPersonsError,
      language: language === null,
      linguist: isLinguistRequired && linguist === null,
      rate: rate === null,
      address: address === null,
      interpretationStatus: interpretationStatus === null,
      timeStart: timeStart === null,
      timeEnd: timeEnd === null,
      accountCharge: accountCharge === null,
      totalCost: totalCost === null,
      linguistPay: linguistPay === null,
      details: details === null,
      rateSimultaneous:
        isSimultaneous && isSimultaneousRateMissing(rateSimultaneous),
      clientRateSimultaneous:
        isSimultaneous &&
        isSimultaneousRateMissing(clientRateSimultaneous),
    };

    setNameError(errors.name);
    setDateError(errors.date);
    setProjectNumberError(errors.projectNumber);
    setInterpretationTypeError(errors.interpretationType);
    setClientError(errors.client);
    setClientRateError(errors.clientRate);
    setRequestorError(errors.requestor);
    setContactPersonsError(errors.contactPersons);
    setLanguageError(errors.language);
    setLinguistError(errors.linguist);
    setRateError(errors.rate);
    setAddressError(errors.address);
    setInterpretationStatusError(errors.interpretationStatus);
    setTimeStartError(errors.timeStart);
    setTimeEndError(errors.timeEnd);
    setAccountChargeError(errors.accountCharge);
    setTotalCostError(errors.totalCost);
    setLinguistPayError(errors.linguistPay);
    setDetailsError(errors.details);
    setRateSimultaneousError(errors.rateSimultaneous);
    setClientRateSimultaneousError(errors.clientRateSimultaneous);

    return errors;
  };

  // Check if assignment was paid and if critical fields have changed
  const checkPaidWarning = () => {
    // Only check for existing assignments
    if (isNewAssignment) return false;

    // Check if the assignment was paid
    const wasPaid = overlayData.Paid === true || overlayData.Paid === 1;

    if (!wasPaid) return false;

    // Check if critical fields have changed
    const criticalChanges = [];

    // Check if client changed
    if (client?.id !== overlayData.Client?.id) {
      criticalChanges.push("Client");
    }

    // Check if linguist changed
    if (linguist?.id !== overlayData.Linguist?.id) {
      criticalChanges.push("Linguist");
    }

    // Check if account charge changed
    if (Number(accountCharge) !== Number(overlayData.AccountCharge)) {
      criticalChanges.push("Account Charge");
    }

    // Check if total cost changed
    if (Number(totalCost) !== Number(overlayData.TotalCost)) {
      criticalChanges.push("Total Cost");
    }

    // Check if linguist pay changed
    if (Number(linguistPay) !== Number(overlayData.LinguistPay)) {
      criticalChanges.push("Linguist Pay");
    }

    if (criticalChanges.length > 0) {
      let message = "⚠️ WARNING: This assignment has been marked as PAID.\n\n";
      message += `You are changing: ${criticalChanges.join(", ")}\n\n`;

      // Special check for linguist change - check if the linguist was paid
      if (criticalChanges.includes("Linguist")) {
        message +=
          "🔹 You are changing the linguist for this paid assignment.\n";
        message += `   Previous linguist: ${overlayData.Linguist?.Name || "None"}\n`;
        message += `   New linguist: ${linguist?.Name || "None"}\n\n`;
      }

      message += "Are you sure you want to proceed with these changes?";

      setPaidWarningMessage(message);
      return true;
    }

    return false;
  };

  const buildDynamicCostLines = () => {
    const toAmount = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
    };

    const lineConfigByType = costTypeOptions.reduce((acc, option) => {
      acc[option.key] = {
        CostTypeName: option.costTypeName,
        SourceLegacyField: option.sourceLegacyField,
        IncludeInTotalCost: option.effectiveIncludeInTotalCost,
        IncludeInAccountCharge: option.effectiveIncludeInAccountCharge,
        IncludeInLinguistPay:
          option.effectiveIncludeInLinguistPay ??
          option.includeInLinguistPay ??
          false,
      };
      return acc;
    }, {});

    // Per-row flags win over the type-level defaults.
    const rowFlagByType = costRows.reduce((acc, row) => {
      acc[row.typeKey] = {
        IncludeInTotalCost: row.includeInTotalCost,
        IncludeInLinguistPay: row.includeInLinguistPay,
        IncludeInAccountCharge: row.includeInAccountCharge,
      };
      return acc;
    }, {});

    // Build lines from costRows (user-added rows)
    const travelTimeHours =
      travelTimeDuration && travelTimeDuration.isValid()
        ? (travelTimeDuration.hour() * 60 + travelTimeDuration.minute()) / 60
        : 0;
    const travelTimeLinguistCost =
      (travelTimeHours * (parseFloat(rate) || 0)) / 2;

    const costRowLines = costRows
      .map((row, index) => {
        const config = lineConfigByType[row.typeKey];
        if (!config) return null;

        // Travel Time is billed at half the rate: the Account Charge uses
        // the consecutive client rate, while the cost line (Total Cost /
        // Total Linguist Pay) uses the consecutive linguist rate.
        const amount =
          row.typeKey === "travelTimeCost"
            ? toAmount(travelTimeLinguistCost)
            : toAmount(getCostValueByType(row.typeKey));

        const rowFlags = rowFlagByType[row.typeKey] || {};
        return {
          ...config,
          ...rowFlags,
          IncludeInAccountCharge:
            row.typeKey === "paymentFees"
              ? false
              : rowFlags.IncludeInAccountCharge ??
                config.IncludeInAccountCharge,
          Amount: amount,
          DisplayOrder: (index + 1) * 10,
        };
      })
      .filter(Boolean)
      .filter((line) => line.Amount > 0);

    // Also include base cost types (linguistPay, paymentFees, etc) that have non-zero values
    // even if they're not explicitly in costRows (handles auto-calculated values)
    const baseTypeKeys = new Set(["linguistPay", "paymentFees"]);
    const costRowTypeKeys = new Set(costRows.map((row) => row.typeKey));

    const baseLines = Array.from(baseTypeKeys)
      .filter(
        (typeKey) => !costRowTypeKeys.has(typeKey) && lineConfigByType[typeKey],
      )
      .map((typeKey, index) => {
        const config = lineConfigByType[typeKey];
        const amount = toAmount(getCostValueByType(typeKey));
        if (amount === 0) return null;

        return {
          ...config,
          IncludeInAccountCharge:
            typeKey === "paymentFees"
              ? false
              : config.IncludeInAccountCharge,
          Amount: amount,
          DisplayOrder: (costRows.length + index + 1) * 10,
        };
      })
      .filter(Boolean);

    return [...costRowLines, ...baseLines];
  };

  const getMileagePayloadValues = useCallback(() => {
    return {
      mileageRoundtripValue: mileageRoundtrip === "" ? null : mileageRoundtrip,
      mileageCostValue: mileageCost === "" ? null : mileageCost,
    };
  }, [mileageRoundtrip, mileageCost]);

  function addBulkInterpretations() {
    setIsSavingChanges(true);
    setIsEditable(false);

    recurrenceDates.forEach((item, index) => {
      //('Adding Assignment '+(index+1)+ " / "+recurrenceDates.length+". Project Number: "+item['projectNumber'])
      //Maybe add a slight delay and a loading and a text to show progress later
      addNewInterpretationBulk(
        item["time"],
        item["projectNumber"],
        recurrenceDates.length,
        index === recurrenceDates.length - 1,
      );
    });
  }

  function addNewInterpretationBulk(
    singleDate,
    singleProjectNumber,
    length,
    isFinal,
  ) {
    const { mileageRoundtripValue, mileageCostValue } =
      getMileagePayloadValues();
    const dynamicCostLines = buildDynamicCostLines();
    const assignmentCreationSource =
      overlayData?.CreationSource === "quick_create"
        ? "quick_create"
        : "screen_button";

    const data = {
      Assignment: {
        id: 0,
        Date:
          singleDate && singleDate.isValid()
            ? singleDate.format("YYYY-MM-DD")
            : null,
        ProjectNumber: singleProjectNumber,
        CheckingDate: null,
        TimeStart: (timeStart && timeStart.format("HH:mm:ss")) || "00:00:00",
        TimeEnd: (timeEnd && timeEnd.format("HH:mm:ss")) || "00:00:00",
        ServiceCategory: "Interpretation",
        TotalDuration:
          (totalDuration && totalDuration.format("HH:mm:ss")) || "00:00:00",
        Duration: (duration && duration.format("HH:mm:ss")) || "00:00:00",
        ActualDuration:
          (actualDuration && actualDuration.format("HH:mm:ss")) || "00:00:00",
        Name: name,
        Details: details,
        InterpretationStatus:
          interpretationStatus === "" ? null : interpretationStatus,
        AccountCharge: accountCharge === "" ? null : accountCharge,
        TotalCost: totalCost === "" ? null : totalCost,
        AdditionalCost: additionalCost === "" ? null : additionalCost,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        LinguistPay: linguistPay === "" ? null : linguistPay,
        Profit: profit === "" ? null : profit,
        Margin: margin === "" ? null : margin,
        InterpretationType: interpretationType,
        ServiceSubtypeID: resolveInterpretationSubtypeId(interpretationType),
        Mode: mode === "" ? null : mode,
        Address: address,
        MileageRoundtrip: mileageRoundtripValue,
        MileageCost: mileageCostValue,
        ParkingFeesTolls: parkingFeesTolls === "" ? null : parkingFeesTolls,
        TravelTimeDuration:
          (travelTimeDuration && travelTimeDuration.format("HH:mm:ss")) || null,
        PreparationTime:
          (preparationTime && preparationTime.format("HH:mm:ss")) || "00:00:00",
        SchedulingTime:
          (schedulingTime && schedulingTime.format("HH:mm:ss")) || "00:00:00",
        ActualStart:
          (actualStart && actualStart.format("HH:mm:ss")) || "00:00:00",
        ActualEnd: (actualEnd && actualEnd.format("HH:mm:ss")) || "00:00:00",
        Paid: false,
        Rate: rate === "" ? null : rate,
        RateSimultaneous: rateSimultaneous === "" ? null : rateSimultaneous,
        ServiceClientRate:
          clientRate === "" ? null : Number(clientRate).toFixed(2),
        ServiceClientRateSimultaneous:
          clientRateSimultaneous === ""
            ? null
            : Number(clientRateSimultaneous).toFixed(2),
        TotalLinguistPay: totalLinguistPay === "" ? null : totalLinguistPay,
        EquipmentCost: equipmentCost === "" ? null : equipmentCost,
        AdditionalLinguistCost:
          additionalLinguistCost === "" ? null : additionalLinguistCost,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        ApplyTwoHourMinimum: twoHoursMin,
        IsShortAssignment: allowShortAssignments,
        Deleted: false,
        Cancelled:
          interpretationStatus !== null
            ? interpretationStatus.startsWith("Cancel")
            : false,
        Invoiced: false,
        CancellationReason:
          cancellationReason === "" ? null : cancellationReason,
        CreationSource: assignmentCreationSource,
        CreatedBy: currentUser.id,
        CheckedBy: null,
        ContactPersonName: contactPersonName,
        ContactPersonNumber: contactPersonNumber,
        ContactPersons: normalizedContactPersonsForPayload,
      },
      Language: language,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
      CostLines: dynamicCostLines,
    };

    api
      .post("/services/interpretations/addNewInterpretation", {
        data: data,
      })
      .then((response) => {
        if (note) addNewNote(response.data.id);
        const dataHistory = {
          Status: "Created",
          ServiceID: response.data.id,
          ProjectNumber: response.data.ProjectNumber,
          Date_FROM: null,
          Date_TO: response.data.Date,
          ClientID_FROM: null,
          ClientID_TO: response.data.ClientID,
          RequestorID_FROM: null,
          RequestorID_TO: response.data.RequestorID,
          LinguistID_FROM: null,
          LinguistID_TO: response.data.LinguistID,
          ChangedBy: currentUser.id,
          TimeStart_FROM: null,
          TimeStart_TO: response.data.TimeStart,
          TimeEnd_FROM: null,
          TimeEnd_TO: response.data.TimeEnd,
          TotalDuration_FROM: null,
          TotalDuration_TO: response.data.TotalDuration,
          Duration_FROM: null,
          Duration_TO: response.data.Duration,
          ActualDuration_FROM: null,
          ActualDuration_TO: response.data.ActualDuration,
          Name_FROM: null,
          Name_TO: response.data.Name,
          Details_FROM: null,
          Details_TO: response.data.Details,
          InterpretationStatus_FROM: null,
          InterpretationStatus_TO: response.data.InterpretationStatus,
          AccountCharge_FROM: null,
          AccountCharge_TO: response.data.AccountCharge,
          TotalCost_FROM: null,
          TotalCost_TO: response.data.TotalCost,
          AdditionalCost_FROM: null,
          AdditionalCost_TO: response.data.AdditionalCost,
          LinguistPay_FROM: null,
          LinguistPay_TO: response.data.LinguistPay,
          Profit_FROM: null,
          Profit_TO: response.data.Profit,
          Margin_FROM: null,
          Margin_TO: response.data.Margin,
          InterpretationType_FROM: null,
          InterpretationType_TO: response.data.InterpretationType,
          Mode_FROM: null,
          Mode_TO: response.data.Mode,
          Address_FROM: null,
          Address_TO: response.data.Address,
          MileageRoundtrip_FROM: null,
          MileageRoundtrip_TO: response.data.MileageRoundtrip,
          MileageCost_FROM: null,
          MileageCost_TO: response.data.MileageCost,
          ParkingFeesTolls_FROM: null,
          ParkingFeesTolls_TO: response.data.ParkingFeesTolls,
          TravelTimeDuration_FROM: null,
          TravelTimeDuration_TO: response.data.TravelTimeDuration,
          PreparationTime_FROM: null,
          PreparationTime_TO: response.data.PreparationTime,
          SchedulingTime_FROM: null,
          SchedulingTime_TO: response.data.SchedulingTime,
          ActualStart_FROM: null,
          ActualStart_TO: response.data.ActualStart,
          ActualEnd_FROM: null,
          ActualEnd_TO: response.data.ActualEnd,
          InvoiceTo_FROM: null,
          InvoiceTo_TO: response.data.InvoiceTo,
          Paid_FROM: null,
          Paid_TO: response.data.Paid,
          Rate_FROM: null,
          Rate_TO: response.data.Rate,
          RateSimultaneous_FROM: null,
          RateSimultaneous_TO: response.data.RateSimultaneous,
          ServiceClientRate_FROM: null,
          ServiceClientRate_TO: response.data.ServiceClientRate,
          ServiceClientRateSimultaneous_FROM: null,
          ServiceClientRateSimultaneous_TO:
            response.data.ServiceClientRateSimultaneous,
          CancellationReason_FROM: null,
          CancellationReason_TO: response.data.CancellationReason,
          TargetLanguagesIDs_FROM: null,
          TargetLanguagesIDs_TO: [language],
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          ContactPersonName_FROM: null,
          ContactPersonName_TO: response.data.ContactPersonName,
          ContactPersonNumber_FROM: null,
          ContactPersonNumber_TO: response.data.ContactPersonNumber,
        };
        void dataHistory;

        if (isFinal) {
          setIsSavingChanges(false);

          triggerPageRefresh(
            length + " New Interpretations Added Successfully!",
            theme,
          );

          releaseLock();
          setOverlayName("");
        }
      })
      .catch((err) => console.log(err));
  }

  function addNewInterpretation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const { mileageRoundtripValue, mileageCostValue } =
      getMileagePayloadValues();
    const dynamicCostLines = buildDynamicCostLines();
    const assignmentCreationSource =
      overlayData?.CreationSource === "quick_create"
        ? "quick_create"
        : "screen_button";

    const data = {
      Assignment: {
        id: 0,
        Date: date && date.isValid() ? date.format("YYYY-MM-DD") : null,
        ProjectNumber: projectNumber,
        CheckingDate: null,
        TimeStart: (timeStart && timeStart.format("HH:mm:ss")) || "00:00:00",
        TimeEnd: (timeEnd && timeEnd.format("HH:mm:ss")) || "00:00:00",
        ServiceCategory: "Interpretation",
        TotalDuration:
          (totalDuration && totalDuration.format("HH:mm:ss")) || "00:00:00",
        Duration: (duration && duration.format("HH:mm:ss")) || "00:00:00",
        ActualDuration:
          (actualDuration && actualDuration.format("HH:mm:ss")) || "00:00:00",
        Name: name,
        Details: details,
        InterpretationStatus:
          interpretationStatus === "" ? null : interpretationStatus,
        AccountCharge: accountCharge === "" ? null : accountCharge,
        TotalCost: totalCost === "" ? null : totalCost,
        AdditionalCost: additionalCost === "" ? null : additionalCost,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        LinguistPay: linguistPay === "" ? null : linguistPay,
        Profit: profit === "" ? null : profit,
        Margin: margin === "" ? null : margin,
        InterpretationType: interpretationType,
        ServiceSubtypeID: resolveInterpretationSubtypeId(interpretationType),
        Mode: mode === "" ? null : mode,
        Address: address,
        MileageRoundtrip: mileageRoundtripValue,
        MileageCost: mileageCostValue,
        ParkingFeesTolls: parkingFeesTolls === "" ? null : parkingFeesTolls,
        TravelTimeDuration:
          (travelTimeDuration && travelTimeDuration.format("HH:mm:ss")) || null,
        PreparationTime:
          (preparationTime && preparationTime.format("HH:mm:ss")) || "00:00:00",
        SchedulingTime:
          (schedulingTime && schedulingTime.format("HH:mm:ss")) || "00:00:00",
        ActualStart:
          (actualStart && actualStart.format("HH:mm:ss")) || "00:00:00",
        ActualEnd: (actualEnd && actualEnd.format("HH:mm:ss")) || "00:00:00",
        Paid: false,
        Rate: rate === "" ? null : rate,
        RateSimultaneous: rateSimultaneous === "" ? null : rateSimultaneous,
        ServiceClientRate:
          clientRate === "" ? null : Number(clientRate).toFixed(2),
        ServiceClientRateSimultaneous:
          clientRateSimultaneous === ""
            ? null
            : Number(clientRateSimultaneous).toFixed(2),
        TotalLinguistPay: totalLinguistPay === "" ? null : totalLinguistPay,
        EquipmentCost: equipmentCost === "" ? null : equipmentCost,
        AdditionalLinguistCost:
          additionalLinguistCost === "" ? null : additionalLinguistCost,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        ApplyTwoHourMinimum: twoHoursMin,
        IsShortAssignment: allowShortAssignments,
        Deleted: false,
        Cancelled:
          interpretationStatus !== null
            ? interpretationStatus.startsWith("Cancel")
            : false,
        Invoiced: false,
        CancellationReason:
          cancellationReason === "" ? null : cancellationReason,
        CreationSource: assignmentCreationSource,
        CreatedBy: currentUser.id,
        CheckedBy: null,
        ContactPersonName: contactPersonName,
        ContactPersonNumber: contactPersonNumber,
        ContactPersons: normalizedContactPersonsForPayload,
      },
      Language: language,
      Client: client,
      Requestor: requestor,
      Linguist: linguist === "" ? null : linguist,
      Linguists: offerLinguists,
      Invoicer: invoiceTo === "" ? null : invoiceTo,
      CostLines: dynamicCostLines,
    };

    api
      .post("/services/interpretations/addNewInterpretation", {
        data: data,
      })
      .then((response) => {
        if (note) addNewNote(response.data.id);

        const dataHistory = {
          Status: "Created",
          ServiceID: response.data.id,
          ProjectNumber: response.data.ProjectNumber,
          Date_FROM: null,
          Date_TO: response.data.Date,
          ClientID_FROM: null,
          ClientID_TO: response.data.ClientID,
          RequestorID_FROM: null,
          RequestorID_TO: response.data.RequestorID,
          LinguistID_FROM: null,
          LinguistID_TO: response.data.LinguistID,
          ChangedBy: currentUser.id,
          TimeStart_FROM: null,
          TimeStart_TO: response.data.TimeStart,
          TimeEnd_FROM: null,
          TimeEnd_TO: response.data.TimeEnd,
          TotalDuration_FROM: null,
          TotalDuration_TO: response.data.TotalDuration,
          Duration_FROM: null,
          Duration_TO: response.data.Duration,
          ActualDuration_FROM: null,
          ActualDuration_TO: response.data.ActualDuration,
          Name_FROM: null,
          Name_TO: response.data.Name,
          Details_FROM: null,
          Details_TO: response.data.Details,
          InterpretationStatus_FROM: null,
          InterpretationStatus_TO: response.data.InterpretationStatus,
          AccountCharge_FROM: null,
          AccountCharge_TO: response.data.AccountCharge,
          TotalCost_FROM: null,
          TotalCost_TO: response.data.TotalCost,
          AdditionalCost_FROM: null,
          AdditionalCost_TO: response.data.AdditionalCost,
          LinguistPay_FROM: null,
          LinguistPay_TO: response.data.LinguistPay,
          Profit_FROM: null,
          Profit_TO: response.data.Profit,
          Margin_FROM: null,
          Margin_TO: response.data.Margin,
          InterpretationType_FROM: null,
          InterpretationType_TO: response.data.InterpretationType,
          Mode_FROM: null,
          Mode_TO: response.data.Mode,
          Address_FROM: null,
          Address_TO: response.data.Address,
          MileageRoundtrip_FROM: null,
          MileageRoundtrip_TO: response.data.MileageRoundtrip,
          MileageCost_FROM: null,
          MileageCost_TO: response.data.MileageCost,
          ParkingFeesTolls_FROM: null,
          ParkingFeesTolls_TO: response.data.ParkingFeesTolls,
          TravelTimeDuration_FROM: null,
          TravelTimeDuration_TO: response.data.TravelTimeDuration,
          PreparationTime_FROM: null,
          PreparationTime_TO: response.data.PreparationTime,
          SchedulingTime_FROM: null,
          SchedulingTime_TO: response.data.SchedulingTime,
          ActualStart_FROM: null,
          ActualStart_TO: response.data.ActualStart,
          ActualEnd_FROM: null,
          ActualEnd_TO: response.data.ActualEnd,
          InvoiceTo_FROM: null,
          InvoiceTo_TO: response.data.InvoiceTo,
          Paid_FROM: null,
          Paid_TO: response.data.Paid,
          Rate_FROM: null,
          Rate_TO: response.data.Rate,
          RateSimultaneous_FROM: null,
          RateSimultaneous_TO: response.data.RateSimultaneous,
          ServiceClientRate_FROM: null,
          ServiceClientRate_TO: response.data.ServiceClientRate,
          ServiceClientRateSimultaneous_FROM: null,
          ServiceClientRateSimultaneous_TO:
            response.data.ServiceClientRateSimultaneous,
          TotalLinguistPay_FROM: null,
          TotalLinguistPay_TO: response.data.TotalLinguistPay,
          EquipmentCost_FROM: null,
          EquipmentCost_TO: response.data.EquipmentCost,
          AdditionalLinguistCost_FROM: null,
          AdditionalLinguistCost_TO: response.data.AdditionalLinguistCost,
          CancellationReason_FROM: null,
          CancellationReason_TO: response.data.CancellationReason,
          DisableAutoChargeCalc_FROM: null,
          DisableAutoChargeCalc_TO: response.data.DisableAutoChargeCalc,
          ApplyTwoHourMinimum_FROM: null,
          ApplyTwoHourMinimum_TO: response.data.ApplyTwoHourMinimum,
          TargetLanguagesIDs_FROM: null,
          TargetLanguagesIDs_TO: [language],
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          ContactPersonName_FROM: null,
          ContactPersonName_TO: response.data.ContactPersonName,
          ContactPersonNumber_FROM: null,
          ContactPersonNumber_TO: response.data.ContactPersonNumber,
        };
        void dataHistory;

        setIsSavingChanges(false);

        triggerPageRefresh("New Interpretation Added Successfully!", theme, {
          label: "Open",
          onClick: () => navigate(`/interpretations/${response.data.id}`),
        });

        // Emit assignment saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "Interpretation",
            recordId: response.data.ServiceID || response.data.id,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
            projectNumber: response.data.ProjectNumber,
          });

          // Also notify the interpretations list page to refetch
          socket.emit("assignment:saved", {
            modelType: "InterpretationsList",
            recordId: "all",
            serviceId: response.data.ServiceID || response.data.id,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
            projectNumber: response.data.ProjectNumber,
          });
        }

        setOverlayName("");
      })
      .catch((err) => {
        console.error("Failed to add interpretation:", err);
        setIsSavingChanges(false);
        setIsEditable(true);
        triggerPageRefresh("Failed to add interpretation assignment", theme);
      });
  }

  function updateInterpretation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    try {
      const { mileageRoundtripValue, mileageCostValue } =
        getMileagePayloadValues();
      const dynamicCostLines = buildDynamicCostLines();

      const dataOld = {
        Assignment: {
          id: overlayData.id,
          Date: overlayData.Date,
          ProjectNumber: overlayData.ProjectNumber,
          CheckingDate: null,
          TimeStart: overlayData.TimeStart,
          TimeEnd: overlayData.TimeEnd,
          ServiceCategory: "Interpretation",
          TotalDuration: overlayData.TotalDuration,
          Duration: overlayData.Duration,
          ActualDuration: overlayData.ActualDuration,
          Name: overlayData.Name,
          Details: overlayData.Details,
          ClientRateDiscount: overlayData.ClientRateDiscount || 0,
          InterpretationStatus:
            overlayData.InterpretationStatus === ""
              ? null
              : overlayData.InterpretationStatus,
          AccountCharge:
            overlayData.AccountCharge === "" ? null : overlayData.AccountCharge,
          TotalCost:
            overlayData.TotalCost === "" ? null : overlayData.TotalCost,
          AdditionalCost:
            overlayData.AdditionalCost === "" ||
            overlayData.AdditionalCost === null
              ? "0.00"
              : overlayData.AdditionalCost,
          LinguistPay:
            overlayData.LinguistPay === "" ? null : overlayData.LinguistPay,
          Profit: overlayData.Profit === "" ? null : overlayData.Profit,
          Margin: overlayData.Margin === "" ? null : overlayData.Margin,
          InterpretationType:
            (overlayData.ServiceSubtype?.Name || "") === ""
              ? null
              : overlayData.ServiceSubtype?.Name,
          ServiceSubtypeID:
            overlayData.ServiceSubtypeID === "" ||
            overlayData.ServiceSubtypeID == null
              ? null
              : Number(overlayData.ServiceSubtypeID),
          Mode:
            overlayData.Mode === "" || overlayData.Mode === null
              ? "Consecutive"
              : overlayData.Mode,
          Address: overlayData.Address,
          MileageRoundtrip:
            overlayData.MileageRoundtrip === "" ||
            overlayData.MileageRoundtrip === null
              ? "0.00"
              : overlayData.MileageRoundtrip,
          MileageCost:
            overlayData.MileageCost === "" ? null : overlayData.MileageCost,
          ParkingFeesTolls:
            overlayData.ParkingFeesTolls === ""
              ? null
              : overlayData.ParkingFeesTolls,
          PaymentFees:
            overlayData.PaymentFees === "" ? null : overlayData.PaymentFees,
          TravelTimeDuration: overlayData.TravelTimeDuration || null,
          PreparationTime: overlayData.PreparationTime,
          SchedulingTime: overlayData.SchedulingTime,
          ActualStart: overlayData.ActualStart,
          ActualEnd: overlayData.ActualEnd,
          Paid: false,
          Rate: overlayData.Rate === "" ? null : overlayData.Rate,
          RateSimultaneous:
            overlayData.RateSimultaneous === ""
              ? null
              : overlayData.RateSimultaneous,
          ServiceClientRate:
            overlayData.ServiceClientRate === ""
              ? null
              : overlayData.ServiceClientRate,
          ServiceClientRateSimultaneous:
            overlayData.ServiceClientRateSimultaneous === ""
              ? null
              : overlayData.ServiceClientRateSimultaneous,
          TotalLinguistPay:
            overlayData.TotalLinguistPay === ""
              ? null
              : overlayData.TotalLinguistPay,
          EquipmentCost:
            overlayData.EquipmentCost === "" ? null : overlayData.EquipmentCost,
          AdditionalLinguistCost:
            overlayData.AdditionalLinguistCost === ""
              ? null
              : overlayData.AdditionalLinguistCost,
          DisableAutoChargeCalc: overlayData.DisableAutoChargeCalc || false,
          ApplyTwoHourMinimum: overlayData.ApplyTwoHourMinimum || false,
          IsShortAssignment: overlayData.IsShortAssignment || false,
          Deleted: false,
          Cancelled: overlayData.Cancelled,
          Invoiced: false,
          CancellationReason: overlayData.CancellationReason,
          CreatedBy: currentUser.id,
          CheckedBy: null,
          ContactPersonName: overlayData.ContactPersonName,
          ContactPersonNumber: overlayData.ContactPersonNumber,
          ContactPersons: (overlayData.ContactPersons || []).map((contact) => ({
            id: contact?.id || contact?.ContactPersonID || null,
            Name: contact?.Name || "",
            PhoneNumber: contact?.PhoneNumber || "",
            Ext: contact?.Ext || "",
          })),
        },
        Language: overlayData.ServiceTargetLanguages?.[0]?.Language || null,
        Client: overlayData.Client,
        Requestor: overlayData.Requestor,
        Linguist: overlayData.Linguist,
        Invoicer: overlayData.Invoicer,
      };

      const dataNew = {
        Assignment: {
          id: overlayData.id,
          Date:
            date && typeof date.format === "function"
              ? date.format("YYYY-MM-DD")
              : null,
          ProjectNumber: projectNumber,
          //date.format("YYYY-MM-DD") === overlayData.Date
          //  ? overlayData.ProjectNumber
          //  : projectNumber,
          CheckingDate: null,
          TimeStart: (timeStart && timeStart.format("HH:mm:ss")) || "00:00:00",
          TimeEnd: (timeEnd && timeEnd.format("HH:mm:ss")) || "00:00:00",
          ServiceCategory: "Interpretation",
          TotalDuration:
            (totalDuration && totalDuration.format("HH:mm:ss")) || "00:00:00",
          Duration: (duration && duration.format("HH:mm:ss")) || "00:00:00",
          ActualDuration:
            (actualDuration && actualDuration.format("HH:mm:ss")) || "00:00:00",
          Name: name,
          ClientRateDiscount: Number(clientRateDiscount).toFixed(2),
          Details: details,
          InterpretationStatus:
            interpretationStatus === "" ? null : interpretationStatus,
          AccountCharge:
            accountCharge === "" ? null : Number(accountCharge).toFixed(2),
          TotalCost: totalCost === "" ? null : Number(totalCost).toFixed(2),
          AdditionalCost:
            additionalCost === "" ? null : Number(additionalCost).toFixed(2),
          LinguistPay:
            linguistPay === "" ? null : Number(linguistPay).toFixed(2),
          Profit: profit === "" ? null : Number(profit).toFixed(2),
          Margin: margin === "" ? null : Number(margin).toFixed(2),
          InterpretationType: interpretationType,
          ServiceSubtypeID: resolveInterpretationSubtypeId(interpretationType),
          Mode: mode === "" ? null : mode,
          Address: address,
          MileageRoundtrip: mileageRoundtripValue,
          MileageCost: mileageCostValue,
          ParkingFeesTolls: parkingFeesTolls === "" ? null : parkingFeesTolls,
          PaymentFees: paymentFees === "" ? null : paymentFees,
          TravelTimeDuration:
            (travelTimeDuration && travelTimeDuration.format("HH:mm:ss")) ||
            null,
          // Travel Time legacy column (2026-09-23): when the Travel Time row
          // has been REMOVED, persist 0 explicitly — the server's
          // deriveLegacyCostLines fallback (used when the incoming CostLines
          // array is empty) must never recreate a travel-time cost line from
          // a stale legacy value. While the row exists the column is kept in
          // sync from the cost line server-side (rollups), so it is
          // deliberately not sent (sending the client figure would only add
          // audit noise).
          TravelTimeCost: hasTravelTimeRow ? undefined : 0,
          PreparationTime:
            (preparationTime && preparationTime.format("HH:mm:ss")) ||
            "00:00:00",
          SchedulingTime:
            (schedulingTime && schedulingTime.format("HH:mm:ss")) || "00:00:00",
          ActualStart:
            (actualStart && actualStart.format("HH:mm:ss")) || "00:00:00",
          ActualEnd: (actualEnd && actualEnd.format("HH:mm:ss")) || "00:00:00",
          Paid: false,
          Rate: rate === "" ? null : Number(rate).toFixed(2),
          RateSimultaneous:
            rateSimultaneous === ""
              ? null
              : Number(rateSimultaneous).toFixed(2),
          ServiceClientRate:
            clientRate === "" ? null : Number(clientRate).toFixed(2),
          ServiceClientRateSimultaneous:
            clientRateSimultaneous === ""
              ? null
              : Number(clientRateSimultaneous).toFixed(2),
          TotalLinguistPay:
            totalLinguistPay === ""
              ? null
              : Number(totalLinguistPay).toFixed(2),
          EquipmentCost:
            equipmentCost === "" ? null : Number(equipmentCost).toFixed(2),
          AdditionalLinguistCost:
            additionalLinguistCost === ""
              ? null
              : Number(additionalLinguistCost).toFixed(2),
          DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
          ApplyTwoHourMinimum: twoHoursMin,
          IsShortAssignment: allowShortAssignments,
          Deleted: false,
          Cancelled:
            interpretationStatus !== null
              ? interpretationStatus.startsWith("Cancel")
              : false,
          Invoiced: false,
          CancellationReason:
            cancellationReason === "" ? null : cancellationReason,
          CreatedBy: currentUser.id,
          CheckedBy: null,
          ContactPersonName: contactPersonName,
          ContactPersonNumber: contactPersonNumber,
          ContactPersons: normalizedContactPersonsForPayload,
        },
        Language: language,
        Client: client,
        Requestor: requestor,
        Linguist: linguist === "" ? null : linguist,
        Invoicer: invoiceTo,
        CostLines: dynamicCostLines,
      };

      const allData = {
        Old: dataOld,
        New: dataNew,
      };

      api
        .post("/services/interpretations/updateInterpretation", {
          data: allData,
        })
        .then((response) => {
          if (note) addNewNote(overlayData.id);

          setIsSavingChanges(false);
          triggerPageRefresh(
            "Interpretation Assignment Updated Sucessfully",
            theme,
          );

          // Emit assignment saved event for other viewers
          if (socket) {
            socket.emit("assignment:saved", {
              modelType: "Interpretation",
              recordId: overlayData.id,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.ProjectNumber,
            });

            // Also notify the interpretations list page to refetch
            socket.emit("assignment:saved", {
              modelType: "InterpretationsList",
              recordId: "all",
              serviceId: overlayData.id,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.ProjectNumber,
            });
          }

          releaseLock();
          setOverlayName("");
        })
        .catch((err) => {
          console.error("Failed to update interpretation:", err);
          setIsSavingChanges(false);
          setIsEditable(true);
          triggerPageRefresh(
            "Failed to update interpretation assignment",
            theme,
          );
          showSnackbar(
            "Failed to update interpretation assignment",
            theme,
            "error",
          );
        });
    } catch (err) {
      console.error("Failed to build interpretation update payload:", err);
      setIsSavingChanges(false);
      setIsEditable(true);
      triggerPageRefresh("Failed to prepare interpretation update", theme);
      showSnackbar("Failed to prepare interpretation update", theme, "error");
    }
  }

  function addNewNote(id) {
    const data = {
      Note: note,
      ServiceID: id,
      CreatedBy: currentUser.id,
      CreatedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      IsLatest: 1,
    };

    api
      .post("/services/addNewNote", {
        data: data,
      })
      .then((response) => {
        const dataHistory = {
          Status: "Created",
          ServiceID: overlayData.id,
          Note_FROM: null,
          Note_TO: response.data.id,
          ChangedBy: currentUser.id,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };
        void dataHistory;

        //ADDING THE HISTORY
        /*
        api
          .post("/serviceHistories/addNoteHistory", {
            data: dataHistory,
          })
          .then((response) => {})
          .catch((err) => {
            console.log(err);
          });
        */
      })
      .catch((err) => console.log(err));
  }

  // Fetch mileage rates on component mount
  useEffect(() => {
    const fetchMileageRates = async () => {
      try {
        const response = await api.get("/mileageRates");
        setMileageRates(response.data || []);
        console.log("response.data", response.data);
      } catch (err) {
        console.error("Error fetching mileage rates:", err);
        // Fallback to default 0.7 if fetch fails
      }
    };

    fetchMileageRates();
  }, []);

  addBulkInterpretationsRef.current = addBulkInterpretations;
  addNewInterpretationRef.current = addNewInterpretation;
  updateInterpretationRef.current = updateInterpretation;

  useEffect(() => {
    if (shouldValidate) {
      if (
        !(
          dateError ||
          projectNumberError ||
          nameError ||
          interpretationTypeError ||
          clientError ||
          clientRateError ||
          requestorError ||
          contactPersonsError ||
          languageError ||
          linguistError ||
          rateError ||
          addressError ||
          interpretationStatusError ||
          timeStartError ||
          timeEndError ||
          accountChargeError ||
          totalCostError ||
          linguistPayError ||
          detailsError
        )
      ) {
        const invalidFlagRows = costRows.filter(
          (row) =>
            Number(getCostValueByType(row.typeKey) || 0) > 0 &&
            !row.includeInTotalCost &&
            !row.includeInLinguistPay &&
            !row.includeInAccountCharge,
        );

        if (invalidFlagRows.length > 0) {
          showSnackbar(
            "Each cost line must be included in at least one total (Total Cost, Total Linguist Pay, or Account Charge).",
            theme,
            "error",
          );
        } else {
          isNewAssignment
            ? showRecurring
              ? addBulkInterpretationsRef.current()
              : addNewInterpretationRef.current()
            : updateInterpretationRef.current();
        }
      }

      setShouldValidate(false);
    }
  }, [
    shouldValidate,
    nameError,
    dateError,
    projectNumberError,
    interpretationTypeError,
    clientError,
    clientRateError,
    requestorError,
    contactPersonsError,
    languageError,
    linguistError,
    rateError,
    addressError,
    interpretationStatusError,
    timeStartError,
    timeEndError,
    accountChargeError,
    totalCostError,
    linguistPayError,
    detailsError,
    isNewAssignment,
    showRecurring,
    costRows,
    getCostValueByType,
  ]);

  useEffect(() => {
    updateCurrentProgressRef.current();
  }, [
    name,
    date,
    projectNumber,
    interpretationType,
    client,
    clientRate,
    requestor,
    language,
    linguist,
    rate,
    address,
    interpretationStatus,
    timeStart,
    timeEnd,
    accountCharge,
    totalCost,
    linguistPay,
    details,
  ]);

  useEffect(() => {
    if (!isNewAssignment) {
      initialDataRef.current = {
        name: overlayData.Name,
        date: overlayData.Date, // assuming string format "YYYY-MM-DD"
        projectNumber: overlayData.ProjectNumber,
        interpretationType: overlayData.ServiceSubtype?.Name,
        client: overlayData.Client, // if client is an object, consider comparing by id
        requestor: overlayData.Requestor, // comparing by Name (or id if available)
        invoiceTo: overlayData.Invoicer,
        language: overlayData.ServiceTargetLanguages?.[0]?.Language,
        linguist: overlayData.Linguist,
        address: overlayData.Address,
        interpretationStatus: overlayData.InterpretationStatus,
        cancellationReason: overlayData.CancellationReason,
        accountCharge: overlayData.AccountCharge,
        totalCost: overlayData.TotalCost,
        linguistPay: overlayData.LinguistPay,
        additionalCost: overlayData.AdditionalCost,
        paymentFees: overlayData.PaymentFees,
        mode: overlayData.Mode,
        rate: overlayData.Rate,
        clientRate: initialClientRate, // see your initialization of initialClientRate
        timeStart: overlayData.TimeStart,
        timeEnd: overlayData.TimeEnd,
      };
    }
  }, [isNewAssignment, initialClientRate, overlayData]);

  useEffect(() => {
    if (!isNewAssignment && initialDataRef.current) {
      const dirty =
        name !== initialDataRef.current.name ||
        (date && date.format("YYYY-MM-DD")) !== initialDataRef.current.date ||
        projectNumber !== initialDataRef.current.projectNumber ||
        interpretationType !== initialDataRef.current.interpretationType ||
        client?.Account !== initialDataRef.current.client?.Account ||
        requestor?.Name !== initialDataRef.current.requestor?.Name ||
        invoiceTo?.Name !== initialDataRef.current.invoiceTo?.Name ||
        language?.Name !== initialDataRef.current.language?.Name ||
        linguist?.Name !== initialDataRef.current.linguist?.Name ||
        address !== initialDataRef.current.address ||
        interpretationStatus !== initialDataRef.current.interpretationStatus || //Error here
        (cancellationReason || "") !==
          (initialDataRef.current.cancellationReason || "") ||
        Number(accountCharge) !==
          Number(initialDataRef.current.accountCharge) ||
        Number(totalCost) !== Number(initialDataRef.current.totalCost) ||
        Number(linguistPay) !== Number(initialDataRef.current.linguistPay) ||
        Number(additionalCost) !==
          Number(initialDataRef.current.additionalCost) ||
        Number(paymentFees) !== Number(initialDataRef.current.paymentFees) ||
        mode !== initialDataRef.current.mode ||
        Number(rate) !== Number(initialDataRef.current.rate) ||
        Number(clientRate) !== Number(initialDataRef.current.clientRate) ||
        Number(clientRateSimultaneous) !==
          Number(initialDataRef.current.clientRateSimultaneous || "") ||
        (timeStart && timeStart.format("HH:mm:ss")) !==
          initialDataRef.current.timeStart ||
        (timeEnd && timeEnd.format("HH:mm:ss")) !==
          initialDataRef.current.timeEnd ||
        ((actualStart && actualStart.format("HH:mm:ss")) || "") !==
          (initialDataRef.current.actualStart || "") ||
        ((actualEnd && actualEnd.format("HH:mm:ss")) || "") !==
          (initialDataRef.current.actualEnd || "");
      setHasChanges(dirty);
      // Mirror into the ref so the socket refetch handler sees current dirtiness.
      hasChangesRef.current = dirty;
    }
  }, [
    name,
    date,
    projectNumber,
    interpretationType,
    client,
    requestor,
    invoiceTo,
    language,
    linguist,
    address,
    interpretationStatus,
    cancellationReason,
    accountCharge,
    totalCost,
    linguistPay,
    additionalCost,
    paymentFees,
    mode,
    rate,
    clientRate,
    clientRateSimultaneous,
    timeStart,
    timeEnd,
    actualStart,
    actualEnd,
    isNewAssignment,
  ]);

  const saveInterpretation = () => {
    // Check if we need to show paid warning
    const paidWarningTriggered = !pendingSave && checkPaidWarning();
    if (paidWarningTriggered) {
      setShowPaidWarning(true);
      return;
    }

    // If we reach here, either no warning needed or user confirmed
    setPendingSave(false);

    const errors = validateFields();
    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      return;
    }

    isNewAssignment
      ? showRecurring
        ? addBulkInterpretationsRef.current()
        : addNewInterpretationRef.current()
      : updateInterpretationRef.current();
  };

  const attemptSave = () => {
    if (isSavingChanges) {
      return;
    }

    if (isLocked) {
      const lockedByName = lockedBy?.userName || "another user";
      showSnackbar(
        `This assignment is locked by ${lockedByName}`,
        theme,
        "warning",
      );
      return;
    }

    if (isNewAssignment && !triggerMissingFieldsShake()) {
      return;
    }

    saveInterpretation();
  };
  saveInterpretationRef.current = saveInterpretation;
  attemptSaveRef.current = attemptSave;

  // Handle confirmation of paid assignment warning
  const handlePaidWarningConfirm = () => {
    setShowPaidWarning(false);
    setPendingSave(true);
    // Trigger save again, but this time with pendingSave = true to bypass the check
    setTimeout(() => saveInterpretation(), 0);
  };

  // Handle cancellation of paid assignment warning
  const handlePaidWarningCancel = () => {
    setShowPaidWarning(false);
    setPendingSave(false);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadClick = () => {
    const data = {
      Action: OVERLAY_NAMES.INTERPRETATION_ACTION_OPI_UPLOAD,

      Interpretations: overlayData.Interpretations,
      ClientsOPIMinimums: overlayData.ClientsOPIMinimums,
      Languages: overlayData.Languages,
      Linguists: overlayData.Linguists,
      Clients: overlayData.Clients,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.INTERPRETATION_OPI_UPLOAD);

    handleMenuClose();
  };

  useEffect(() => {
    const handleEnterSubmit = (event) => {
      const target = event.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');
      const isInsidePopup =
        target instanceof Element &&
        (!!target.closest('[role="listbox"]') ||
          !!target.closest(".MuiAutocomplete-popper") ||
          !!target.closest(".MuiPopover-root") ||
          !!target.closest(".MuiMenu-root"));

      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog &&
        !isInsidePopup
      ) {
        event.preventDefault();
        attemptSaveRef.current();
      }
    };

    window.addEventListener("keydown", handleEnterSubmit);
    return () => window.removeEventListener("keydown", handleEnterSubmit);
  }, []);

  // Calculate charges automatically when dependencies change
  useEffect(() => {
    // Skip automatic calculation if disabled
    if (disableAutomaticCalculationOfCharges) {
      return;
    }

    if (
      [
        "Cancelled (With 24 Hrs Notice)",
        "Cancelled (Unable To Fill)",
        //"Cancelled (Re-schedule)",
      ].includes(interpretationStatus)
    ) {
      cancelCharges();
      return;
    }

    if (!totalDuration) return;

    // Helper to get decimal hours from a dayjs duration
    const toDecimal = (d) => (d && d.isValid ? d.hour() + d.minute() / 60 : 0);

    // Calculate durations
    const totalDecimal = toDecimal(totalDuration);
    const prepDecimal = toDecimal(preparationTime);
    const schedDecimal = toDecimal(schedulingTime);

    let durationDecimal = totalDecimal;
    const minHours = twoHoursMin ? 2 : 1;

    // Round to nearest quarter hour FIRST (normalize measured duration)
    durationDecimal = Math.ceil(durationDecimal * 4) / 4;

    // Then apply minimum hours only if short assignments are not allowed
    if (!allowShortAssignments) {
      if (durationDecimal <= 2 && minHours > 1) {
        durationDecimal = minHours;
      }
      if (minHours === 1 && durationDecimal < 1) {
        durationDecimal = minHours;
      }
    }

    let pay = 0;
    let charge = 0;
    let chargeParts = [];
    let totalLingPay = 0;
    let totalLingPayParts = [];

    // Part builders for the helper formulas: a `tip` turns that number into a
    // tooltip on hover (2026-09-23, per Amine).
    const numPart = (value, tip) => ({
      text: Number(value || 0).toFixed(2),
      tip,
    });
    const textPart = (text) => ({ text, tip: null });
    const clientRateTip = (label) =>
      Number(clientRateDiscount) > 0
        ? `${label} — after ${clientRateDiscount}% discount`
        : label;

    // Amount for a cost option — resolves ALL cost types, including custom
    // DB types from Settings (custom types keep their amount in
    // customCostValuesByType). For the built-in types this equals the legacy
    // hardcoded values.
    const amountForOption = (opt) => Number(getCostValueByType(opt.key) || 0);

    // Travel Time is billed at half the rate. The Account Charge uses the
    // consecutive client rate (the travelTimeCost state); Total Linguist Pay
    // and Total Cost use the consecutive linguist rate — even for
    // simultaneous assignments.
    const travelTimeHours =
      travelTimeDuration && travelTimeDuration.isValid()
        ? (travelTimeDuration.hour() * 60 + travelTimeDuration.minute()) / 60
        : 0;
    const travelTimeLinguistCost =
      (travelTimeHours * (parseFloat(rate) || 0)) / 2;
    const travelTimeValueForLinguistPay = (opt) =>
      opt.key === "travelTimeCost"
        ? travelTimeLinguistCost
        : amountForOption(opt);

    const baseOptionByKey = INTERPRETATION_BASE_COST_TYPE_OPTIONS.reduce(
      (acc, opt) => {
        acc[opt.key] = opt;
        return acc;
      },
      {},
    );

    const activeCostOptions =
      costTypeOptions.length > 0
        ? costTypeOptions
        : INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => ({
            ...opt,
            effectiveIncludeInTotalCost: opt.defaultIncludeInTotalCost,
            effectiveIncludeInAccountCharge: opt.defaultIncludeInAccountCharge,
            effectiveIncludeInLinguistPay: opt.includeInLinguistPay ?? false,
          }));

    // Per-row inclusion flags drive the totals (flag-driven, matches Settings).
    const rowFlagByType = costRows.reduce((acc, row) => {
      acc[row.typeKey] = {
        includeInTotalCost: row.includeInTotalCost,
        includeInLinguistPay: row.includeInLinguistPay,
        includeInAccountCharge: row.includeInAccountCharge,
      };
      return acc;
    }, {});

    const mappedActiveOptions = activeCostOptions
      .filter((opt) => opt.key !== "linguistPay")
      // A cost type whose row was REMOVED from the list is out of all three
      // totals (2026-09-23, per Amine — "Travel Time still accounted for
      // after removing it"): without this, a removed type kept contributing
      // through its default flags — and Travel Time even kept its
      // duration-based linguist figure, because that figure never comes from
      // the row value. Any type that still holds a real value gets its row
      // re-added by the keep-rows-in-sync effect, so nothing with data is
      // dropped — absence of a row here genuinely means "removed".
      .filter((opt) => Boolean(rowFlagByType[opt.key]))
      .map((opt) => {
        const rowFlags = rowFlagByType[opt.key];
        return {
          ...opt,
          includeInLinguistPay: rowFlags
            ? rowFlags.includeInLinguistPay
            : typeof opt.effectiveIncludeInLinguistPay === "boolean"
              ? opt.effectiveIncludeInLinguistPay
              : typeof opt.includeInLinguistPay === "boolean"
                ? opt.includeInLinguistPay
                : !!baseOptionByKey[opt.key]?.includeInLinguistPay,
          effectiveIncludeInTotalCost: rowFlags
            ? rowFlags.includeInTotalCost
            : opt.effectiveIncludeInTotalCost,
          effectiveIncludeInAccountCharge: rowFlags
            ? rowFlags.includeInAccountCharge
            : opt.effectiveIncludeInAccountCharge,
        };
      });

    const chargeExtras = mappedActiveOptions.filter(
      (opt) => !!opt.effectiveIncludeInAccountCharge,
    );
    const extraChargeSum = chargeExtras.reduce(
      (sum, opt) => sum + amountForOption(opt),
      0,
    );
    // Each extra renders as "+ 98.80" with a hover tooltip naming the cost
    // type (2026-09-23, per Amine).
    const chargeExtraParts = chargeExtras.flatMap((opt) => [
      textPart(" + "),
      { text: amountForOption(opt).toFixed(2), tip: opt.label },
    ]);
    const totCostExtras = mappedActiveOptions.filter(
      (opt) => !!opt.effectiveIncludeInTotalCost,
    );
    const extraTotCostSum = totCostExtras.reduce(
      (sum, opt) => sum + amountForOption(opt),
      0,
    );
    const totCostExtraParts = totCostExtras.flatMap((opt) => [
      textPart(" + "),
      { text: amountForOption(opt).toFixed(2), tip: opt.label },
    ]);

    if (mode === "Simultaneous") {
      // Calculate the "core" time (total - prep - sched), never negative
      const coreDecimal = Math.max(
        durationDecimal - prepDecimal - schedDecimal,
        0,
      );

      // Linguist pay: rate × (prep + sched) + rateSimultaneous × coreDecimal
      const rateVal = parseFloat(rate || 0);
      const rateSimultaneousVal = parseFloat(rateSimultaneous || 0);

      pay =
        rateVal * (prepDecimal + schedDecimal) +
        rateSimultaneousVal * coreDecimal;

      const totalLingExtras = mappedActiveOptions.filter(
        (opt) => opt.includeInLinguistPay,
      );
      const extraLingPaySum = totalLingExtras.reduce(
        (sum, opt) => sum + travelTimeValueForLinguistPay(opt),
        0,
      );

      // TotalLinguistPay = base pay + all cost types marked includeInLinguistPay
      totalLingPay =
        rateVal * (prepDecimal + schedDecimal) +
        rateSimultaneousVal * coreDecimal +
        extraLingPaySum;

      const lingExtraParts = totalLingExtras.flatMap((opt) => [
        textPart(" + "),
        {
          text: travelTimeValueForLinguistPay(opt).toFixed(2),
          tip:
            opt.key === "travelTimeCost"
              ? "Travel Time — hours × half the linguist rate"
              : opt.label,
        },
      ]);
      totalLingPayParts = [
        numPart(rateSimultaneousVal, "Simultaneous linguist rate ($/hr)"),
        textPart(" x "),
        numPart(coreDecimal, "Simultaneous duration (hours)"),
        textPart(" + "),
        numPart(rateVal, "Linguist rate ($/hr)"),
        textPart(" x ["),
        numPart(prepDecimal, "Preparation time (hours)"),
        textPart(" + "),
        numPart(schedDecimal, "Scheduling time (hours)"),
        textPart("]"),
        ...lingExtraParts,
      ];

      // Client charge - use simultaneous rate for core time, consecutive rate for prep/scheduling
      const clientRateSimultaneousAdj =
        (clientRateSimultaneous / 100) * (100 - clientRateDiscount) || 0;
      const clientRateAdj =
        (clientRate / 100) * (100 - clientRateDiscount) || 0;

      charge =
        clientRateSimultaneousAdj * coreDecimal +
        clientRateAdj * (prepDecimal + schedDecimal) +
        extraChargeSum;

      chargeParts = [
        textPart("( "),
        numPart(
          clientRateSimultaneousAdj,
          clientRateTip("Simultaneous client rate ($/hr)"),
        ),
        textPart(" x "),
        numPart(coreDecimal, "Simultaneous duration (hours)"),
        textPart(" + "),
        numPart(clientRateAdj, clientRateTip("Client rate ($/hr)")),
        textPart(" x ["),
        numPart(schedDecimal, "Scheduling time (hours)"),
        textPart(" + "),
        numPart(prepDecimal, "Preparation time (hours)"),
        textPart("] )"),
        ...chargeExtraParts,
      ];
    } else {
      // Consecutive: all time at 1x
      pay = parseFloat(rate || 0) * parseFloat(durationDecimal || 0);

      const totalLingExtras = mappedActiveOptions.filter(
        (opt) => opt.includeInLinguistPay,
      );
      const extraLingPaySum = totalLingExtras.reduce(
        (sum, opt) => sum + travelTimeValueForLinguistPay(opt),
        0,
      );

      // TotalLinguistPay = base pay + all cost types marked includeInLinguistPay
      const rateVal = parseFloat(rate || 0);
      totalLingPay =
        rateVal * parseFloat(durationDecimal || 0) + extraLingPaySum;

      const lingExtraParts = totalLingExtras.flatMap((opt) => [
        textPart(" + "),
        {
          text: travelTimeValueForLinguistPay(opt).toFixed(2),
          tip:
            opt.key === "travelTimeCost"
              ? "Travel Time — hours × half the linguist rate"
              : opt.label,
        },
      ]);
      totalLingPayParts = [
        numPart(rateVal, "Linguist rate ($/hr)"),
        textPart(" x "),
        numPart(durationDecimal, "Duration (hours)"),
        ...lingExtraParts,
      ];

      const clientRateAdj =
        (clientRate / 100) * (100 - clientRateDiscount) || 0;
      charge =
        clientRateAdj * parseFloat(durationDecimal || 0) + extraChargeSum;

      chargeParts = [
        textPart("( "),
        numPart(clientRateAdj, clientRateTip("Client rate ($/hr)")),
        textPart(" x "),
        numPart(durationDecimal, "Duration (hours)"),
        textPart(" )"),
        ...chargeExtraParts,
      ];
    }

    // Calculate new TotalCost driven by per-row includeInTotalCost flags
    // (the linguist pay base always counts toward Total Cost).
    const totCost = pay + extraTotCostSum;

    const totCostParts = [
      { text: pay.toFixed(2), tip: "Linguist Pay (base)" },
      ...totCostExtraParts,
    ];

    const prof = parseFloat(charge) - parseFloat(totCost);

    const newPay = pay.toFixed(2);
    if (newPay !== linguistPay) {
      setLinguistPay(newPay);
    }

    // Update TotalLinguistPay
    const newTotalLingPay = totalLingPay.toFixed(2);
    if (newTotalLingPay !== totalLinguistPay) {
      setTotalLinguistPay(newTotalLingPay);
    }

    setAccountChargeHelperParts(chargeParts);
    setTotalCostHelperParts(totCostParts);
    setTotalLinguistPayHelperParts(totalLingPayParts);
    // Update calculated values
    setTotalCost(totCost.toFixed(2));
    setAccountCharge(charge.toFixed(2));
    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (parseFloat(charge) === 0) {
      setMargin(0);
    } else {
      const calculatedMargin = (parseFloat(prof) / parseFloat(charge)) * 100;
      setMargin(
        isNaN(calculatedMargin) || !isFinite(calculatedMargin)
          ? 0
          : calculatedMargin.toFixed(2),
      );
    }
  }, [
    interpretationStatus,
    totalDuration,
    clientRate,
    clientRateSimultaneous,
    rate,
    rateSimultaneous,
    parkingFeesTolls,
    additionalCost,
    paymentFees,
    additionalLinguistCost,
    equipmentCost,
    mileageCost,
    travelTimeCost,
    travelTimeDuration,
    mode,
    twoHoursMin,
    clientRateDiscount,
    linguistPay,
    preparationTime,
    schedulingTime,
    disableAutomaticCalculationOfCharges,
    allowShortAssignments,
    totalLinguistPay,
    costRows,
    costTypeOptions,
    getCostValueByType,
    cancelCharges,
  ]);

  // REMOVED 2026-09-22 (financial bug): the old "manual mode keeps Total
  // Cost / Total Linguist Pay aligned with Linguist Pay" effect forced both
  // totals to equal the base-pay row. The totals are SUMS (base + flagged
  // cost rows — see the auto-calc above), so the alignment dropped every
  // extra (mileage/parking/etc.) and, combined with the onChange cross-
  // writes, corrupted the Linguist Pay row itself on save. Manual mode now
  // leaves all three values under user control; nothing auto-overwrites.

  // Recalculate profit and margin whenever accountCharge or totalCost changes
  useEffect(() => {
    const charge = parseFloat(accountCharge || 0);
    const totCost = parseFloat(totalCost || 0);
    const prof = charge - totCost;

    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const calculatedMargin = (prof / charge) * 100;
      setMargin(
        isNaN(calculatedMargin) || !isFinite(calculatedMargin)
          ? 0
          : calculatedMargin.toFixed(2),
      );
    }
  }, [accountCharge, totalCost]);

  // Update mileage cost when mileage roundtrip or date changes.
  // Skipped entirely while the Mileage row is absent (2026-09-23, per Amine):
  // otherwise the derive ran again on the next change (e.g. a date edit or a
  // re-enabled auto-calc) and the keep-rows-in-sync effect re-created the row
  // the user had removed.
  useEffect(() => {
    if (!shouldAutoCalculateMileageCost) {
      return;
    }
    if (!hasMileageRow) return;

    if (mileageRoundtrip && date && date.isValid()) {
      const mileageRate = getMileageRateForYear(date);
      setMileageCost(
        (parseFloat(mileageRoundtrip || 0) * mileageRate).toFixed(2),
      );
    } else {
      setMileageCost(0);
    }
  }, [
    mileageRoundtrip,
    date,
    getMileageRateForYear,
    shouldAutoCalculateMileageCost,
    hasMileageRow,
  ]);

  // Update travel time cost when travel time duration or client rate changes.
  // Skipped entirely while the Travel Time row is absent (2026-09-23, per
  // Amine): otherwise a client-rate change silently re-derived a non-zero
  // cost, which the keep-rows-in-sync effect then used to re-create the very
  // row the user had removed.
  useEffect(() => {
    if (!hasTravelTimeRow) return;
    if (travelTimeDuration && travelTimeDuration.isValid()) {
      const decimalHours =
        (travelTimeDuration.hour() * 60 + travelTimeDuration.minute()) / 60;
      const rate = parseFloat(clientRate) || 0;
      const calculatedCost = ((decimalHours * rate) / 2).toFixed(2);
      setTravelTimeCost(calculatedCost);
    } else {
      setTravelTimeCost(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelTimeDuration, clientRate, hasTravelTimeRow]);

  return (
    <>
    <Overlay
      id="interpretation-new-edit"
      open={true}
      onClose={handleBackButtonClick}
      title={
        isNewAssignment
          ? "New Interpretation Assignment"
          : completing
            ? "Complete Interpretation Assignment"
            : "Edit Interpretation Assignment:"
      }
      warnings={[
        ...(selectedDateHoliday
          ? [
              {
                type: "info",
                message: `${selectedDateHoliday.Name}${selectedDateHoliday.Description ? ` (${selectedDateHoliday.Description})` : ""}`,
              },
            ]
          : []),
        ...(timezoneWarning
          ? [{ type: "warning", message: timezoneWarning }]
          : []),
        ...(paidLocked
          ? [
              {
                type: "error",
                message:
                  "Editing is disabled for services on a paid invoice. Un-mark the invoice as paid first to edit this service.",
              },
            ]
          : []),
      ]}
      backButtonProps={{
        onMouseDown: handleBackButtonPressStart,
        onMouseUp: handleBackButtonPressEnd,
        onMouseLeave: handleBackButtonPressEnd,
        onTouchStart: handleBackButtonPressStart,
        onTouchEnd: handleBackButtonPressEnd,
        onTouchCancel: handleBackButtonPressEnd,
      }}
      headerActions={
        isNewAssignment ? (
          <>
            <IconButton
              onClick={handleMenuOpen}
              size="large"
              sx={{ alignSelf: "center" }}
            >
              <MoreVertIcon fontSize="inherit" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleUploadClick}>
                <ListItemIcon>
                  <UploadFileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Upload OPI Logs</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : null
      }
      footer={
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "50%",
              flexDirection: "row",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color:
                  progress < 50
                    ? theme.palette.error[600]
                    : progress < 100
                      ? theme.palette.warning[500]
                      : theme.palette.primary.main,
              }}
            >
              {progressMessage}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "50%",
              flexDirection: "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Discard">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      releaseLock();
                      setOverlayName("");
                    }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Save">
                  <IconButton
                    onClick={attemptSave}
                    disabled={isSavingChanges || isLocked || paidLocked}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.grey[400],
                        color: theme.palette.grey[200],
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => {
                    releaseLock();
                    setOverlayName("");
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  Discard
                </Button>
                <Box p="0 .5rem" />
                <Button
                  loading={isSavingChanges}
                  disabled={isSavingChanges || isLocked || paidLocked}
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<SaveRoundedIcon />}
                  type="button"
                  onClick={attemptSave}
                  sx={{ borderRadius: "12px" }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        </>
      }
    >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          component="form"
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
              height: "90%",
            },
          }}
        >
          <Grid container spacing={2} width="100%">
              {!completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <MobileDatePicker
                      disabled={!isEditable}
                      id={"Date"}
                      label="Date"
                      size="small"
                      format="dddd, MMMM DD, YYYY"
                      value={date}
                      onChange={async (newValue) => {
                        setDate(newValue);
                        setDateError(newValue === null);

                        if (newValue) {
                          setRepeatUntilDate(newValue.add(1, "month"));

                          const repeatUntilDateTemp = newValue.add(
                            1,
                            period === "Month" ? "year" : "month",
                          );

                          setRepeatUntilDate(repeatUntilDateTemp);
                        }

                        const selectedDays =
                          tempSelectedDays.length === 0
                            ? [newValue?.format("ddd") || ""]
                            : tempSelectedDays;

                        handleRecursionChange(
                          newValue,
                          repeatEvery,
                          period,
                          repeatUntilDate,
                          selectedDays,
                          showRecurring,
                        );

                        if (newValue) {
                          if (
                            newValue.format("YYYY-MM-DD") === overlayData.Date
                          ) {
                            setProjectNumber(overlayData.ProjectNumber);
                          } else {
                            // Get count for this date and generate project number
                            const count = await getDateCount(newValue);
                            const projNum = generateProjectNumber(
                              newValue,
                              count,
                            );
                            setProjectNumber(projNum);
                          }
                        }
                      }}
                      //onChange={}

                      slotProps={{
                        textField: {
                          size: "small",
                          error: dateError,
                        },
                      }}
                      sx={{
                        ...getShakeSx("Date"),
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            (date && date.format("YYYY-MM-DD")) || "",
                            overlayData.Date,
                          ),
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"ProjectNumber"}
                      label="Project Number"
                      error={projectNumberError || projectNumberExists}
                      value={projectNumber}
                      onChange={(event) => {
                        setProjectNumber(event.target.value);
                        setProjectNumberError(event.target.value === null);
                        setProjectNumberExists(false);
                      }}
                      onBlur={async () => {
                        if (projectNumber && isNewAssignment) {
                          try {
                            const response = await api.get(
                              `/services/check-project-number?projectNumber=${projectNumber}`,
                            );
                            setProjectNumberExists(response.data.exists);
                          } catch (error) {
                            console.error(
                              "Error checking project number:",
                              error,
                            );
                          }
                        }
                      }}
                      helperText={
                        projectNumberExists
                          ? "This project number already exists"
                          : ""
                      }
                      sx={{
                        ...getTextFieldBorderStyles(
                          projectNumberExists
                            ? theme.palette.error.main
                            : getBorderColor(
                                theme,
                                isNewAssignment,
                                projectNumber || "",
                                overlayData.ProjectNumber,
                              ),
                        ),
                        ...getShakeSx("ProjectNumber"),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"Name"}
                      label="Name"
                      error={nameError}
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value);
                        setNameError(event.target.value === null);

                        fillDetails(
                          requestor.Name,
                          event.target.value,
                          language.Name,
                          interpretationType,
                        );
                      }}
                      sx={{
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            name || "",
                            overlayData.Name,
                          ),
                        ),
                        ...getShakeSx("Name"),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id={"InterpretationType"}
                      label="Interpretation Type"
                      error={interpretationTypeError}
                      value={interpretationType}
                      onChange={(event) => {
                        setInterpretationType(event.target.value);
                        setInterpretationTypeError(event.target.value === null);

                        fillDetails(
                          requestor.Name,
                          name,
                          language.Name,
                          event.target.value,
                        );
                      }}
                      sx={{
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            interpretationType || "",
                            overlayData.ServiceSubtype?.Name,
                          ),
                        ),
                        ...getShakeSx("InterpretationType"),
                      }}
                    >
                      {interpretationTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              ) : null}

              {!completing ? (
                <Grid container size={{ xs: 12, md: 6 }}>
                  <Grid size={{ xs: 12 }}>
                    <Autocomplete
                      disabled={!isEditable}
                      disableClearable
                      key={"Client"}
                      size="small"
                      sx={{
                        height: "auto !important",
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          minHeight: "40px !important",
                          height: "40px !important",
                        },
                        "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                          padding: "8.5px 12px !important",
                        },
                      }}
                      options={overlayData.Clients}
                      getOptionLabel={(clientOption) =>
                        clientOption?.Account || ""
                      }
                      value={client}
                      onChange={(event, newValue) => {
                        let nextClient = null;

                        if (typeof newValue === "string") {
                          nextClient = { Account: newValue };
                          setAddNewClient(false);
                        } else if (newValue && newValue.inputValue) {
                          // Create a new value from the user input.
                          nextClient = { Account: newValue.inputValue };
                          setAddNewClient(false);
                        } else {
                          nextClient = newValue;
                        }

                        setClient(nextClient);
                        setClientError(nextClient === null);

                        const currentLanguageId =
                          language?.LanguageID || language?.id;
                        if (nextClient?.id && currentLanguageId) {
                          const nextClientRate = updateClientRate(
                            nextClient,
                            currentLanguageId,
                          );
                          setClientRate(nextClientRate || 0);
                          setClientRateSimultaneous(
                            nextClientRate ? Number(nextClientRate) : 0,
                          );
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client"
                          size="small"
                          error={clientError}
                          sx={{
                            height: "auto !important",
                            ...getTextFieldBorderStyles(
                              getBorderColor(
                                theme,
                                isNewAssignment,
                                (client && client.Account) || "",
                                (overlayData.Client &&
                                  overlayData.Client.Account) ||
                                  "",
                              ),
                            ),
                            "& .MuiOutlinedInput-root": {
                              py: 0,
                              minHeight: "40px !important",
                              height: "40px !important",
                              "&.MuiInputBase-root fieldset": {
                                borderColor: getBorderColor(
                                  theme,
                                  isNewAssignment,
                                  (client && client.Account) || "",
                                  (overlayData.Client &&
                                    overlayData.Client.Account) ||
                                    "",
                                ),
                              },
                            },
                            ...getShakeSx("Client"),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"Mode"}
                  label="Mode"
                  error={modeError}
                  value={mode}
                  onChange={(event) => {
                    setMode(event.target.value);
                    setModeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          mode || "",
                          overlayData.Mode,
                          true,
                        ),
                      },
                    },
                  }}
                >
                  {["Consecutive", "Simultaneous"].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {!completing ? (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    disabled={!isEditable}
                    disableClearable
                    key={"Language"}
                    size="small"
                    options={languageOptions}
                    getOptionLabel={(language) => language?.Name || ""}
                    renderOption={renderInterpretationLanguageOption}
                    value={language}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        setLanguage({
                          Name: newValue,
                        });
                        fillDetails(
                          requestor.Name,
                          name,
                          newValue,
                          interpretationType,
                        );
                      } else if (newValue && newValue.inputValue) {
                        // Create a new value from the user input
                        setLanguage({
                          Name: newValue.inputValue,
                        });
                        fillDetails(
                          requestor.Name,
                          name,
                          newValue.inputValue,
                          interpretationType,
                        );
                      } else {
                        setLanguage(newValue);
                        fillDetails(
                          requestor.Name,
                          name,
                          newValue.Name,
                          interpretationType,
                        );

                        if (client) {
                          const tempRate = updateClientRate(
                            client,
                            newValue.id,
                          );

                          setClientRate(tempRate);
                          setClientRateSimultaneous(tempRate);
                        }
                      }

                      setSelectedOfferLinguists([]);
                      setRate(0);
                      setRateSimultaneous(0);
                      setLanguageError(newValue === null);
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);

                      const { inputValue } = params;
                      // Suggest the creation of a new value
                      const isExisting = options.some(
                        (option) => inputValue === option.Name,
                      );
                      if (inputValue !== "" && !isExisting) {
                        filtered.push({
                          inputValue,
                          Name: `Add "${inputValue}"`,
                        });
                      }

                      return filtered;
                    }}
                    selectOnFocus
                    clearOnBlur
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Language"
                        placeholder=""
                        error={languageError}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (language && language?.Name) || "",
                            (overlayData &&
                              overlayData.ServiceTargetLanguages &&
                              overlayData.ServiceTargetLanguages[0].Language
                                ?.Name) ||
                              "",
                          ),
                        },
                      },
                      ...getShakeSx("Language"),
                    }}
                  />
                </Grid>
              ) : null}

              <Grid
                size={{
                  xs: 12,
                  md:
                    completing || isNewAssignment
                      ? mode === "Simultaneous"
                        ? completing
                          ? 2
                          : 3
                        : completing
                          ? 3
                          : 6
                      : mode === "Simultaneous"
                        ? 2
                        : 3,
                }}
              >
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"Client Rate"}
                  label="Client Rate"
                  error={clientRateError}
                  value={clientRate}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  onChange={(event) => {
                    setClientRate(event.target.value);
                    setClientRateError(event.target.value === null);
                    setClientRateSimultaneous(event.target.value);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(clientRate) || "",
                          Number(initialClientRate),
                        ),
                      },
                    },
                    ...getShakeSx("ClientRate"),
                  }}
                />
              </Grid>

              {mode === "Simultaneous" ? (
                <Grid size={{ xs: 12, md: isNewAssignment ? 3 : 2 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"Client Rate Simultaneous"}
                    label="Client Rate (Simultaneous)"
                    error={clientRateSimultaneousError}
                    value={clientRateSimultaneous}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                    onChange={(event) => {
                      setClientRateSimultaneous(event.target.value);
                      // Blank/zero = missing (would silently bill $0); clear
                      // the error once a valid rate is entered.
                      setClientRateSimultaneousError(
                        event.target.value === "" ||
                          event.target.value === null ||
                          Number(event.target.value) === 0,
                      );
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            Number(clientRateSimultaneous) || "",
                            Number(overlayData.ServiceClientRateSimultaneous) ||
                              "",
                            true,
                          ),
                        },
                      },
                      ...getShakeSx("ClientRateSimultaneous"),
                    }}
                  />
                </Grid>
              ) : null}

              {completing || !isNewAssignment ? (
                <Grid size={{ xs: 12, md: mode === "Simultaneous" ? 2 : 3 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"Client Rate Discount"}
                    label="Client Rate Discount"
                    error={clientRateDiscountError}
                    value={clientRateDiscount}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      },
                    }}
                    onChange={(event) => {
                      setClientRateDiscount(event.target.value);
                      setClientRateDiscountError(event.target.value === null);
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            Number(clientRateDiscount) || "",
                            Number(overlayData.ClientRateDiscount),
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
              ) : null}
              {!completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      disabled={!isEditable}
                      disableClearable
                      key={"Requestor"}
                      size="small"
                      id={"Requestor"}
                      options={requestorOptions}
                      getOptionLabel={(requestor) => requestor?.Name || ""}
                      renderOption={renderInterpretationRequestorOption}
                      value={requestor}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          const nextRequestor = {
                            Name: newValue,
                          };
                          setRequestor(nextRequestor);
                          setAddNewRequestor(false);
                          // Default the invoice recipient to the requestor.
                          applyRequestorInvoiceDefaults(nextRequestor);
                          fillDetails(
                            newValue,
                            name,
                            language.Name,
                            interpretationType,
                          );
                        } else if (newValue && newValue.inputValue) {
                          // Create a new value from the user input
                          const nextRequestor = {
                            Name: newValue.inputValue,
                          };
                          setRequestor(nextRequestor);
                          setAddNewRequestor(true);
                          // Default the invoice recipient to the requestor.
                          applyRequestorInvoiceDefaults(nextRequestor);
                          fillDetails(
                            newValue.inputValue,
                            name,
                            language.Name,
                            interpretationType,
                          );
                        } else {
                          setRequestor(newValue);
                          setAddNewRequestor(false);
                          // Auto-populate invoice recipient + CC from the
                          // requestor's group (or default to the requestor).
                          applyRequestorInvoiceDefaults(newValue);
                          fillDetails(
                            newValue.Name,
                            name,
                            language.Name,
                            interpretationType,
                          );
                        }

                        setRequestorError(newValue === null);
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some(
                          (option) => inputValue === option.Name,
                        );
                        if (inputValue !== "" && !isExisting) {
                          filtered.push({
                            inputValue,
                            Name: `Add "${inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Requestor"
                          placeholder=""
                          error={requestorError}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (requestor && requestor.Name) || "",
                              (overlayData &&
                                overlayData.Requestor &&
                                overlayData.Requestor.Name) ||
                                "",
                            ),
                          },
                        },
                        ...getShakeSx("Requestor"),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      disabled={!isEditable}
                      disableClearable
                      key={"InvoiceTo"}
                      size="small"
                      id={"InvoiceTo"}
                      options={(client && client.Requestors) || []}
                      getOptionLabel={(requestor) => requestor?.Name || ""}
                      value={invoiceTo}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setInvoiceTo({
                            Name: newValue,
                          });
                          setAddNewInvoiceTo(false);
                        } else if (newValue && newValue.inputValue) {
                          // Create a new value from the user input
                          setInvoiceTo({
                            Name: newValue.inputValue,
                          });
                          setAddNewInvoiceTo(true);
                        } else {
                          setInvoiceTo(newValue);
                          setAddNewInvoiceTo(false);
                        }

                        setInvoiceToError(newValue === null);
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some(
                          (option) => inputValue === option.Name,
                        );
                        if (inputValue !== "" && !isExisting) {
                          filtered.push({
                            inputValue,
                            Name: `Add "${inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Invoice To"
                          placeholder=""
                          error={invoiceToError}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (invoiceTo && invoiceTo?.Name) || "",
                              (overlayData.Invoicer &&
                                overlayData.Invoicer?.Name) ||
                                "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                    {ccRequestors.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        CC: {ccRequestors.map((cc) => cc?.Name).join(", ")}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 6 }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"Address"}
                    label="Address"
                    error={addressError}
                    value={address}
                    onChange={(event) => {
                      setAddress(event.target.value);
                      setAddressError(event.target.value === null);
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            address || "",
                            overlayData.Address,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"InterpretationStatus"}
                  label="Interpretation Status"
                  error={interpretationStatusError}
                  //defaultValue={interpretationStatus === null ? "Requested" : ""}
                  value={interpretationStatus}
                  onChange={(event) => {
                    setInterpretationStatus(event.target.value);
                    setInterpretationStatusError(event.target.value === null);

                    if (!event.target.value.startsWith("Cancel"))
                      setCancellationReason("");
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          interpretationStatus || "",
                          overlayData.InterpretationStatus,
                        ),
                      },
                    },
                  }}
                >
                  {overlayData.InterpretationStatuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disableClearable
                  key={"Linguist"}
                  size="small"
                  multiple
                  limitTags={2}
                  filterSelectedOptions
                  disableCloseOnSelect
                  options={filterLinguistsByLanguage(
                    linguistOptions,
                    language?.Name,
                    showAllLinguists,
                  )}
                  getOptionLabel={(linguistOption) =>
                    linguistOption?.Name || ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    const optionId = Number(option?.id || 0);
                    const valueId = Number(value?.id || 0);

                    if (optionId > 0 && valueId > 0) {
                      return optionId === valueId;
                    }

                    return (option?.Name || "") === (value?.Name || "");
                  }}
                  value={offerLinguists}
                  inputValue={linguistInputValue}
                  renderOption={renderLinguistOption}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "reset") {
                      setLinguistInputValue("");
                      return;
                    }

                    setLinguistInputValue(newInputValue);
                  }}
                  onChange={(event, newValue) => {
                    const linguistById = new Map(
                      (linguistOptions || [])
                        .filter(
                          (linguistOption) =>
                            Number(linguistOption?.id || 0) > 0,
                        )
                        .map((linguistOption) => [
                          Number(linguistOption.id),
                          linguistOption,
                        ]),
                    );

                    const linguistByName = new Map(
                      (linguistOptions || [])
                        .filter(
                          (linguistOption) =>
                            String(linguistOption?.Name || "").trim().length >
                            0,
                        )
                        .map((linguistOption) => [
                          String(linguistOption.Name).trim().toLowerCase(),
                          linguistOption,
                        ]),
                    );

                    const nextLinguists = Array.isArray(newValue)
                      ? newValue.map((item) => {
                          if (typeof item === "string") {
                            return { Name: item };
                          }

                          if (item && item.inputValue) {
                            return { Name: item.inputValue };
                          }

                          const itemId = Number(item?.id || 0);
                          if (itemId > 0) {
                            const matchedById = linguistById.get(itemId);
                            if (matchedById) {
                              return {
                                ...matchedById,
                                ...item,
                              };
                            }
                          }

                          const normalizedName = String(item?.Name || "")
                            .trim()
                            .toLowerCase();
                          if (normalizedName) {
                            const matchedByName =
                              linguistByName.get(normalizedName);
                            if (matchedByName) {
                              return {
                                ...matchedByName,
                                ...item,
                              };
                            }
                          }

                          return item;
                        })
                      : [];

                    const toDisplayName = (linguistOption) =>
                      String(linguistOption?.Name || "").trim() ||
                      `Linguist #${linguistOption?.id || "Unknown"}`;

                    const portalEligible = nextLinguists.filter(
                      (linguistOption) =>
                        String(linguistOption?.FirebaseUID || "").trim()
                          .length > 0,
                    );
                    const portalNotEligible = nextLinguists.filter(
                      (linguistOption) =>
                        String(linguistOption?.FirebaseUID || "").trim()
                          .length === 0,
                    );

                    console.groupCollapsed(
                      `[Interpretation Offers] Linguist selection updated (${nextLinguists.length} selected)`,
                    );
                    console.log(
                      "Selected linguists:",
                      nextLinguists.map((linguistOption) => ({
                        id: linguistOption?.id || null,
                        name: toDisplayName(linguistOption),
                        hasPortalAccess:
                          String(linguistOption?.FirebaseUID || "").trim()
                            .length > 0,
                      })),
                    );
                    console.log(
                      "Will get Portal offer:",
                      portalEligible.map((linguistOption) =>
                        toDisplayName(linguistOption),
                      ),
                    );
                    console.log(
                      "Will NOT get Portal offer:",
                      portalNotEligible.map((linguistOption) =>
                        toDisplayName(linguistOption),
                      ),
                    );
                    console.groupEnd();

                    setSelectedOfferLinguists(nextLinguists);

                    const nextPrimaryLinguist = nextLinguists[0] || null;

                    if (
                      nextPrimaryLinguist &&
                      nextPrimaryLinguist.LinguistLanguages &&
                      nextPrimaryLinguist.LinguistLanguages.length > 0
                    ) {
                      const linguistCurrentRateRaw =
                        nextPrimaryLinguist.LinguistLanguages[0].Rate;

                      const assignmentRateRaw = overlayData.Rate;
                      const linguistCurrentRateNum = Number(
                        linguistCurrentRateRaw,
                      );
                      const assignmentRateNum = Number(assignmentRateRaw);

                      const linguistCurrentRate = Number.isNaN(
                        linguistCurrentRateNum,
                      )
                        ? 0
                        : linguistCurrentRateNum;
                      const assignmentRate = Number.isNaN(assignmentRateNum)
                        ? 0
                        : assignmentRateNum;

                      if (
                        !isNewAssignment &&
                        nextPrimaryLinguist?.id === originalLinguistId &&
                        assignmentRate !== linguistCurrentRate
                      ) {
                        setRateHelperText(
                          `Linguist New Rate: $${linguistCurrentRate} | Linguist Assignment Rate: $${assignmentRate}`,
                        );
                        setRate(assignmentRate);
                        setRateSimultaneous(assignmentRate);
                      } else {
                        setRate(linguistCurrentRate);
                        setRateSimultaneous(linguistCurrentRate);
                        setRateHelperText("");
                      }

                      setTwoHoursMin(
                        nextPrimaryLinguist.MinimumHours &&
                          nextPrimaryLinguist.MinimumHours === 2,
                      );

                      setIsSavingChanges(false);
                    } else {
                      setRate("");
                      setRateSimultaneous("");
                      setRateHelperText("");
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;

                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const notes = linguistPaymentNotesById[option?.id] || [];
                      const hasNotes = notes.length > 0;
                      const chip = (
                        <Chip
                          size="small"
                          variant="filled"
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {getLinguistFirstName(option?.Name)}
                              {hasNotes && (
                                <FeedbackRoundedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: theme.palette.warning.main,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          {...getTagProps({ index })}
                          key={option?.id || option?.Name || index}
                          sx={{
                            height: 24,
                            borderRadius: 1,
                            fontWeight: 600,
                            backgroundColor: hasNotes
                              ? theme.palette.warning.main + "15"
                              : theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            border: `1px solid ${hasNotes ? theme.palette.warning.main + "40" : theme.palette.divider}`,
                            "& .MuiChip-label": {
                              px: 1,
                            },
                            "& .MuiChip-deleteIcon": {
                              color: theme.palette.text.secondary,
                              fontSize: 16,
                              mr: 0.25,
                              ":hover": {
                                color: theme.palette.text.primary,
                              },
                            },
                          }}
                        />
                      );

                      if (!hasNotes) return chip;

                      return (
                        <HtmlTooltip
                          key={option?.id || option?.Name || index}
                          title={
                            <Box sx={{ p: 0.5, maxWidth: 320 }}>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                display="block"
                                gutterBottom
                              >
                                Payment Notes
                              </Typography>
                              {notes.map((note, noteIdx) => (
                                <Typography
                                  key={noteIdx}
                                  variant="caption"
                                  display="block"
                                  sx={{
                                    mb: noteIdx < notes.length - 1 ? 0.5 : 0,
                                    pb: noteIdx < notes.length - 1 ? 0.5 : 0,
                                    borderBottom:
                                      noteIdx < notes.length - 1
                                        ? `1px solid ${theme.palette.divider}`
                                        : "none",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {note.Body}
                                </Typography>
                              ))}
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          {chip}
                        </HtmlTooltip>
                      );
                    })
                  }
                  renderInput={(params) => {
                    const distanceHelperText = isCalculatingLinguistDistances
                      ? "Calculating Distances..."
                      : hasLinguistDistances
                        ? "Distances Calculated"
                        : null;

                    return (
                      <TextField
                        {...params}
                        size="small"
                        label="Offer to Linguists"
                        placeholder=""
                        error={linguistError}
                        helperText={
                          distanceHelperText ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color={
                                isCalculatingLinguistDistances
                                  ? "text.secondary"
                                  : "success.main"
                              }
                            >
                              {distanceHelperText}
                            </Typography>
                          ) : (
                            "Select one or more linguists to receive the offer."
                          )
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {offerLinguists.length > 0 && (
                                <IconButton
                                  onClick={handleClear}
                                  edge="end"
                                  tabIndex={-1}
                                  size="small"
                                  sx={{
                                    p: 0.25,
                                    "& .MuiSvgIcon-root": { fontSize: 16 },
                                  }}
                                >
                                  <ClearRoundedIcon />
                                </IconButton>
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    );
                  }}
                  sx={{
                    ...getShakeSx("Linguist"),
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          linguist?.Name || "",
                          (overlayData.Linguist && overlayData.Linguist.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {/* First row: Rate and 2 Hr Min checkbox */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {/* Rate and RateSimultaneous: split 65% width */}
                    <Box
                      sx={{
                        flex: "1 1 0%",
                        minWidth: "65%",
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: "1 1 50%" }}>
                        <TextField
                          disabled={!isEditable}
                          size="small"
                          id={"Rate"}
                          fullWidth
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            },
                          }}
                          label="Rate"
                          error={rateError}
                          value={rate}
                          helperText={rateHelperText}
                          onChange={(event) => {
                            setRate(event.target.value);
                            setRateError(event.target.value === null);
                            setRateHelperText(""); // clear helper when manually edited
                            setRateSimultaneous(event.target.value);
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              padding: "8.5px 12px !important",
                            },
                            "& .MuiOutlinedInput-root": {
                              py: 0,
                              "&.MuiInputBase-root fieldset": {
                                borderColor: getBorderColor(
                                  theme,
                                  isNewAssignment,
                                  Number(rate) || "",
                                  Number(overlayData.Rate),
                                ),
                              },
                            },
                          }}
                        />
                      </Box>

                      {mode === "Simultaneous" && (
                        <Box sx={{ flex: "1 1 50%" }}>
                          <TextField
                            disabled={!isEditable}
                            size="small"
                            id={"RateSimultaneous"}
                            label="Rate (Simultaneous)"
                            error={rateSimultaneousError}
                            value={rateSimultaneous}
                            fullWidth
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              },
                            }}
                            onChange={(event) => {
                              setRateSimultaneous(event.target.value);
                              // Blank/zero = missing (would silently bill $0);
                              // clear the error once a valid rate is entered.
                              setRateSimultaneousError(
                                event.target.value === "" ||
                                  event.target.value === null ||
                                  Number(event.target.value) === 0,
                              );
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: "8.5px 12px !important",
                              },
                              "& .MuiOutlinedInput-root": {
                                py: 0,
                                "&.MuiInputBase-root fieldset": {
                                  borderColor: getBorderColor(
                                    theme,
                                    isNewAssignment,
                                    Number(rateSimultaneous) || "",
                                    Number(overlayData.RateSimultaneous) || "",
                                    true,
                                  ),
                                },
                              },
                              ...getShakeSx("RateSimultaneous"),
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Tooltip + checkbox: can shrink, max ~35% */}
                    <Box
                      sx={{
                        px: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap", // optional, if you want them to wrap on small screens
                      }}
                    >
                      <HtmlTooltip
                        title={
                          <Box p=".5rem">
                            <Typography
                              color="inherit"
                              style={{ whiteSpace: "pre-line" }}
                            >
                              {
                                "Will the client be charged, and the linguist be paid at a 2 hours minimum for this assignment?"
                              }
                            </Typography>
                          </Box>
                        }
                      >
                        <FormControlLabel
                          control={<Checkbox />}
                          id={"TwoHoursMinimum"}
                          label={"2 Hr Min"}
                          checked={twoHoursMin}
                          onChange={(event) => {
                            setTwoHoursMin(event.target.checked);
                          }}
                        />
                      </HtmlTooltip>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {!completing ? (
                <Grid container size={{ xs: 12, md: 12 }} spacing={1}>
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        mb: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Contact Persons
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={!isEditable}
                        onClick={addContactPersonRow}
                        sx={{ p: 0.5 }}
                      >
                        <AddRoundedIcon fontSize="small" />
                      </IconButton>
                      <Divider
                        sx={{
                          flex: 1,
                          ml: 0.5,
                        }}
                      />
                    </Box>
                  </Grid>
                  {contactPersons.map((contact, index) => (
                    <React.Fragment key={`contact-person-${index}`}>
                      <Grid size={{ xs: 12 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            mb: index === contactPersons.length - 1 ? 0 : 1,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Grid container spacing={1}>
                              <Grid size={{ xs: 12, md: 7 }}>
                                <Autocomplete
                                  disabled={!isEditable}
                                  disableClearable
                                  key={`ContactPerson-${index}`}
                                  size="small"
                                  id={`ContactPerson-${index}`}
                                  options={contactPersonOptions}
                                  getOptionLabel={(contactPerson) =>
                                    contactPerson?.Name || ""
                                  }
                                  value={
                                    contact?.id
                                      ? contactPersonOptions.find(
                                          (option) => option.id === contact.id,
                                        ) || {
                                          id: contact.id,
                                          Name: contact.Name || "",
                                          PhoneNumber:
                                            contact.PhoneNumber || "",
                                        }
                                      : contact?.Name
                                        ? {
                                            Name: contact.Name,
                                            PhoneNumber:
                                              contact.PhoneNumber || "",
                                          }
                                        : null
                                  }
                                  onChange={(event, newValue) => {
                                    handleContactPersonSelect(index, newValue);
                                  }}
                                  filterOptions={(options, params) => {
                                    const filtered = filter(options, params);

                                    const { inputValue } = params;
                                    const isExisting = options.some(
                                      (option) => inputValue === option.Name,
                                    );

                                    if (inputValue !== "" && !isExisting) {
                                      filtered.push({
                                        inputValue,
                                        Name: `Add "${inputValue}"`,
                                      });
                                    }

                                    return filtered;
                                  }}
                                  isOptionEqualToValue={(option, value) => {
                                    if (!option || !value) return false;
                                    if (option.id && value.id)
                                      return option.id === value.id;
                                    return (
                                      (option.Name || "").trim() ===
                                      (value.Name || "").trim()
                                    );
                                  }}
                                  renderOption={(props, option) => {
                                    const { key, ...optionProps } = props;
                                    const optionKey =
                                      option?.id ??
                                      [
                                        option?.inputValue || "",
                                        option?.Name || "",
                                        option?.PhoneNumber || "",
                                        option?.Ext || "",
                                      ].join("|");

                                    return (
                                      <li
                                        {...optionProps}
                                        key={optionKey || key}
                                      >
                                        {option?.Name || ""}
                                      </li>
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      error={Boolean(
                                        contactRowsValidation[index]
                                          ?.nameInvalid,
                                      )}
                                      label={
                                        index === 0
                                          ? "Contact Person"
                                          : `Contact Person ${index + 1}`
                                      }
                                    />
                                  )}
                                  selectOnFocus
                                  clearOnBlur
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      py: 0,
                                      "&.MuiInputBase-root fieldset": {
                                        borderColor: contactRowsValidation[
                                          index
                                        ]?.nameInvalid
                                          ? theme.palette.error.main
                                          : theme.palette.primary.main,
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, md: 2 }}>
                                <TextField
                                  disabled={!isEditable}
                                  size="small"
                                  select
                                  label="Code"
                                  value={contact.countryCode || "US"}
                                  onChange={(event) => {
                                    const selectedCode = event.target.value;
                                    const country = countryOptions.find(
                                      (c) => c.code === selectedCode,
                                    );
                                    setContactPersons((prev) =>
                                      prev.map((row, rowIndex) =>
                                        rowIndex === index
                                          ? {
                                              ...row,
                                              countryCode: selectedCode,
                                              callingCode: country
                                                ? String(country.callingCode)
                                                : row.callingCode,
                                            }
                                          : row,
                                      ),
                                    );
                                  }}
                                  fullWidth
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      py: 0,
                                      "&.MuiInputBase-root fieldset": {
                                        borderColor: getBorderColor(
                                          theme,
                                          isNewAssignment,
                                          contact.countryCode || "US",
                                          "US",
                                        ),
                                      },
                                    },
                                  }}
                                  SelectProps={{
                                    renderValue: (selCode) => {
                                      const c = countryOptions.find(
                                        (o) => o.code === selCode,
                                      );
                                      if (!c) return selCode;
                                      return (
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                          }}
                                        >
                                          <img
                                            src={c.flagUrl}
                                            alt={c.name}
                                            style={{
                                              width: 16,
                                              height: "auto",
                                              borderRadius: 2,
                                            }}
                                          />
                                          <span>+{c.callingCode}</span>
                                        </span>
                                      );
                                    },
                                    MenuProps: {
                                      PaperProps: {
                                        sx: { maxHeight: 350 },
                                      },
                                    },
                                  }}
                                >
                                  {countryOptions.map((country) => (
                                    <MenuItem
                                      key={country.code}
                                      value={country.code}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1.5,
                                          width: "100%",
                                        }}
                                      >
                                        <img
                                          src={country.flagUrl}
                                          alt={country.name}
                                          style={{
                                            width: 20,
                                            height: "auto",
                                            borderRadius: 2,
                                            flexShrink: 0,
                                          }}
                                          loading="lazy"
                                        />
                                        <span
                                          style={{
                                            flex: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            fontSize: 13,
                                          }}
                                        >
                                          {country.name}
                                        </span>
                                        <span
                                          style={{
                                            color: "#999",
                                            fontSize: 12,
                                            flexShrink: 0,
                                          }}
                                        >
                                          +{country.callingCode}
                                        </span>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                              <Grid size={{ xs: 6, md: 2 }}>
                                <TextField
                                  disabled={!isEditable}
                                  size="small"
                                  error={Boolean(
                                    contactRowsValidation[index]?.phoneInvalid,
                                  )}
                                  label={
                                    index === 0
                                      ? "Number"
                                      : `Number ${index + 1}`
                                  }
                                  value={
                                    contact._phoneRaw !== undefined
                                      ? contact._phoneRaw
                                      : contact.PhoneNumber
                                  }
                                  onChange={(event) => {
                                    // Strip all non-digit characters except leading +
                                    let raw = event.target.value;
                                    const hasPlus = raw
                                      .trimStart()
                                      .startsWith("+");
                                    const digits = raw.replace(/\D/g, "");
                                    raw = hasPlus ? "+" + digits : digits;
                                    setContactPersons((prev) =>
                                      prev.map((row, rowIndex) =>
                                        rowIndex === index
                                          ? {
                                              ...row,
                                              PhoneNumber: raw,
                                              _phoneRaw: raw,
                                            }
                                          : row,
                                      ),
                                    );
                                  }}
                                  onFocus={() => {
                                    // Show stripped digits for editing (remove any formatting)
                                    setContactPersons((prev) =>
                                      prev.map((row, rowIndex) => {
                                        if (rowIndex !== index) return row;
                                        const hasPlus =
                                          row.PhoneNumber.trimStart().startsWith(
                                            "+",
                                          );
                                        const digits = row.PhoneNumber.replace(
                                          /\D/g,
                                          "",
                                        );
                                        const clean = hasPlus
                                          ? "+" + digits
                                          : digits;
                                        return { ...row, _phoneRaw: clean };
                                      }),
                                    );
                                  }}
                                  onBlur={() => {
                                    setContactPersons((prev) =>
                                      prev.map((row, rowIndex) => {
                                        if (rowIndex !== index) return row;
                                        const countryCode =
                                          row.countryCode || "US";
                                        const formatted = formatPhoneNumber(
                                          row.PhoneNumber,
                                          countryCode,
                                        );
                                        return {
                                          ...row,
                                          PhoneNumber: formatted,
                                          _phoneRaw: undefined,
                                        };
                                      }),
                                    );
                                  }}
                                  fullWidth
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      py: 0,
                                      "&.MuiInputBase-root fieldset": {
                                        borderColor: contactRowsValidation[
                                          index
                                        ]?.phoneInvalid
                                          ? theme.palette.error.main
                                          : theme.palette.primary.main,
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid size={{ xs: 3, md: 1 }}>
                                <TextField
                                  disabled={!isEditable}
                                  size="small"
                                  label="Ext"
                                  value={contact.Ext || ""}
                                  onChange={(event) => {
                                    updateContactPersonRow(
                                      index,
                                      "Ext",
                                      event.target.value,
                                    );
                                  }}
                                  fullWidth
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      py: 0,
                                      "&.MuiInputBase-root fieldset": {
                                        borderColor: theme.palette.primary.main,
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>

                          {contactPersons.length > 1 ? (
                            <Box
                              sx={{
                                flex: "0 0 auto",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mt: { xs: 0, md: 0.5 },
                              }}
                            >
                              <IconButton
                                disabled={!isEditable}
                                onClick={() => removeContactPersonRow(index)}
                                size="small"
                              >
                                <ClearRoundedIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : null}
                        </Box>
                      </Grid>
                    </React.Fragment>
                  ))}
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ mt: 0.5 }} />
                  </Grid>
                </Grid>
              ) : null}

              {!completing && interpretationStatus?.startsWith("Cancel") ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"CancellationReason"}
                      label="Cancellation Reason"
                      value={cancellationReason}
                      onChange={(event) => {
                        setCancellationReason(event.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              cancellationReason || "",
                              overlayData.CancellationReason,
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 4 }}>
                <MobileTimePicker
                  disabled={completing ? true : !isEditable}
                  value={timeStart || null}
                  label="Start Time"
                  onChange={(newTime) => {
                    setTimeStart(newTime);
                    setTimeStartError(!newTime);
                    setActualStart(newTime);
                    setActualStartError(!newTime);

                    // If start time is 11AM or later, automatically set end time to 1 hour later
                    let effectiveTimeEnd = timeEnd;
                    if (newTime) {
                      const autoEndTime = newTime.add(1, "hour");
                      setTimeEnd(autoEndTime);
                      setTimeEndError(false);
                      setActualEnd(autoEndTime);
                      setActualEndError(false);
                      effectiveTimeEnd = autoEndTime;
                    }

                    if (newTime && effectiveTimeEnd) {
                      const tempDuration = getDurationValue(
                        newTime,
                        effectiveTimeEnd,
                      );

                      setDuration(tempDuration);
                      setActualDuration(tempDuration);

                      const tempTotalDuration = sumDurationValues([
                        tempDuration || 0,
                        schedulingTime,
                        preparationTime,
                      ]);

                      setTotalDuration(tempTotalDuration);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: timeStartError,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColorTimes(
                          (timeStart && timeStart.format("HH:mm:ss")) || "",
                          overlayData.TimeStart || "",
                          (actualStart && actualStart.format("HH:mm:ss")) || "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <MobileTimePicker
                  disabled={completing ? true : !isEditable}
                  value={timeEnd || null}
                  label="End Time"
                  onChange={(newTime) => {
                    setTimeEnd(newTime);
                    setTimeEndError(!newTime);
                    setActualEnd(newTime);
                    setActualEndError(!newTime);

                    if (timeStart && newTime) {
                      const tempDuration = getDurationValue(timeStart, newTime);

                      setDuration(tempDuration);
                      setActualDuration(tempDuration);

                      const tempTotalDuration = sumDurationValues([
                        tempDuration || 0,
                        schedulingTime,
                        preparationTime,
                      ]);

                      setTotalDuration(tempTotalDuration);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: timeEndError,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColorTimes(
                          (timeEnd && timeEnd.format("HH:mm:ss")) || "",
                          overlayData.TimeEnd || "",
                          (actualEnd && actualEnd.format("HH:mm:ss")) || "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TimeField
                  disabled={false}
                  readOnly
                  label="Duration"
                  value={duration}
                  format="HH:mm"
                  size="small"
                  id={"Duration"}
                  slotProps={{
                    textField: {
                      InputProps: createTimeFieldAdornments(
                        theme,
                        !isEditable || !timeStart,
                        () => {
                          if (duration && timeStart) {
                            // Decrement duration by 15 minutes
                            const currentMinutes =
                              duration.hour() * 60 + duration.minute();
                            const newMinutes = Math.max(0, currentMinutes - 15);
                            const newDuration = dayjs()
                              .hour(Math.floor(newMinutes / 60))
                              .minute(newMinutes % 60)
                              .second(0);

                            // Update end time based on new duration
                            const newTimeEnd = timeStart.add(
                              newMinutes,
                              "minute",
                            );

                            setDuration(newDuration);
                            setActualDuration(newDuration);
                            setTimeEnd(newTimeEnd);
                            setActualEnd(newTimeEnd);
                            setTimeEndError(!newTimeEnd);
                            setActualEndError(!newTimeEnd);

                            const tempTotalDuration = sumDurationValues([
                              newDuration
                                ? newDuration.hour() * 60 + newDuration.minute()
                                : 0,
                              schedulingTime,
                              preparationTime,
                            ]);

                            setTotalDuration(tempTotalDuration);
                          }
                        },
                        () => {
                          if (duration && timeStart) {
                            // Increment duration by 15 minutes
                            const currentMinutes =
                              duration.hour() * 60 + duration.minute();
                            const newMinutes = currentMinutes + 15;
                            const newDuration = dayjs()
                              .hour(Math.floor(newMinutes / 60))
                              .minute(newMinutes % 60)
                              .second(0);

                            // Update end time based on new duration
                            const newTimeEnd = timeStart.add(
                              newMinutes,
                              "minute",
                            );

                            setDuration(newDuration);
                            setActualDuration(newDuration);
                            setTimeEnd(newTimeEnd);
                            setActualEnd(newTimeEnd);
                            setTimeEndError(!newTimeEnd);
                            setActualEndError(!newTimeEnd);

                            const tempTotalDuration = sumDurationValues([
                              newDuration
                                ? newDuration.hour() * 60 + newDuration.minute()
                                : 0,
                              schedulingTime,
                              preparationTime,
                            ]);

                            setTotalDuration(tempTotalDuration);
                          }
                        },
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                      cursor: "default",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (duration && duration.format("HH:mm:ss")) || "",
                          overlayData.Duration || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              {timezoneTimeLines.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.25,
                      mt: 0.25,
                      pr: 0.5,
                    }}
                  >
                    {timezoneTimeLines.map((line, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        sx={{
                          color:
                            index === 0
                              ? theme.palette.text.primary
                              : theme.palette.text.secondary,
                          fontWeight: index === 0 ? 600 : 400,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              )}

              {showActualTimes || completing ? (
                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <MobileTimePicker
                      disabled={!isEditable}
                      value={actualStart || null}
                      label="Actual Start"
                      onChange={(newTime) => {
                        setActualStart(newTime);
                        setActualStartError(!newTime);

                        const tempActualDuration = getDurationValue(
                          newTime,
                          actualEnd,
                        );

                        setActualDuration(tempActualDuration);

                        const tempTotalDuration = sumDurationValues([
                          tempActualDuration && duration
                            ? Math.max(
                                tempActualDuration.hour() * 60 +
                                  tempActualDuration.minute(),
                                duration.hour() * 60 + duration.minute(),
                              )
                            : tempActualDuration || duration || 0,
                          schedulingTime,
                          preparationTime,
                        ]);

                        setTotalDuration(tempTotalDuration);
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: actualStartError,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (actualStart && actualStart.format("HH:mm:ss")) ||
                                "",
                              overlayData.ActualStart || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <MobileTimePicker
                      disabled={!isEditable}
                      value={actualEnd || null}
                      label="Actual End"
                      onChange={(newTime) => {
                        setActualEnd(newTime);
                        setActualEndError(!newTime);

                        const tempActualDuration = getDurationValue(
                          actualStart,
                          newTime,
                        );

                        setActualDuration(tempActualDuration);

                        const tempTotalDuration = sumDurationValues([
                          tempActualDuration && duration
                            ? Math.max(
                                tempActualDuration.hour() * 60 +
                                  tempActualDuration.minute(),
                                duration.hour() * 60 + duration.minute(),
                              )
                            : tempActualDuration || duration || 0,
                          schedulingTime,
                          preparationTime,
                        ]);

                        setTotalDuration(tempTotalDuration);
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: actualEndError,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (actualEnd && actualEnd.format("HH:mm:ss")) || "",
                              overlayData.ActualEnd || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Actual Duration"
                      value={actualDuration}
                      format="HH:mm"
                      size="small"
                      id={"ActualDuration"}
                      slotProps={{
                        textField: {
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !isEditable || !actualStart,
                            () => {
                              if (actualDuration && actualStart) {
                                // Decrement actual duration by 15 minutes
                                const currentMinutes =
                                  actualDuration.hour() * 60 +
                                  actualDuration.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newActualDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                // Update actual end time based on new duration
                                const newActualEnd = actualStart.add(
                                  newMinutes,
                                  "minute",
                                );

                                setActualDuration(newActualDuration);
                                setActualEnd(newActualEnd);
                                setActualEndError(!newActualEnd);

                                const tempTotalDuration = sumDurationValues([
                                  newActualDuration && duration
                                    ? Math.max(
                                        newActualDuration.hour() * 60 +
                                          newActualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : newActualDuration || duration || 0,
                                  schedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                            () => {
                              if (actualDuration && actualStart) {
                                // Increment actual duration by 15 minutes
                                const currentMinutes =
                                  actualDuration.hour() * 60 +
                                  actualDuration.minute();
                                const newMinutes = currentMinutes + 15;
                                const newActualDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                // Update actual end time based on new duration
                                const newActualEnd = actualStart.add(
                                  newMinutes,
                                  "minute",
                                );

                                setActualDuration(newActualDuration);
                                setActualEnd(newActualEnd);
                                setActualEndError(!newActualEnd);

                                const tempTotalDuration = sumDurationValues([
                                  newActualDuration && duration
                                    ? Math.max(
                                        newActualDuration.hour() * 60 +
                                          newActualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : newActualDuration || duration || 0,
                                  schedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (actualDuration &&
                                actualDuration.format("HH:mm:ss")) ||
                                "",
                              overlayData.ActualDuration || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Scheduling Time"
                      value={schedulingTime}
                      format="HH:mm"
                      size="small"
                      id={"SchedulingTime"}
                      slotProps={{
                        textField: {
                          error: schedulingTimeError,
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !isEditable,
                            () => {
                              if (schedulingTime) {
                                // Decrement scheduling time by 15 minutes
                                const currentMinutes =
                                  schedulingTime.hour() * 60 +
                                  schedulingTime.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newSchedulingTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setSchedulingTime(newSchedulingTime);
                                setSchedulingTimeError(!newSchedulingTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  newSchedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);

                                // Reset manual flags for charges when times change
                              }
                            },
                            () => {
                              if (schedulingTime) {
                                // Increment scheduling time by 15 minutes
                                const currentMinutes =
                                  schedulingTime.hour() * 60 +
                                  schedulingTime.minute();
                                const newMinutes = currentMinutes + 15;
                                const newSchedulingTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setSchedulingTime(newSchedulingTime);
                                setSchedulingTimeError(!newSchedulingTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  newSchedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              } else {
                                // Initialize with 15 minutes if null
                                const newSchedulingTime = dayjs()
                                  .hour(0)
                                  .minute(15)
                                  .second(0);

                                setSchedulingTime(newSchedulingTime);
                                setSchedulingTimeError(!newSchedulingTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  newSchedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (schedulingTime &&
                                schedulingTime.format("HH:mm:ss")) ||
                                "",
                              overlayData.SchedulingTime || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Preparation Time"
                      value={preparationTime}
                      format="HH:mm"
                      size="small"
                      id={"PreparationTime"}
                      slotProps={{
                        textField: {
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !isEditable,
                            () => {
                              if (preparationTime) {
                                // Decrement preparation time by 15 minutes
                                const currentMinutes =
                                  preparationTime.hour() * 60 +
                                  preparationTime.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newPreparationTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setPreparationTime(newPreparationTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  schedulingTime,
                                  newPreparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                            () => {
                              if (preparationTime) {
                                // Increment preparation time by 15 minutes
                                const currentMinutes =
                                  preparationTime.hour() * 60 +
                                  preparationTime.minute();
                                const newMinutes = currentMinutes + 15;
                                const newPreparationTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setPreparationTime(newPreparationTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  schedulingTime,
                                  newPreparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              } else {
                                // Initialize with 15 minutes if null
                                const newPreparationTime = dayjs()
                                  .hour(0)
                                  .minute(15)
                                  .second(0);

                                setPreparationTime(newPreparationTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  schedulingTime,
                                  newPreparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (preparationTime &&
                                preparationTime.format("HH:mm:ss")) ||
                                "",
                              overlayData.PreparationTime || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Total Duration"
                      value={totalDuration}
                      format="HH:mm"
                      size="small"
                      id={"TotalDuration"}
                      slotProps={{
                        textField: {
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !allowTotalDurationEditing,
                            () => {
                              if (totalDuration) {
                                // Decrement total duration by 15 minutes
                                const currentMinutes =
                                  totalDuration.hour() * 60 +
                                  totalDuration.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newTotalDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setTotalDuration(newTotalDuration);

                                // Reset manual flags for charges when times change
                              }
                            },
                            () => {
                              if (totalDuration) {
                                // Increment total duration by 15 minutes
                                const currentMinutes =
                                  totalDuration.hour() * 60 +
                                  totalDuration.minute();
                                const newMinutes = currentMinutes + 15;
                                const newTotalDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setTotalDuration(newTotalDuration);

                                // Reset manual flags for charges when times change
                              } else {
                                // Initialize with 15 minutes if null
                                const newTotalDuration = dayjs()
                                  .hour(0)
                                  .minute(15)
                                  .second(0);

                                setTotalDuration(newTotalDuration);

                                // Reset manual flags for charges when times change
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (totalDuration &&
                                totalDuration.format("HH:mm:ss")) ||
                                "",
                              overlayData.TotalDuration || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    mb: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2">Costs</Typography>
                  <IconButton
                    size="small"
                    onClick={addCostRow}
                    disabled={
                      !isEditable ||
                      costRows.length >=
                        costTypeOptions.filter(
                          (option) =>
                            option.key === "linguistPay" || option.isEnabled,
                        ).length
                    }
                    sx={{ p: 0.5 }}
                  >
                    <AddRoundedIcon fontSize="small" />
                  </IconButton>
                  <Divider sx={{ flex: 1, ml: 0.5 }} />
                </Box>
              </Grid>

              {costRows.length > 0 && (
                <Grid container spacing={2} size={{ xs: 12 }}>
                  {costRows.map((row) => {
                    const selectedCostTypeKeys = costRows
                      .filter((item) => item.id !== row.id)
                      .map((item) => item.typeKey);
                    const availableOptionsForRow = costTypeOptions.filter(
                      (option) =>
                        option.key === row.typeKey ||
                        (option.isEnabled &&
                          !selectedCostTypeKeys.includes(option.key)),
                    );

                    const currentValue = getCostValueByType(row.typeKey);
                    const isMileageRow = row.typeKey === "mileageCost";
                    const isTravelTimeRow = row.typeKey === "travelTimeCost";
                    const isLinguistPayRow = row.typeKey === "linguistPay";
                    const isLockedLinguistPayRow =
                      row.typeKey === "linguistPay";
                    const isPaymentFeesRow = row.typeKey === "paymentFees";
                    const inclusionChanged =
                      row.includeInTotalCost !== row.originalIncludeInTotalCost ||
                      row.includeInLinguistPay !== row.originalIncludeInLinguistPay ||
                      row.includeInAccountCharge !== row.originalIncludeInAccountCharge;
                    const checkedFlagCount = [
                      row.includeInTotalCost,
                      row.includeInLinguistPay,
                      row.includeInAccountCharge,
                    ].filter(Boolean).length;
                    const isLastCheckedFlag = (flag) =>
                      Boolean(flag) && checkedFlagCount === 1;
                    const costTypeBorderColor = row.typeKey
                      ? theme.palette.primary.main
                      : theme.palette.error.main;
                    const travelTimeHours =
                      travelTimeDuration && travelTimeDuration.isValid()
                        ? (travelTimeDuration.hour() * 60 +
                            travelTimeDuration.minute()) /
                          60
                        : 0;
                    const travelTimeLinguistCost = isTravelTimeRow
                      ? ((travelTimeHours * (parseFloat(rate) || 0)) / 2).toFixed(
                          2,
                        )
                      : "";

                    return (
                      <Grid size={{ xs: 12 }} key={`cost-row-${row.id}`}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Grid container spacing={1.5}>
                              <Grid
                                size={{
                                  xs: 12,
                                  md: "grow",
                                }}
                              >
                                <TextField
                                  disabled={
                                    !isEditable || isLockedLinguistPayRow
                                  }
                                  select
                                  size="small"
                                  label="Cost Type"
                                  value={row.typeKey}
                                  onChange={(event) =>
                                    updateCostRowType(
                                      row.id,
                                      event.target.value,
                                    )
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "& .MuiInputBase-input": {
                                        padding: "8.5px 12px !important",
                                      },
                                      "&.MuiInputBase-root fieldset": {
                                        py: 0,
                                        borderColor: costTypeBorderColor,
                                      },
                                    },
                                  }}
                                >
                                  {availableOptionsForRow.map((option) => (
                                    <MenuItem
                                      key={option.key}
                                      value={option.key}
                                    >
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              {isMileageRow ? (
                                <Grid size={{ xs: 12, md: "grow" }}>
                                  <TextField
                                    disabled={!isEditable}
                                    size="small"
                                    id={"MileageRoundtrip"}
                                    label="Mileage Roundtrip"
                                    error={mileageRoundtripError}
                                    value={mileageRoundtrip}
                                    onFocus={() => {
                                      if (Number(mileageRoundtrip) === 0) {
                                        setMileageRoundtrip("");
                                      }
                                    }}
                                    onBlur={() => {
                                      if (Number(mileageRoundtrip) === 0) {
                                        setMileageRoundtrip(0);
                                      }
                                    }}
                                    onChange={(event) => {
                                      setHasExplicitMileageEdit(true);
                                      applyShouldAutoCalculateMileageCost(true);
                                      setMileageRoundtrip(event.target.value);
                                      setMileageRoundtripError(
                                        event.target.value === null,
                                      );
                                      const mileageRate = getMileageRateForYear(
                                        date || dayjs.tz().toDate(),
                                      );
                                      setMileageCost(
                                        event.target.value
                                          ? (
                                              event.target.value * mileageRate
                                            ).toFixed(2)
                                          : "",
                                      );
                                    }}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        padding: "8.5px 12px !important",
                                      },
                                      "& .MuiOutlinedInput-root": {
                                        py: 0,
                                        "&.MuiInputBase-root fieldset": {
                                          borderColor: getBorderColor(
                                            theme,
                                            isNewAssignment,
                                            Number(mileageRoundtrip) || "",
                                            Number(
                                              overlayData.MileageRoundtrip,
                                            ) || "",
                                            true,
                                          ),
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              ) : null}

                              {isTravelTimeRow ? (
                                <Grid size={{ xs: 12, md: "grow" }}>
                                  <TimeField
                                    disabled={!isEditable}
                                    label="Travel Time Duration"
                                    value={travelTimeDuration}
                                    format="HH:mm"
                                    size="small"
                                    id="TravelTimeDuration"
                                    onChange={(newValue) => {
                                      setTravelTimeDuration(newValue);
                                      if (newValue && newValue.isValid()) {
                                        const decimalHours =
                                          (newValue.hour() * 60 +
                                            newValue.minute()) /
                                          60;
                                        const rate =
                                          parseFloat(clientRate) || 0;
                                        setTravelTimeCost(
                                          ((decimalHours * rate) / 2).toFixed(
                                            2,
                                          ),
                                        );
                                      } else {
                                        setTravelTimeCost(0);
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        InputProps: createTimeFieldAdornments(
                                          theme,
                                          !isEditable,
                                          () => {
                                            const current =
                                              travelTimeDuration ||
                                              dayjs()
                                                .hour(0)
                                                .minute(0)
                                                .second(0);
                                            const currentMinutes =
                                              current.hour() * 60 +
                                              current.minute();
                                            const newMinutes = Math.max(
                                              0,
                                              currentMinutes - 15,
                                            );
                                            setTravelTimeDuration(
                                              dayjs()
                                                .hour(
                                                  Math.floor(newMinutes / 60),
                                                )
                                                .minute(newMinutes % 60)
                                                .second(0),
                                            );
                                          },
                                          () => {
                                            const current =
                                              travelTimeDuration ||
                                              dayjs()
                                                .hour(0)
                                                .minute(0)
                                                .second(0);
                                            const currentMinutes =
                                              current.hour() * 60 +
                                              current.minute();
                                            const newMinutes =
                                              currentMinutes + 15;
                                            setTravelTimeDuration(
                                              dayjs()
                                                .hour(
                                                  Math.floor(newMinutes / 60),
                                                )
                                                .minute(newMinutes % 60)
                                                .second(0),
                                            );
                                          },
                                        ),
                                        sx: {
                                          "& .MuiInputBase-input": {
                                            padding: "8.5px 12px !important",
                                          },
                                          "& .MuiOutlinedInput-root": {
                                            py: 0,
                                            "&.MuiInputBase-root fieldset": {
                                              borderColor: getBorderColor(
                                                theme,
                                                isNewAssignment,
                                                travelTimeDuration
                                                  ? travelTimeDuration.format(
                                                      "HH:mm:ss",
                                                    )
                                                  : "",
                                                overlayData.TravelTimeDuration ||
                                                  "",
                                                true,
                                              ),
                                            },
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              ) : null}

                              <Grid
                                size={{
                                  xs: 12,
                                  md: "grow",
                                }}
                              >
                                <TextField
                                  disabled={!isEditable}
                                  size="small"
                                  label={
                                    row.typeKey === "mileageCost"
                                      ? "Mileage Cost"
                                      : row.typeKey === "travelTimeCost"
                                        ? "Travel Time Cost (Client)"
                                        : costTypeOptions.find(
                                            (option) =>
                                              option.key === row.typeKey,
                                          )?.label || "Amount"
                                  }
                                  slotProps={{
                                    input: {
                                      readOnly:
                                        (isLinguistPayRow &&
                                          !disableAutomaticCalculationOfCharges) ||
                                        isTravelTimeRow,
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                    },
                                  }}
                                  value={currentValue}
                                  onFocus={() => {
                                    if (Number(currentValue) === 0) {
                                      setCostValueByType(row.typeKey, "");
                                    }
                                  }}
                                  onBlur={() => {
                                    if (
                                      Number(
                                        getCostValueByType(row.typeKey),
                                      ) === 0
                                    ) {
                                      setCostValueByType(row.typeKey, 0);
                                    }
                                  }}
                                  onChange={(event) => {
                                    if (row.typeKey === "mileageCost") {
                                      // Manual edit — stop auto-calculating so
                                      // the typed value is not overwritten.
                                      mileageCostManuallyEditedRef.current =
                                        true;
                                      applyShouldAutoCalculateMileageCost(
                                        false,
                                      );
                                    }
                                    setCostValueByType(
                                      row.typeKey,
                                      event.target.value,
                                    );
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      py: 0,
                                      "&.MuiInputBase-root fieldset": {
                                        borderColor: getBorderColor(
                                          theme,
                                          isNewAssignment,
                                          Number(currentValue) || "",
                                          Number(
                                            getOriginalCostValueByType(
                                              row.typeKey,
                                            ),
                                          ) || "",
                                          true,
                                        ),
                                      },
                                    },
                                  }}
                                />
                              </Grid>

                              {isTravelTimeRow ? (
                                <Grid size={{ xs: 12, md: "grow" }}>
                                  <TextField
                                    disabled={!isEditable}
                                    size="small"
                                    label="Travel Time Cost (Linguist)"
                                    slotProps={{
                                      input: {
                                        readOnly: true,
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            $
                                          </InputAdornment>
                                        ),
                                      },
                                    }}
                                    value={travelTimeLinguistCost}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        padding: "8.5px 12px !important",
                                      },
                                      "& .MuiOutlinedInput-root": {
                                        py: 0,
                                        "&.MuiInputBase-root fieldset": {
                                          borderColor: getBorderColor(
                                            theme,
                                            isNewAssignment,
                                            Number(travelTimeLinguistCost) || "",
                                            Number(
                                              getOriginalTravelTimeLinguistCost(),
                                            ) || "",
                                            true,
                                          ),
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              ) : null}

                              <Grid size={{ xs: 12, md: "grow" }}>
                                <TextField
                                  select
                                  disabled={!isEditable}
                                  size="small"
                                  label="Include in"
                                  slotProps={{
                                    select: {
                                      multiple: true,
                                      value: [
                                        ...(row.includeInTotalCost
                                          ? ["includeInTotalCost"]
                                          : []),
                                        ...(isLinguistPayRow ||
                                        row.includeInLinguistPay
                                          ? ["includeInLinguistPay"]
                                          : []),
                                        ...(row.includeInAccountCharge
                                          ? ["includeInAccountCharge"]
                                          : []),
                                      ],
                                      onChange: (event) => {
                                        const raw = event.target.value;
                                        const value = Array.isArray(raw)
                                          ? raw
                                          : raw == null
                                            ? []
                                            : [raw];
                                        if (value.length === 0) return;
                                        const next = new Set(value);
                                        if (
                                          row.includeInTotalCost !==
                                          next.has("includeInTotalCost")
                                        ) {
                                          updateCostRowFlag(
                                            row.id,
                                            "includeInTotalCost",
                                            next.has("includeInTotalCost"),
                                          );
                                        }
                                        if (
                                          !isLinguistPayRow &&
                                          row.includeInLinguistPay !==
                                            next.has("includeInLinguistPay")
                                        ) {
                                          updateCostRowFlag(
                                            row.id,
                                            "includeInLinguistPay",
                                            next.has("includeInLinguistPay"),
                                          );
                                        }
                                        if (
                                          row.includeInAccountCharge !==
                                          next.has("includeInAccountCharge")
                                        ) {
                                          updateCostRowFlag(
                                            row.id,
                                            "includeInAccountCharge",
                                            next.has("includeInAccountCharge"),
                                          );
                                        }
                                      },
                                      renderValue: (selected) =>
                                        selected.length === 3
                                          ? "All"
                                          : selected
                                              .map(
                                                (v) =>
                                                  ({
                                                    includeInTotalCost:
                                                      "Total Cost",
                                                    includeInLinguistPay:
                                                      "Total Linguist Pay",
                                                    includeInAccountCharge:
                                                      "Account Charge",
                                                  })[v],
                                              )
                                              .filter(Boolean)
                                              .join(", "),
                                    },
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "& .MuiInputBase-input": {
                                        padding: "8.5px 12px !important",
                                      },
                                      "&.MuiInputBase-root fieldset": {
                                        py: 0,
                                        borderColor: getBorderColor(
                                          theme,
                                          isNewAssignment,
                                          inclusionChanged ? "changed" : "unchanged",
                                          "unchanged",
                                        ),
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem
                                    value="includeInTotalCost"
                                    disabled={
                                      !isEditable ||
                                      isLinguistPayRow ||
                                      isLastCheckedFlag(row.includeInTotalCost)
                                    }
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={Boolean(
                                        row.includeInTotalCost,
                                      )}
                                      disabled={
                                        !isEditable ||
                                        isLinguistPayRow ||
                                        isLastCheckedFlag(
                                          row.includeInTotalCost,
                                        )
                                      }
                                    />
                                    Total Cost
                                  </MenuItem>
                                  <MenuItem
                                    value="includeInLinguistPay"
                                    disabled={!isEditable || isLinguistPayRow}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        isLinguistPayRow
                                          ? true
                                          : Boolean(row.includeInLinguistPay)
                                      }
                                      disabled={
                                        !isEditable || isLinguistPayRow
                                      }
                                    />
                                    Total Linguist Pay
                                  </MenuItem>
                                  <MenuItem
                                    value="includeInAccountCharge"
                                    disabled={
                                      !isEditable ||
                                      isLinguistPayRow ||
                                      isPaymentFeesRow ||
                                      isLastCheckedFlag(
                                        row.includeInAccountCharge,
                                      )
                                    }
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={Boolean(
                                        row.includeInAccountCharge,
                                      )}
                                      disabled={
                                        !isEditable ||
                                        isLinguistPayRow ||
                                        isPaymentFeesRow ||
                                        isLastCheckedFlag(
                                          row.includeInAccountCharge,
                                        )
                                      }
                                    />
                                    Account Charge
                                  </MenuItem>
                                </TextField>
                              </Grid>
                            </Grid>
                          </Box>
                          <Box
                            sx={{ flex: "0 0 auto", mt: { xs: 0, md: 0.5 } }}
                          >
                            <IconButton
                              disabled={!isEditable || isLockedLinguistPayRow}
                              onClick={() => removeCostRow(row.id)}
                              size="small"
                            >
                              <ClearRoundedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>

              {costRows.some(
                (row) =>
                  row.typeKey === "linguistPay" ||
                  row.typeKey === "additionalLinguistCost",
              ) ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"TotalLinguistPay"}
                      label="Total Linguist Pay"
                      helperText={
                        <CostFormulaHelper
                          parts={totalLinguistPayHelperParts}
                        />
                      }
                      slotProps={{
                        input: {
                          readOnly: !disableAutomaticCalculationOfCharges,
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        },
                      }}
                      value={totalLinguistPay}
                      onChange={(event) => {
                        // 2026-09-22 (financial bug): this field must NEVER
                        // write into the Linguist Pay cost ROW. The old
                        // `setLinguistPay(nextValue)` here (and on Total
                        // Cost) copied the TOTAL into the base-pay row on
                        // save — service 26021003 stored a Linguist Pay row
                        // of 204.54 instead of the base 60.00, which then
                        // double-counted the extras in every total.
                        setTotalLinguistPay(event.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor:
                              totalLinguistPay === null ||
                              totalLinguistPay === ""
                                ? theme.palette.grey.main
                                : theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalCost"}
                  label="Total Cost"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  helperText={
                    disableAutomaticCalculationOfCharges ? null : (
                      <CostFormulaHelper parts={totalCostHelperParts} />
                    )
                  }
                  error={totalCostError}
                  value={totalCost}
                  onFocus={() => {
                    if (Number(totalCost) === 0) {
                      setTotalCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(totalCost) === 0) {
                      setTotalCost(0);
                    }
                  }}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    // 2026-09-22 (financial bug): must NOT write into the
                    // Linguist Pay cost ROW — see the TotalLinguistPay field.
                    setTotalCost(nextValue);
                    setTotalCostError(nextValue === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(totalCost) || "",
                          Number(overlayData.TotalCost),
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"AccountCharge"}
                  label="Account Charge"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  helperText={
                    disableAutomaticCalculationOfCharges ? null : (
                      <CostFormulaHelper parts={accountChargeHelperParts} />
                    )
                  }
                  error={accountChargeError}
                  value={accountCharge}
                  onFocus={() => {
                    if (Number(accountCharge) === 0) {
                      setAccountCharge("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(accountCharge) === 0) {
                      setAccountCharge(0);
                    }
                  }}
                  onChange={(event) => {
                    setAccountCharge(event.target.value);
                    setAccountChargeError(event.target.value === null);

                    const accountChargeValue = parseFloat(event.target.value);
                    const totalCostValue = parseFloat(totalCost);

                    // Prevent division by zero and handle invalid values
                    if (
                      !event.target.value ||
                      accountChargeValue === 0 ||
                      isNaN(accountChargeValue) ||
                      isNaN(totalCostValue)
                    ) {
                      setProfit(0);
                      setMargin(0);
                      return;
                    }

                    const prof = accountChargeValue - totalCostValue;

                    setProfit(prof.toFixed(2));
                    const calculatedMargin = (prof / accountChargeValue) * 100;
                    setMargin(
                      isNaN(calculatedMargin) || !isFinite(calculatedMargin)
                        ? 0
                        : calculatedMargin.toFixed(2),
                    );
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(accountCharge) || "",
                          Number(overlayData.AccountCharge),
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              {!completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={isEditable ? !allowDetailsEditing : true}
                      size="small"
                      id={"Details"}
                      label="Details"
                      error={detailsError}
                      value={details}
                      onChange={(event) => {
                        setDetails(event.target.value);
                        setDetailsError(event.target.value === null);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              details || "",
                              overlayData.Details,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={true}
                  size="small"
                  id={"Profit"}
                  label="Profit"
                  value={profit}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor:
                          profit === null || profit === ""
                            ? theme.palette.grey.main
                            : theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={true}
                  size="small"
                  id={"Margin"}
                  label="Margin"
                  value={margin}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor:
                          margin === null || margin === ""
                            ? theme.palette.grey.main
                            : theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"The Note"}
                    label="Note"
                    multiline
                    value={note}
                    inputRef={noteInputRef}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setNote(nextValue);
                      handleNoteTagState(
                        nextValue,
                        event.target.selectionStart ?? nextValue.length,
                      );
                    }}
                    onKeyUp={(event) => {
                      const target = event.target;
                      const textValue =
                        target && typeof target.value === "string"
                          ? target.value
                          : note;
                      const cursorPosition =
                        target && typeof target.selectionStart === "number"
                          ? target.selectionStart
                          : textValue.length;
                      handleNoteTagState(textValue, cursorPosition);
                    }}
                    onClick={(event) => {
                      const target = event.target;
                      const textValue =
                        target && typeof target.value === "string"
                          ? target.value
                          : note;
                      const cursorPosition =
                        target && typeof target.selectionStart === "number"
                          ? target.selectionStart
                          : textValue.length;
                      handleNoteTagState(textValue, cursorPosition);
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setIsMentionOpen(false);
                      }, 120);
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor:
                            note === null || note === ""
                              ? theme.palette.grey.main
                              : theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                  {isMentionOpen && (
                    <Popper
                      open={isMentionOpen}
                      anchorEl={noteInputRef.current}
                      placement="bottom-start"
                      sx={{
                        zIndex: 1800,
                        width: noteInputRef.current?.offsetWidth || 360,
                      }}
                    >
                      <Paper
                        elevation={4}
                        sx={{ mt: 0.5, maxHeight: 280, overflowY: "auto" }}
                      >
                        {mentionTrigger === "#" && isMentionLoading ? (
                          <Box
                            sx={{
                              p: 1.5,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <CircularProgress size={20} />
                          </Box>
                        ) : mentionTrigger === "#" ? (
                          serviceTagSuggestions.length > 0 ? (
                            <List dense disablePadding>
                              {serviceTagSuggestions.map((service) => (
                                <ListItemButton
                                  key={service.id}
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    handleSelectServiceTag(service);
                                  }}
                                >
                                  <ListItemText
                                    primary={`#${service.ProjectNumber}`}
                                    secondary={service.Details || "No details"}
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          ) : (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ p: 1.5 }}
                            >
                              No matching services
                            </Typography>
                          )
                        ) : userTagSuggestions.length > 0 ? (
                          <List dense disablePadding>
                            {userTagSuggestions.map((member) => {
                              const mentionToken =
                                getMemberMentionToken(member);
                              return (
                                <ListItemButton
                                  key={member.id}
                                  onMouseDown={(event) => {
                                    event.preventDefault();
                                    handleSelectUserTag(member);
                                  }}
                                >
                                  <ListItemText
                                    primary={`@${mentionToken} · ${member.Name || "User"}`}
                                    secondary={member.Email || ""}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        ) : (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ p: 1.5 }}
                          >
                            No matching users
                          </Typography>
                        )}
                      </Paper>
                    </Popper>
                  )}
                </Box>
              </Grid>

              {showRecurring && !completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id={"RepeatEvery"}
                      label="Repeat Every"
                      error={repeatEveryError}
                      value={repeatEvery}
                      onChange={(event) => {
                        setRepeatEvery(event.target.value);
                        setRepeatEveryError(event.target.value === null);

                        handleRecursionChange(
                          date,
                          event.target.value,
                          period,
                          repeatUntilDate,
                          tempSelectedDays,
                          showRecurring,
                        );
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              repeatEvery || "",
                              " ",
                              true,
                            ),
                          },
                        },
                      }}
                    >
                      {repeatEveryList.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id={"Period"}
                      label="Period"
                      error={periodError}
                      value={period}
                      onChange={(event) => {
                        if (!date || !date.isValid?.()) return;

                        setPeriod(event.target.value);
                        setPeriodError(event.target.value === null);

                        let selectedDays = [];

                        if (event.target.value === "Day")
                          selectedDays = [
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                            "Sun",
                          ];

                        if (event.target.value === "Week")
                          selectedDays = [date.format("ddd")];

                        setTempSelectedDays(selectedDays);

                        const repeatUntilDateTemp = date.add(
                          1,
                          event.target.value === "Month" ? "year" : "month",
                        );

                        setRepeatUntilDate(repeatUntilDateTemp);

                        handleRecursionChange(
                          date,
                          repeatEvery,
                          event.target.value,
                          repeatUntilDateTemp,
                          selectedDays,
                          showRecurring,
                        );
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              period || "",
                              " ",
                              true,
                            ),
                          },
                        },
                      }}
                    >
                      {periodsList.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <MobileDatePicker
                      disabled={!isEditable}
                      id={"RepeatUntil"}
                      label="Repeat Until"
                      size="small"
                      format="dddd, MMMM DD, YYYY"
                      value={repeatUntilDate}
                      onChange={(newValue) => {
                        if (newValue && newValue.isValid()) {
                          setRepeatUntilDate(newValue);
                          setRepeatUntilDateError(false);

                          handleRecursionChange(
                            date,
                            repeatEvery,
                            period,
                            newValue,
                            tempSelectedDays,
                            showRecurring,
                          );
                        } else {
                          setRepeatUntilDateError(true);
                        }
                      }}
                      sx={{
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            (repeatUntilDate &&
                              repeatUntilDate.format("YYYY-MM-DD")) ||
                              "",
                            "",
                            true,
                          ),
                        ),
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: dateError,
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <InlineGridSelector
                      disabled={
                        period === "Month" ||
                        (period === "Day" && repeatEvery !== 1)
                      } //
                      label="Days of the week"
                      items={daysList}
                      helperText={recursionStatusText}
                      selectedItems={tempSelectedDays}
                      onChange={handleDaysChange}
                      onSave={null}
                      itemsPerLine={7}
                      isMulti={true}
                      lockedItem={date?.format("ddd") || ""}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid container spacing={1.5} sx={{ pb: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Options
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: "10px", height: "100%" }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Scheduling
                    </Typography>

                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Show the inputs for Actual Time, Scheduling, Preparation, and Total Duration."
                            }
                            {`\n\nNo: Show only the Start Time, End Time, and Duration.`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"ShowActualTimes"}
                          label="Show Actual Times"
                          checked={showActualTimes}
                          onChange={(event) => {
                            setShowActualTimes(event.target.checked);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            ml: 4.5,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Include actual, preparation, scheduling, and total
                          duration fields.
                        </Typography>
                      </Box>
                    </HtmlTooltip>

                    {isNewAssignment ? (
                      <HtmlTooltip
                        title={
                          <Box p=".5rem">
                            <Typography
                              color="inherit"
                              style={{ whiteSpace: "pre-line" }}
                            >
                              {
                                "Yes: Show the options to make the assignment recurring."
                              }
                              {`\n\nNo: Hide the options to make the assignment recurring.`}
                            </Typography>
                          </Box>
                        }
                      >
                        <Box sx={{ py: 0.25 }}>
                          <FormControlLabel
                            control={<Switch sx={optionSwitchSx} />}
                            id={"showRecurring"}
                            label="Recurring Assignment"
                            checked={showRecurring}
                            onChange={(event) => {
                              setShowRecurring(event.target.checked);

                              if (event.target.checked)
                                handleRecursionChange(
                                  date,
                                  repeatEvery,
                                  period,
                                  repeatUntilDate,
                                  tempSelectedDays,
                                  event.target.checked,
                                );
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              ml: 4.5,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            Create a repeating schedule and generate linked
                            assignments.
                          </Typography>
                        </Box>
                      </HtmlTooltip>
                    ) : null}

                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Allows the creation of assignments that are less than 1 hour long (Scheduling, or calling assignments)."
                            }
                            {`\n\nNo: Assignments will be at least 1 hour long.`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"AllowShortAssignments"}
                          label="Allow Short Assignments (< 1 hour)"
                          checked={allowShortAssignments}
                          onChange={(event) => {
                            setAllowShortAssignments(event.target.checked);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            ml: 4.5,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Permit assignments shorter than one hour.
                        </Typography>
                      </Box>
                    </HtmlTooltip>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: "10px", height: "100%" }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Linguist Matching
                    </Typography>

                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Linguists list will have all Linguists (Current, Former, Prospect...)."
                            }
                            {`\n\nNo: Linguists list will only show Current Linguists.`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"ShowAllLinguists"}
                          label="Include Former and Prospect Linguists"
                          checked={showAllLinguists}
                          onChange={(event) => {
                            setShowAllLinguists(event.target.checked);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            ml: 4.5,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Expand the list beyond currently active linguists.
                        </Typography>
                      </Box>
                    </HtmlTooltip>

                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Only show Linguists who speak the selected Language."
                            }
                            {`\n\nNo: Show all linguists in the chosen pool.`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"FilterLinguistsByLanguage"}
                          label="Match Linguists to Selected Language"
                          checked={filterLingsByLangs}
                          onChange={(event) => {
                            setFilterLingsByLangs(event.target.checked);
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            ml: 4.5,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Improve assignment accuracy by filtering to
                          language-capable linguists.
                        </Typography>
                      </Box>
                    </HtmlTooltip>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: "10px", height: "100%" }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Manual Overrides
                    </Typography>

                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {"Yes: Allow the editing of the details."}
                            {`\n\nNo: Details will not be editable and will be filled according to Name, Requestor, and Language.`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"AllowDetailsEditing"}
                          label="Edit Details Manually"
                          checked={allowDetailsEditing}
                          onChange={(event) => {
                            setAllowDetailsEditing(event.target.checked);

                            if (!event.target.checked)
                              fillDetails(
                                requestor.Name,
                                name,
                                language.Name,
                                interpretationType,
                              );
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            ml: 4.5,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Enable direct edits instead of generated details text.
                        </Typography>
                      </Box>
                    </HtmlTooltip>

                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {"Yes: Allow the editing of the Total Duration."}
                            {`\n\nNo: Total Duration will not be editable and will be calculated from Start/End and Actual Start/End.`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"allowTotalDurationEditing"}
                          label="Edit Total Duration Manually"
                          checked={allowTotalDurationEditing}
                          onChange={(event) => {
                            setAllowTotalDurationEditing(event.target.checked);

                            if (!event.target.checked) {
                              const tempTotalDuration = sumDurationValues([
                                duration && actualDuration
                                  ? Math.max(
                                      actualDuration.hour() * 60 +
                                        actualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : duration || actualDuration || 0,
                                schedulingTime,
                                preparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            }
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            ml: 4.5,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Override computed total duration when operationally
                          needed.
                        </Typography>
                      </Box>
                    </HtmlTooltip>

                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Automatic calculation of Linguist Pay, Total Cost, and Account Charge will be disabled."
                            }
                            {`\n\nNo: Automatic calculation of Linguist Pay, Total Cost, and Account Charge will be enabled.`}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box sx={{ py: 0.25 }}>
                        <FormControlLabel
                          control={<Switch sx={optionSwitchSx} />}
                          id={"disableAutomaticCalculationOfCharges"}
                          label="Disable Automatic Charge Calculation"
                          checked={disableAutomaticCalculationOfCharges}
                          onChange={(event) => {
                            setDisableAutomaticCalculationOfCharges(
                              event.target.checked,
                            );
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            ml: 4.5,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Manage pay and charge values manually for this
                          assignment.
                        </Typography>
                      </Box>
                    </HtmlTooltip>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
    </Overlay>

      {/* Paid Assignment Warning Dialog */}
      <Dialog
        open={showPaidWarning}
        onClose={handlePaidWarningCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            fontWeight: "bold",
          }}
        >
          ⚠️ Paid Assignment Warning
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText
            sx={{
              color: theme.palette.text.primary,
              whiteSpace: "pre-line",
              fontWeight: 500,
            }}
          >
            {paidWarningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handlePaidWarningCancel}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaidWarningConfirm}
            color="warning"
            variant="contained"
            sx={{ borderRadius: "8px" }}
            autoFocus
          >
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default InterpretationNewEdit;
